// src/bis-applies.ts
var plain = (t) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
var lookup = (terms) => {
  const map = /* @__PURE__ */ new Map();
  for (const t of terms) for (const a of [t.key, t.label, ...t.aliases ?? []]) map.set(plain(a), t.key);
  return (v) => typeof v === "string" ? map.get(plain(v)) ?? null : null;
};
var BIS_CHANNELS = [
  { key: "instagram", label: "Instagram", aliases: ["insta", "ig", "reels", "feed"] },
  { key: "facebook", label: "Facebook", aliases: ["fb"] },
  { key: "linkedin", label: "LinkedIn" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
  { key: "whatsapp", label: "WhatsApp", aliases: ["zap", "grupo de mensagens", "mensagem"] },
  { key: "email", label: "e-mail", aliases: ["e mail", "newsletter", "email marketing"] },
  { key: "site", label: "site", aliases: ["website", "landing page", "pagina", "blog"] },
  { key: "paid_media", label: "m\xEDdia paga", aliases: ["midia paga", "anuncios", "ads", "google ads", "meta ads", "trafego pago"] },
  { key: "print", label: "impresso", aliases: ["impressao", "grafica", "papel", "folheto", "flyer"] },
  { key: "place", label: "no local", aliases: ["ponto fisico", "loja", "unidade", "recepcao", "mural", "pdv", "ponto de venda", "sinalizacao"] },
  { key: "press", label: "imprensa", aliases: ["assessoria", "release"] },
  { key: "event", label: "evento", aliases: ["feira", "congresso", "estande"] },
  { key: "sales", label: "venda direta", aliases: ["comercial", "reuniao de venda", "visita"] }
];
var BIS_PIECES = [
  { key: "aviso_interno", label: "aviso interno", aliases: ["aviso"] },
  { key: "comunicado", label: "comunicado" },
  { key: "convite_interno", label: "convite interno", aliases: ["convite"] },
  { key: "aviso_publico", label: "aviso ao p\xFAblico" },
  { key: "vaga", label: "vaga", aliases: ["divulgacao de vaga"] },
  { key: "post_institucional", label: "post institucional", aliases: ["post", "publicacao"] },
  { key: "material_comercial", label: "material comercial", aliases: ["folder", "catalogo", "tabela"] },
  { key: "campanha", label: "campanha externa", aliases: ["campanha"] },
  { key: "assinatura", label: "assinatura de e-mail", aliases: ["assinatura"] },
  { key: "cartao", label: "cart\xE3o de visita", aliases: ["cartao"] },
  { key: "anuncio", label: "an\xFAncio", aliases: ["ad", "criativo"] },
  { key: "proposta", label: "proposta", aliases: ["proposta comercial", "orcamento"] },
  { key: "apresentacao", label: "apresenta\xE7\xE3o", aliases: ["slides", "deck"] },
  { key: "embalagem", label: "embalagem", aliases: ["rotulo", "etiqueta"] },
  { key: "video", label: "v\xEDdeo", aliases: ["filme", "roteiro"] },
  { key: "pagina", label: "p\xE1gina de site", aliases: ["landing"] },
  { key: "resposta", label: "resposta a mensagem", aliases: ["atendimento", "dm", "comentario"] }
];
var BIS_AUDIENCE_SCOPES = [
  { key: "internal", label: "para dentro da empresa", aliases: ["interno", "colaboradores", "equipe", "funcionarios"] },
  { key: "external", label: "para fora da empresa", aliases: ["externo", "clientes", "publico"] }
];
var bisChannelKey = lookup(BIS_CHANNELS);
var bisPieceKey = lookup(BIS_PIECES);
var scopeKey = lookup(BIS_AUDIENCE_SCOPES);
var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var bisAudienceKey = (v) => typeof v === "string" && UUID.test(v.trim()) ? v.trim().toLowerCase() : scopeKey(v);
var bisChannelLabel = (key) => BIS_CHANNELS.find((c) => c.key === key)?.label ?? key;
var bisPieceLabel = (key) => BIS_PIECES.find((c) => c.key === key)?.label ?? key;
var bisAudienceLabel = (key, titleOf) => BIS_AUDIENCE_SCOPES.find((c) => c.key === key)?.label ?? titleOf?.(key) ?? "um p\xFAblico do BIS";
function canonicalBisApplies(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const unknown = [];
  const axis = (v, key) => {
    const out = [];
    for (const x of Array.isArray(v) ? v : []) {
      const k = key(x);
      if (k) {
        if (!out.includes(k)) out.push(k);
      } else if (typeof x === "string" && x.trim()) unknown.push(x.trim().slice(0, 60));
    }
    return out.slice(0, 16);
  };
  const id = (v) => typeof v === "string" && UUID.test(v.trim()) ? v.trim().toLowerCase() : null;
  const programs = axis(src.programs, id);
  const a = { channels: axis(src.channels, bisChannelKey), pieces: axis(src.pieces, bisPieceKey), audiences: axis(src.audiences, bisAudienceKey), ...programs.length ? { programs } : {} };
  return { applies: a.channels.length || a.pieces.length || a.audiences.length || programs.length ? a : null, unknown };
}
function bisAppliesTo(applies, ctx) {
  if (!applies) return true;
  const a = canonicalBisApplies(applies).applies;
  if (!a) return true;
  const ok = (own, given) => !own.length || !given || given.some((g) => own.includes(g));
  const programs = a.programs ?? [];
  if (programs.length && !(ctx.program && programs.includes(ctx.program))) return false;
  return ok(a.channels, ctx.channels) && ok(a.pieces, ctx.piece ? [ctx.piece] : void 0) && ok(a.audiences, ctx.audiences);
}
function bisSpecificity(applies, ctx) {
  const a = applies ? canonicalBisApplies(applies).applies : null;
  if (!a) return 0;
  const hit2 = (own, given) => own.length && given?.some((g) => own.includes(g)) ? 1 : 0;
  return hit2(a.channels, ctx.channels) + hit2(a.pieces, ctx.piece ? [ctx.piece] : void 0) + hit2(a.audiences, ctx.audiences) + (ctx.program && (a.programs ?? []).includes(ctx.program) ? 2 : 0);
}
function bisFor(items, ctx) {
  return items.map((i, n) => ({ i, n, s: bisSpecificity(i.applies, ctx) })).filter((x) => bisAppliesTo(x.i.applies, ctx)).sort((a, b) => b.s - a.s || a.n - b.n).map((x) => x.i);
}
function bisAppliesText(applies, titleOf) {
  const a = applies ? canonicalBisApplies(applies).applies : null;
  if (!a) return "";
  return [(a.programs ?? []).map((k) => `s\xF3 ${titleOf?.(k) ?? "um programa"}`).join(", "), a.channels.map(bisChannelLabel).join(", "), a.pieces.map(bisPieceLabel).join(", "), a.audiences.map((k) => bisAudienceLabel(k, titleOf)).join(", ")].filter(Boolean).join(" \xB7 ");
}
var str = (v) => typeof v === "string" ? v.trim() : "";
function bisProgramActive(data, today) {
  const from = str(data?.startsOn), to = str(data?.endsOn);
  return (!from || today >= from) && (!to || today <= to);
}
function bisActivePrograms(items, today) {
  return items.filter((i) => i.kind === "program" && bisProgramActive(i.data, today));
}
function bisProgramOf(text, programs) {
  const t = ` ${plain(text)} `;
  let best = null;
  for (const p of programs) {
    const names = [p.title, ...Array.isArray(p.data?.aliases) ? p.data.aliases.filter((x) => typeof x === "string") : []].map(plain).filter((n) => n.length >= 3);
    for (const n of names) if (t.includes(` ${n} `) && (!best || n.length > best.n)) best = { p, n: n.length };
  }
  return best?.p ?? null;
}

// src/pieces.ts
var PIECE_FAMILIES = ["social", "print", "digital", "video", "document", "study"];
var PIECE_FAMILY_LABELS = { social: "Redes", print: "Impresso", digital: "Digital", video: "V\xEDdeo", document: "Documento", study: "Estudo" };
var PIECE_UNITS = ["px", "cm", "mm", "m"];
var PIECE_TYPE_MAX = 80;
var PIECE_SIZES_MAX = 8;
var PIECE_TYPE_DEFAULTS = [
  { key: "post", name: "Post", family: "social", unit: "px", sizes: [{ w: 1080, h: 1350 }, { w: 1080, h: 1080 }], toClient: true, bisPiece: "post_institucional" },
  { key: "carrossel", name: "Carrossel", family: "social", unit: "px", sizes: [{ w: 1080, h: 1350 }, { w: 1080, h: 1080 }], toClient: true, bisPiece: "post_institucional" },
  { key: "story", name: "Story", family: "social", unit: "px", sizes: [{ w: 1080, h: 1920 }], toClient: true },
  { key: "reel", name: "Reel", family: "social", unit: "px", sizes: [{ w: 1080, h: 1920 }], toClient: true },
  { key: "video", name: "V\xEDdeo", family: "video", unit: "px", sizes: [{ w: 1920, h: 1080 }, { w: 1080, h: 1920 }], toClient: true },
  { key: "anuncio", name: "An\xFAncio", family: "digital", unit: "px", sizes: [{ w: 1080, h: 1080 }, { w: 1080, h: 1920 }, { w: 1200, h: 628 }], toClient: true },
  { key: "banner", name: "Banner de site", family: "digital", unit: "px", sizes: [{ w: 1920, h: 600 }], toClient: true },
  { key: "email", name: "E-mail", family: "digital", unit: "px", sizes: [], toClient: true },
  { key: "apresentacao", name: "Apresenta\xE7\xE3o", family: "document", unit: "px", sizes: [{ w: 1920, h: 1080 }], toClient: true, bisPiece: "apresentacao" },
  { key: "manual", name: "Manual", family: "document", unit: "cm", sizes: [{ w: 21, h: 29.7 }], toClient: true },
  { key: "roteiro", name: "Roteiro", family: "document", unit: "cm", sizes: [], toClient: true },
  { key: "folheto", name: "Folheto", family: "print", unit: "cm", sizes: [{ w: 21, h: 29.7 }, { w: 15, h: 21 }, { w: 10, h: 21 }], toClient: true },
  { key: "cartaz", name: "Cartaz", family: "print", unit: "cm", sizes: [{ w: 29.7, h: 42 }, { w: 42, h: 59.4 }], toClient: true },
  { key: "outdoor", name: "Outdoor", family: "print", unit: "m", sizes: [{ w: 9, h: 3 }], toClient: true },
  { key: "cartao-de-visita", name: "Cart\xE3o de visita", family: "print", unit: "cm", sizes: [{ w: 9, h: 5 }], toClient: true, bisPiece: "cartao" },
  { key: "embalagem", name: "Embalagem", family: "print", unit: "mm", sizes: [], toClient: true },
  { key: "moodboard", name: "Moodboard", family: "study", unit: "px", sizes: [{ w: 1920, h: 1080 }], toClient: false }
];
var pieceSlug = (text) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
var pieceNumber = (n) => String(Math.round(n * 100) / 100);
var pieceSizeText = (s, unit) => `${pieceNumber(s.w)}x${pieceNumber(s.h)}${unit}`;
function parsePieceSize(text) {
  const m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(text);
  if (!m) return null;
  const w = Number(m[1].replace(",", "."));
  const h = Number(m[2].replace(",", "."));
  return w > 0 && h > 0 && w < 1e5 && h < 1e5 ? { w, h } : null;
}
function pieceFileName(p) {
  const spec = [p.number != null ? `cont-${String(p.number).padStart(2, "0")}` : "", pieceSlug(p.spec ?? "")].filter(Boolean).join("-");
  const size = p.width && p.height ? pieceSizeText({ w: p.width, h: p.height }, p.unit || "px") : "";
  return [[pieceSlug(p.piece), spec].filter(Boolean).join("-"), pieceSlug(p.client), pieceSlug(p.campaign ?? ""), size].filter(Boolean).join("_");
}

// src/bis-molds.ts
var MOLD_ROOM_STATUSES = ["open", "done"];
var MOLD_ROOM_FILES_MIN = 2;
var MOLD_ROOM_FILES_MAX = 24;
var MOLD_ROOM_PREVIEWS = 6;
var MOLD_FIELD_LABELS = { name: "nome", role: "cargo", phone: "telefone", email: "e-mail", extra: "registro ou linha livre", headline: "t\xEDtulo", body: "texto", eyebrow: "r\xF3tulo acima do t\xEDtulo", footer: "rodap\xE9" };
var moldFieldLabel = (field) => MOLD_FIELD_LABELS[field] ?? "texto que muda";
var quote = (values) => values.slice(0, 3).map((v) => `"${v}"`).join(", ");
function moldQuestions(r) {
  const out = [];
  const yesNo = (yes, no) => [{ value: "yes", label: yes }, { value: "no", label: no }];
  for (const w of r.warnings) {
    if (w.kind !== "fixed_text_differs") continue;
    const variants = [w.expected, ...new Set(w.found.map((f) => f.text))];
    out.push({ id: `fixed:${out.length}`, ref: w.expected, text: "um texto que deveria ser igual em todos os arquivos veio diferente em alguns. qual \xE9 o certo?", seen: `a maioria diz "${w.expected}"; ${w.found.map((f) => `"${f.text}" em ${f.file}`).join("; ")}`, options: variants.map((v) => ({ value: v, label: v })), suggested: w.expected });
  }
  for (const f of r.fields) {
    const known = f.detected in MOLD_FIELD_LABELS;
    const label = known ? `o ${moldFieldLabel(f.detected)}` : "este texto";
    if (!known) out.push({ id: `what:${f.detected}`, text: "este texto muda de um arquivo para o outro, e a leitura n\xE3o reconheceu o que \xE9. o que \xE9?", seen: quote(f.values), options: Object.entries(MOLD_FIELD_LABELS).filter(([k]) => !r.fields.some((x) => x.detected === k)).map(([value, l]) => ({ value, label: l })), suggested: r.fields.some((x) => x.detected === "role") ? "extra" : "role" });
    if (f.lines === 2 && f.alwaysLines) out.push({ id: `lines:${f.detected}`, text: `em todos os arquivos ${label} ocupa duas linhas. \xE9 regra do desenho ou s\xF3 aconteceu nestes exemplos?`, seen: quote(f.values), options: [{ value: "two", label: "\xE9 regra: sempre duas linhas, mesmo quando \xE9 curto" }, { value: "free", label: "s\xF3 aconteceu: quebra quando n\xE3o couber" }], suggested: "two" });
    if (f.upper) out.push({ id: `upper:${f.detected}`, text: `${label} veio em caixa alta em todos os arquivos. o molde escreve sempre em caixa alta?`, seen: quote(f.values), options: yesNo("sim, sempre em caixa alta", "n\xE3o, como a pessoa escrever"), suggested: "yes" });
    if (f.detected !== "name") out.push({ id: `optional:${f.detected}`, text: `quando a pessoa n\xE3o tem ${known ? moldFieldLabel(f.detected) : "este dado"}, a linha some do desenho?`, seen: f.prefix ? `a linha come\xE7a com "${f.prefix.trim()}"` : void 0, options: yesNo("some, com o r\xF3tulo junto", "n\xE3o: este dado \xE9 obrigat\xF3rio"), suggested: "yes" });
  }
  const blocked = r.fields.filter((f) => f.blocked);
  if (blocked.length) out.push({ id: "reach:*", text: "h\xE1 uma forma do desenho ao lado dos textos. quando um texto for comprido, ele pode passar por cima dela?", seen: `ao lado de: ${blocked.map((f) => moldFieldLabel(f.detected)).join(", ")}`, options: [{ value: "margin", label: "pode: vai at\xE9 a margem" }, { value: "shape", label: "n\xE3o pode: para antes da forma" }], suggested: "margin" });
  return out;
}
function moldOptions(questions, answers) {
  const o = { rename: {}, lines: {}, upper: {}, optional: {}, reach: {}, fixed: {} };
  for (const q of questions) {
    const given = answers[q.id];
    const v = given && q.options.some((x) => x.value === given) ? given : q.suggested;
    const [kind, key = ""] = [q.id.slice(0, q.id.indexOf(":")), q.id.slice(q.id.indexOf(":") + 1)];
    if (kind === "what") o.rename[key] = v;
    if (kind === "lines") o.lines[key] = v === "two" ? 2 : 0;
    if (kind === "upper") o.upper[key] = v === "yes";
    if (kind === "optional") o.optional[key] = v === "yes";
    if (kind === "reach") o.reach[key] = v;
    if (kind === "fixed" && q.ref) o.fixed[q.ref] = v;
  }
  return o;
}
function moldNotices(r, brandFonts) {
  const out = [];
  const have = brandFonts.map((f) => f.toLowerCase());
  const missing = [...new Set(r.fonts.map((f) => f.replace(/\s+\d00(\s+italic)?$/i, "")))].filter((f) => !have.includes(f.toLowerCase()));
  if (missing.length) out.push(`falta o arquivo de fonte de ${missing.join(", ")} no bloco de tipografia do BIS: sem ele, a prova e o MODO desenham com uma fonte substituta, e as medidas mudam`);
  for (const w of r.warnings) {
    if (w.kind === "color_off_palette") out.push(`h\xE1 cor no arquivo que n\xE3o est\xE1 na paleta do BIS (${w.colors.slice(0, 4).join("; ")}): ou falta a cor na paleta, com o CMYK, ou o arquivo usa uma cor fora da marca`);
    if (w.kind === "has_images") out.push(`o arquivo tem ${w.count === 1 ? "uma imagem" : `${w.count} imagens`} (foto, ou logo colado como imagem) que n\xE3o ${w.count === 1 ? "entra" : "entram"} no molde nesta fase: s\xF3 vetor e texto`);
    if (w.kind === "nothing_readable") out.push("n\xE3o h\xE1 texto nem vetor para ler nesta p\xE1gina: o arquivo veio como imagem, ou o texto foi convertido em curvas");
  }
  if (!r.fields.length && r.files > 1) out.push("nenhum texto muda de um arquivo para o outro: ou os arquivos s\xE3o iguais, ou o texto foi convertido em curvas (o molde \xE9 lido do PDF com o texto vivo)");
  return out;
}

// src/bis-references.ts
var BIS_REF_FINDING_KINDS = ["diverges", "complements", "new", "confirms", "conflict"];
var BIS_REF_FINDING_LABELS = {
  diverges: "diverge do que existe",
  complements: "complementa um bloco",
  new: "novo",
  confirms: "confirma o que existe",
  conflict: "as \xE1reas divergem"
};
var BIS_REF_FINDING_STATES = ["open", "applied", "dismissed"];
var BIS_REF_STATUSES = ["reading", "thinking", "ready", "failed", "skipped"];
var BIS_REF_TEXT_MAX = 12e4;
var BIS_REF_FINDINGS_MAX = 40;
var BIS_REF_BATCH = 12;
var BIS_REF_QUESTIONS_MAX = 4;
var BIS_REF_MESSAGES_MAX = 30;
var bisRefBasis = (name, where) => `documento de refer\xEAncia \u201C${name.slice(0, 120)}\u201D${where.trim() ? `, ${where.trim().slice(0, 80)}` : ""}`.slice(0, 300);
var BIS_REF_ESTIMATE_USD = 0.25;
var BIS_BUILD_KIND = "material";
var BIS_BUILD_AREAS = ["brand", "products", "listening", "future", "market", "places", "data", "decided"];
var BIS_BUILD_AREA_INFO = {
  brand: { label: "marca", tab: "marca", feeds: "identidade, visual e a voz de hoje", empty: "sem cadastro nem radar da pr\xF3pria marca: identidade e voz ficam sem base" },
  products: { label: "produtos e servi\xE7os", tab: "produtos", feeds: "o que a marca vende e o que cada um tem de dizer", empty: "nenhum produto ou servi\xE7o cadastrado" },
  listening: { label: "escuta", tab: "escuta", feeds: "p\xFAblicos, voz e comportamento", empty: "ningu\xE9m foi ouvido: p\xFAblicos e voz ficam s\xF3 com o que a marca publica" },
  future: { label: "futuro", tab: "futuro", feeds: "dire\xE7\xE3o", empty: "a diretoria ainda n\xE3o foi ouvida: o cap\xEDtulo de futuro fica vazio" },
  market: { label: "mercado", tab: "mercado", feeds: "posicionamento, diferenciais, espa\xE7os vazios e as regras do setor", empty: "sem concorrente lido nem setor pesquisado: posicionamento e regras de conformidade ficam sem base" },
  places: { label: "pra\xE7as", tab: "pra\xE7as", feeds: "onde cada coisa vale", empty: "nenhuma unidade nem pra\xE7a cadastrada" },
  data: { label: "dados externos", tab: "dados", feeds: "o que \xE9 medido", empty: "nenhum dado pr\xF3prio ligado (site, busca, Instagram, portal)" },
  decided: { label: "o que a Raro j\xE1 decidiu", tab: null, feeds: "Solu\xE7\xF5es, decis\xF5es, aprendizados e hip\xF3teses sustentadas", empty: "nenhum projeto com decis\xE3o ou Solu\xE7\xE3o aprovada" }
};

// src/modo.ts
var MODO_SNAP_PAGES = 2;
var MODO_PIECE_KINDS = ["a4", "card", "story", "signature", "bizcard"];
var MODO_PIECE_KIND_LABELS = { a4: "A4 vertical", card: "card 4:5 (WhatsApp, feed)", story: "story 9:16", signature: "assinatura de e-mail", bizcard: "cart\xE3o de visita 9 \xD7 5 cm" };
var MODO_PERSON_KINDS = ["signature", "bizcard"];
var MODO_HIGHLIGHT_MAX = 34;
var MODO_HIGHLIGHT_DETAIL_MAX = 60;
var modoMoldZones = (mold) => Boolean(mold?.template && /data-zone="content"/.test(mold.template));
var MODO_LEGAL_MAX = 4;
var MODO_LEGAL_TEXT_MAX = 280;
var MODO_VARIANTS = {
  0: { name: "Claro", why: "fundo branco com a faixa da marca. l\xEA-se de longe e imprime bem em qualquer impressora." },
  1: { name: "Cor cheia", why: "a cor da marca ocupa a pe\xE7a inteira. chama aten\xE7\xE3o num grupo de mensagens ou num corredor movimentado." },
  2: { name: "Suave", why: "tom claro da marca, mais leve. bom para mural e para comunica\xE7\xE3o de gente." }
};
function modoFontLabel(label) {
  const italic = /\b(italic|it[aá]lico)\b/i.test(label);
  const w = /\b([1-9]00)\b/.exec(label);
  return { family: label.replace(/\b(italic|it[aá]lico)\b/gi, "").replace(/\b[1-9]00\b/, "").replace(/\s+/g, " ").trim(), weight: w ? Number(w[1]) : 400, italic };
}
function modoMoldOf(i) {
  const d = i.data ?? {};
  const pick = (v, list2) => list2.includes(v) ? v : list2[0];
  const colors = (Array.isArray(d.backgrounds) ? d.backgrounds : []).filter((c) => isHexColor(c.hex)).slice(0, 3).map((c) => ({ name: c.name || c.hex, hex: c.hex }));
  return {
    id: i.id,
    title: i.title,
    note: i.body,
    applies: i.applies ?? null,
    backgrounds: colors,
    logo: pick(d.logoPosition, ["bottom-left", "bottom-right", "bottom-center", "top-left", "top-right"]),
    align: pick(d.align, ["left", "center"]),
    headingCase: pick(d.headingCase, ["normal", "upper"]),
    headingWeight: pick(d.headingWeight, ["heavy", "bold", "regular"]),
    bar: pick(d.bar, ["none", "top", "left"]),
    pattern: pick(d.pattern, ["none", "corner", "full"]),
    eyebrow: pick(d.eyebrow, ["show", "hide"]),
    print: pick(d.print, ["digital", "offset", "both"])
  };
}
var MODO_KIT_FILE_MAX = 7e5;
var MODO_KIT_LOGOS_MAX = 5;
var MODO_KIT_FONTS_MAX = 8;
var rgb = (hex2) => {
  const h = hex2.replace("#", "");
  const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)];
};
var hex = (c) => `#${c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`;
var mix = (a, b, t) => {
  const [x, y] = [rgb(a), rgb(b)];
  return hex(x.map((v, i) => v + (y[i] - v) * t));
};
function colorLuminance(color) {
  const [r, g, b] = rgb(color).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
var isHexColor = (v) => typeof v === "string" && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim());
function modoKitColors(primary) {
  const color = primary.trim();
  return { color, dark: mix(color, "#000000", 0.45), soft: mix(color, "#ffffff", 0.9), ink: mix(color, "#0b0b0b", 0.82) };
}
var MODO_REQUEST_TYPES = [
  { key: "aviso_interno", label: "aviso interno", audience: "internal", risk: "low", kinds: ["a4", "card"], hint: "equipamento parado, mudan\xE7a de sala, hor\xE1rio, lembrete para a equipe" },
  { key: "comunicado", label: "comunicado", audience: "internal", risk: "medium", kinds: ["a4", "card"], hint: "decis\xE3o, mudan\xE7a de regra ou de processo para a empresa" },
  { key: "convite_interno", label: "convite interno", audience: "internal", risk: "low", kinds: ["card", "a4", "story"], hint: "treinamento, reuni\xE3o, evento, anivers\xE1rio, confraterniza\xE7\xE3o" },
  { key: "aviso_publico", label: "aviso ao p\xFAblico", audience: "external", risk: "low", kinds: ["a4", "card"], hint: "aviso que cliente ou visitante l\xEA: hor\xE1rio de atendimento, fechamento, obra" },
  { key: "vaga", label: "vaga", audience: "external", risk: "medium", kinds: ["card", "story"], hint: "divulga\xE7\xE3o de vaga de trabalho" },
  { key: "post_institucional", label: "post institucional", audience: "external", risk: "medium", kinds: ["card", "story"], hint: "data comemorativa, not\xEDcia da empresa, agradecimento, para redes" },
  { key: "material_comercial", label: "material comercial", audience: "external", risk: "high", kinds: ["card", "a4", "story"], hint: "oferta, produto, pre\xE7o, condi\xE7\xE3o, argumento de venda" },
  { key: "campanha", label: "campanha externa", audience: "external", risk: "high", kinds: ["card", "story", "a4"], hint: "campanha para o p\xFAblico, com verba ou com promessa" },
  { key: "apresentacao", label: "apresenta\xE7\xE3o", audience: "internal", risk: "low", kinds: ["a4", "card"], hint: "apresenta\xE7\xE3o para a diretoria ou a equipe: de resultados, com os n\xFAmeros da marca (acessos ao site, busca, redes, destaques e sugest\xF5es), ou de marca, um guia do que o BIS diz (identidade, voz, p\xFAblicos, visual, regras)" },
  { key: "assinatura", label: "assinatura de e-mail", audience: "internal", risk: "low", kinds: ["signature"], hint: "assinatura de e-mail de uma pessoa ou da equipe" },
  { key: "cartao", label: "cart\xE3o de visita", audience: "internal", risk: "low", kinds: ["bizcard"], hint: "cart\xE3o de visita de uma pessoa ou da equipe" }
];
var MODO_KIND_CHANNELS = { a4: ["print", "place"], card: ["whatsapp", "instagram", "facebook", "linkedin"], story: ["instagram", "facebook", "whatsapp"], signature: ["email"], bizcard: ["print"] };
var MODO_STATIC_TYPES = ["assinatura", "cartao", "papelaria", "placa"];
var modoPhotoQuery = (piece) => (piece.photoAsk?.trim() || [piece.eyebrow, piece.headline, modoPlain(piece.body)].filter(Boolean).join(". ")).slice(0, 400);
function modoAsksPartner(text) {
  const t = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (!/\b(logotipo|logomarca|(o|os|um|esse|este|desse|deste|aquele|seu|nosso|novo)\s+(logo|logos)|marca do (cliente|hotel|parceiro|patrocinador|evento)|marca deles|marca dele|marca dela)\b/.test(t)) return null;
  if (/\b(tir[aeo]|remov[aeo]|sem o logo|apag[aeu])/.test(t) && /\b(parceir|cliente|hotel|patrocin|dele|dela|deles|outro|outra)/.test(t)) return "remove";
  if (/\b(us[aeo]r?|coloc|adicion|inclu|poe|ponha|inser|junt|aplic|acrescent|bot[ae]|enviei|anexei|mandei|pode usar)/.test(t)) return "add";
  return null;
}
var modoExpertFits = (piece) => !piece.person && (piece.kind === "a4" || piece.kind === "card" || piece.kind === "story") && !MODO_STATIC_TYPES.includes(piece.type ?? "");
var modoHasPreview = (kind) => !kind || kind === "card" || kind === "story";
var modoBisContext = (type, kind, program) => ({ piece: type.key, channels: MODO_KIND_CHANNELS[kind], audiences: [type.audience], program: program ?? null });
function modoMoldsFor(molds, piece, programs) {
  if (!molds.length) return [];
  const theme = (piece.program ? programs?.find((x) => x.id === piece.program)?.colors : null) ?? [];
  if (theme.length) molds = molds.map((m) => (m.applies?.programs ?? []).length ? m : { ...m, backgrounds: [...theme.map((c) => ({ name: c.name, hex: c.hex })), ...m.backgrounds.filter((b) => !theme.some((c) => c.hex.toLowerCase() === b.hex.toLowerCase()))].slice(0, 3) });
  const type = MODO_REQUEST_TYPES.find((t) => t.key === piece.type);
  const pool = piece.person ? molds.filter((m) => type && (m.applies?.pieces ?? []).includes(type.key)) : molds.filter((m) => !(m.applies?.pieces ?? []).some((p) => p === "assinatura" || p === "cartao") || type && (m.applies?.pieces ?? []).includes(type.key));
  return bisFor(pool, type ? modoBisContext(type, piece.kind, piece.program) : { channels: MODO_KIND_CHANNELS[piece.kind], program: piece.program ?? null });
}
function modoMoldFor(molds, piece, programs) {
  const list2 = modoMoldsFor(molds, piece, programs);
  return (piece.mold ? list2.find((m) => m.id === piece.mold) : null) ?? list2[0] ?? null;
}
var modoVariantCount = (mold) => mold ? Math.max(1, Math.min(3, mold.backgrounds.length)) : 0;
var modoRequestType = (key) => MODO_REQUEST_TYPES.find((t) => t.key === key) ?? MODO_REQUEST_TYPES[0];
var MODO_ROUTES = ["auto", "area", "guardian", "legal"];
var MODO_ROUTE_LABELS = { auto: "pode circular", area: "passa pelo respons\xE1vel da \xE1rea", guardian: "passa pelo marketing", legal: "passa pelo marketing com o jur\xEDdico" };
function modoRoute(input) {
  const hard = input.checks.find((c) => c.verdict === "violation" && c.ruleKind === "compliance");
  if (hard) return { route: "legal", why: `uma regra de conformidade foi acionada: ${hard.title}` };
  if (input.personOnly) return { route: "auto", why: "molde fixo da marca: s\xF3 mudam nome, cargo e contato" };
  const soft = input.checks.find((c) => c.verdict === "violation");
  if (input.type.audience === "external") return { route: "guardian", why: `${input.type.label} vai para fora da empresa` };
  if (input.type.risk === "high") return { route: "guardian", why: `${input.type.label} \xE9 um tipo de risco alto` };
  if (soft) return { route: "area", why: `a pe\xE7a fere uma regra da marca: ${soft.title}` };
  if (input.type.risk === "medium") return { route: "area", why: `${input.type.label} \xE9 interno, de risco m\xE9dio` };
  return { route: "auto", why: `${input.type.label}, interno e de risco baixo, dentro das regras conferidas` };
}
var MODO_JUSTIFICATION_MIN = 12;
var modoStatusOf = (route) => route === "auto" ? "approved" : route === "legal" ? "blocked" : "pending";
function modoClassesFor(role) {
  return role === "client" || role === "network" ? ["all"] : ["all", "internal", "restricted"];
}
var modoReadable = (items, classes) => items.filter((i) => classes.includes(i.infoClass));
var plain2 = (t) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
var hasTerm = (text, term) => {
  const t = plain2(term).trim();
  return t.length > 1 && new RegExp(`(^|[^a-z0-9])${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(plain2(text));
};
var list = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
var modoPieceText = (p) => [p.eyebrow, p.headline, [p.highlight?.label, p.highlight?.detail].filter(Boolean).join(", "), p.body.replace(/\*\*/g, ""), p.footer, p.person ? `${p.person.name} ${p.person.role}` : "", ...(p.legal ?? []).map((l) => l.text)].filter(Boolean).join("\n");
function modoVocabularyChecks(piece, items) {
  const text = modoPieceText(piece);
  const out = [];
  for (const i of items) {
    if (i.kind !== "vocabulary") continue;
    const never = list(i.data?.never).filter((w) => hasTerm(text, w));
    const avoid = list(i.data?.avoid).filter((w) => hasTerm(text, w));
    const swaps = (Array.isArray(i.data?.swaps) ? i.data.swaps : []).filter((s) => s.from && hasTerm(text, s.from));
    if (never.length) out.push({ itemId: i.id, title: i.title, force: "never", ruleKind: "brand", verdict: "violation", note: `a marca nunca usa: ${never.join(", ")}`, by: "rule" });
    if (avoid.length) out.push({ itemId: i.id, title: i.title, force: "should", ruleKind: "brand", verdict: "attention", note: `a marca evita: ${avoid.join(", ")}`, by: "rule" });
    for (const s of swaps) out.push({ itemId: i.id, title: i.title, force: "should", ruleKind: "brand", verdict: "attention", note: `em vez de "${s.from}", a marca diz "${s.to ?? ""}"`, by: "rule" });
    if (!never.length && !avoid.length && !swaps.length && (list(i.data?.never).length || list(i.data?.avoid).length)) out.push({ itemId: i.id, title: i.title, force: null, ruleKind: "brand", verdict: "ok", note: "nenhuma palavra que a marca evita ou pro\xEDbe", by: "rule" });
  }
  return out;
}
function modoRuleEdit(piece, input) {
  const text = input.trim();
  const swap = /^troqu?e\s+["“']?(.+?)["”']?\s+(?:para|por)\s+["“']?(.+?)["”']?[.!]?$/i.exec(text);
  if (swap) {
    const [, from, to] = swap;
    const re = new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const person = piece.person ? { name: piece.person.name.replace(re, to), role: piece.person.role.replace(re, to), phone: piece.person.phone.replace(re, to), email: piece.person.email.replace(re, to) } : void 0;
    const next = { ...piece, ...person ? { person } : {}, headline: piece.headline.replace(re, to), body: piece.body.replace(re, to), footer: piece.footer.replace(re, to), eyebrow: piece.eyebrow.replace(re, to) };
    return JSON.stringify(next) === JSON.stringify(piece) ? null : { piece: next, said: `Pronto, troquei "${from}" por "${to}".` };
  }
  if (piece.person) {
    const f = /^(nome|cargo|telefone|e-?mail|registro)\s*[:=]\s*(.+)$/i.exec(text);
    if (f) {
      const key = { nome: "name", cargo: "role", telefone: "phone", email: "email", "e-mail": "email", registro: "extra" }[f[1].toLowerCase()];
      return { piece: { ...piece, person: { ...piece.person, [key]: f[2].trim() } }, said: `Pronto, ${f[1].toLowerCase()} trocado.` };
    }
  }
  const field = /^(t[ií]tulo|chamada|texto|corpo|rodap[eé]|assinatura)\s*[:=]\s*(.+)$/is.exec(text);
  if (field && !piece.person) {
    const k = plain2(field[1]);
    const key = k === "titulo" || k === "chamada" ? "headline" : k === "texto" || k === "corpo" ? "body" : "footer";
    return { piece: { ...piece, [key]: field[2].trim() }, said: `Pronto, ${field[1].toLowerCase()} trocado.` };
  }
  const short = plain2(text).split(/\s+/).length <= 6;
  if (short && /outr[ao] (cor|versao|opcao|molde)|mud[ae] o (visual|layout|fundo|molde)/.test(plain2(text))) return { piece: { ...piece, variant: (piece.variant + 1) % 3 }, said: "Pronto: outra base da marca, com o mesmo texto." };
  if (piece.person || !short) return null;
  if (/\b(story|stories)\b/.test(plain2(text))) return { piece: { ...piece, kind: "story" }, said: "Pronto, passei para story 9:16." };
  if (/\ba4\b/.test(plain2(text))) return { piece: { ...piece, kind: "a4" }, said: "Pronto, passei para A4." };
  if (/\b(card|quadrado|feed|whats|whatsapp)\b/.test(plain2(text))) return { piece: { ...piece, kind: "card" }, said: "Pronto, passei para card." };
  return null;
}
var modoBaseRoles = (roles, program) => ["eyebrow", "headline", "body", "cta"].filter((r) => roles.includes(r)).join("+") + (program ? `@${program}` : "");
var MODO_BASE_RETIRE_AT = 3;
var MODO_COMPOSE_ESTIMATE_USD = 0.45;
var MODO_TURN_ESTIMATE_USD = 0.08;
var MODO_DECISION_CONFIDENCE = 0.8;
var MODO_GUARD_MIN = 0.75;
var MODO_REUSE_MAX_DISTANCE = 0.42;
var MODO_REUSE_SURE_DISTANCE = 0.3;
var MODO_EXAMPLES_MAX = 3;
var MODO_PREVIEW_CHANNELS = ["instagram", "story", "facebook", "linkedin", "email"];
var MODO_PREVIEW_LABELS = { instagram: "Instagram", story: "Story", facebook: "Facebook", linkedin: "LinkedIn", email: "E-mail" };
function modoAskChannels(ask) {
  const t = ask.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const has = (re) => re.test(t);
  const out = /* @__PURE__ */ new Set();
  if (has(/\b(instagram|insta|feed|reels?)\b/)) {
    out.add("instagram");
    out.add("story");
    out.add("facebook");
  }
  if (has(/\b(story|stories)\b/)) out.add("story");
  if (has(/\b(facebook|face)\b/)) out.add("facebook");
  if (has(/\blinkedin\b/)) out.add("linkedin");
  if (has(/\b(e-?mail marketing|email mkt|e-?mail mkt|newsletter|e-?mail)\b/)) out.add("email");
  return MODO_PREVIEW_CHANNELS.filter((c) => out.has(c));
}
var modoAsksSocial = (ask) => /\b(post|posts|feed|instagram|insta|facebook|linkedin|rede social|redes sociais|story|stories|reels?)\b/i.test(ask.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
var MODO_NO_CAPTION = "LEGENDA: n\xE3o precisa";
var modoUnitText = (u) => [`${u.name}${u.address ? `, ${u.address}` : ""}${u.city ? `, ${u.city}${u.uf ? `/${u.uf}` : ""}` : ""}`, u.phone && `Telefone: ${u.phone}`, u.hours && `Atendimento: ${u.hours}`].filter(Boolean).join(". ");
var MODO_READY_EMAIL_AFTER_MIN = 3;
var MODO_ATTACH_KINDS = ["image", "pdf", "sheet"];
var MODO_ATTACH_MAX = 5;
var MODO_ATTACH_BYTES = 20 * 1024 * 1024;
var MODO_ATTACH_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,application/pdf,.pdf,.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
function modoAttachKind(name, mime) {
  const ext = name.toLowerCase().split(".").pop() ?? "", m = mime.toLowerCase();
  if (m === "application/pdf" || ext === "pdf") return "pdf";
  if (["xlsx", "csv"].includes(ext) || m.includes("spreadsheetml") || m === "text/csv") return "sheet";
  if (m.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic"].includes(ext)) return ext === "gif" || m === "image/gif" || m === "image/svg+xml" ? null : "image";
  return null;
}
function modoAttachRefusal(name) {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (ext === "xls") return "planilha .xls antiga eu n\xE3o leio: salve como .xlsx e envie de novo";
  if (ext === "doc" || ext === "docx") return "documento do Word eu ainda n\xE3o leio: salve como PDF e envie de novo";
  return "aceito imagem, planilha (.xlsx ou .csv) e PDF";
}
var MODO_ATTACH_READING_MAX = 12e3;
var MODO_ATTACH_PROMPT_MAX = 6e3;
var MODO_DATA_NEGATED = /\b(sem|nao|nem|menos|exceto|tirando|fora)\b[^.,;:!?()]{0,60}/g;
var modoAsksData = (ask) => /\b(acessos?|visitas?|sessoes|usuarios|trafego|organicos?|palavras?[- ]chaves?|posic(ao|oes) no google|ranking|busca do google|search console|analytics|ga4|metricas?|indicadores?|numeros|desempenho|resultados?|relatorios?|seguidores|alcance|engajamento|impressoes|cliques)\b/.test(ask.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(MODO_DATA_NEGATED, " "));
var MODO_SLIDE_KINDS = ["cover", "numbers", "chart", "bullets", "image", "closing", "big", "cards", "statement"];
var MODO_SLIDE_KIND_LABELS = { cover: "capa", numbers: "n\xFAmeros", chart: "gr\xE1fico", bullets: "lista", image: "foto", closing: "fecho", big: "n\xFAmero em destaque", cards: "cart\xF5es", statement: "frase de destaque" };
var modoSlideHasStats = (kind) => kind === "numbers" || kind === "big";
function modoShortNumber(value) {
  const v = value.trim();
  if (!/^\d{1,3}(\.\d{3})+$/.test(v)) return value;
  const n = Number(v.replace(/\./g, ""));
  if (n >= 1e6) return `${(Math.floor(n / 1e5) / 10).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`;
  if (n >= 1e4) return `${Math.floor(n / 1e3)} mil`;
  return value;
}
var MODO_PHOTO_PLACES = ["right", "left", "bottom", "corner", "full"];
var MODO_PHOTO_PLACE_LABELS = { right: "\xE0 direita", left: "\xE0 esquerda", bottom: "em faixa embaixo", corner: "no canto", full: "de fundo" };
var modoPhotoPlaceOk = (kind, place) => place === "right" || place === "left" || place === "full" || (place === "bottom" ? !["cards", "chart", "big"].includes(kind) : !["cards", "chart", "big", "numbers"].includes(kind));
var MODO_PHOTO_MIN = 0.12;
function modoPhotoBox(b) {
  const v = b;
  if (!v || ![v.x, v.y, v.w, v.h].every((n) => typeof n === "number" && Number.isFinite(n))) return void 0;
  const r = (n) => Math.round(n * 1e3) / 1e3, w = Math.min(1, Math.max(MODO_PHOTO_MIN, v.w)), h = Math.min(1, Math.max(MODO_PHOTO_MIN, v.h));
  return { x: r(Math.min(1 - w, Math.max(0, v.x))), y: r(Math.min(1 - h, Math.max(0, v.y))), w: r(w), h: r(h) };
}
function modoPhotoFocus(f) {
  const v = f, snap = (n) => n === 0 || n === 1 ? n : 0.5;
  return v && (typeof v.x === "number" || typeof v.y === "number") ? { x: snap(v.x), y: snap(v.y) } : void 0;
}
var MODO_CARDS_MAX = 5;
var MODO_TEXT_KEYS = ["eyebrow", "title", "text", "bullets"];
var MODO_TEXT_SCALE = { min: 0.7, max: 1.5 };
var MODO_BOX_WIDTH = { min: 0.35, max: 1.25 };
var MODO_SLIDE_EDIT_ESTIMATE_USD = 0.05;
var MODO_SLIDE_ASK_MAX = 600;
var MODO_DECK_MAX = 12;
var MODO_DECK_ESTIMATE_USD = 0.15;
var MODO_EMPHASIS_MAX = 3;
var modoPlain = (text) => text.replace(/\*\*/g, "");
function modoEmphasis(text) {
  const parts = text.split("**");
  if (parts.length < 3 || parts.length % 2 === 0) return modoPlain(text);
  let used = 0;
  const plain3 = modoPlain(text).trim().length;
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    const inner = part.trim();
    if (!inner || inner.includes("\n") || inner.length > 60 || inner.length > plain3 * 0.6 || used >= MODO_EMPHASIS_MAX) return part;
    used++;
    return `**${inner}**`;
  }).join("");
}
var MODO_ICON_OPTIONS = 3;
var MODO_ICON_SUBJECT_MAX = 60;
var MODO_ICON_ESTIMATE_USD = 0.08;
var MODO_ICON_GPT_ESTIMATE_USD = 0.3;
var MODO_ICON_COMPARE_MAX = 4;
var MODO_ICON_SOLID_SOFT = 1;
var ICON_SHAPE = /^<svg[^>]*>(\s*<(path|circle|ellipse|rect|line|polyline|polygon)\b[^<>]*\/>\s*)+<\/svg>$/;
function modoIconSvg(raw, cap, solid = false) {
  const stroke = `stroke-width="2" stroke-linecap="${cap}" stroke-linejoin="round"`;
  let svg = raw.trim().replace(/>\s+</g, "><").replace(/<(path|circle|ellipse|rect|line|polyline|polygon)\b([^<>]*?)\s*><\/\1>/g, "<$1$2/>").replace(/\sstroke-(width|linecap|linejoin)="[^"]*"/g, "").replace(/^<svg[^>]*>/, solid ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="${MODO_ICON_SOLID_SOFT}" stroke-linejoin="round">` : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" ${stroke}>`).replace(/\brx="([\d.]+)"/g, (_, r) => `rx="${Math.min(1.5, Number(r))}"`).replace(/\bry="[\d.]+"/g, "");
  if (solid) svg = svg.replace(/<(path|circle|ellipse|rect|line|polyline|polygon)\b([^<>]*)\/>/g, (el, tag, rest) => /\sfill="none"/.test(rest) ? el : `<${tag}${rest.replace(/\sstroke="none"/g, "").replace(/\s+$/, "")}/>`);
  if (solid) svg = svg.replace(/<(path|circle|ellipse|rect|line|polyline|polygon)\b([^<>]*\sstroke="currentColor"[^<>]*)\/>/g, (_, tag, rest) => `<${tag}${rest.replace(/\s+$/, "")} ${stroke}/>`);
  if (svg.length > 7e3 || !ICON_SHAPE.test(svg)) return null;
  const inner = svg.replace(/^<svg[^>]*>/, "").replace(/<\/svg>$/, "");
  for (const m of inner.matchAll(/\s([a-zA-Z:-]+)="([^"]*)"/g)) {
    const [, name = "", value = ""] = m;
    if (["d", "cx", "cy", "r", "rx", "x", "y", "x1", "y1", "x2", "y2", "width", "height", "points", "fill-rule", "clip-rule"].includes(name)) {
      if (!/^[\sA-Za-z0-9.,+-]*$/.test(value)) return null;
      continue;
    }
    if ((name === "fill" || name === "stroke") && /^(currentColor|none)$/.test(value)) continue;
    if (solid && (name === "stroke-width" && value === "2" || name === "stroke-linecap" && value === cap || name === "stroke-linejoin" && value === "round")) continue;
    return null;
  }
  return svg;
}
var MODO_LAYER_ZOOM_MAX = 2.5;
function modoPhotoPlace(frame, ratio, focus, zoom) {
  const z = Math.min(MODO_LAYER_ZOOM_MAX, Math.max(1, zoom || 1)), fx = Math.min(1, Math.max(0, focus.x)), fy = Math.min(1, Math.max(0, focus.y));
  const wide = frame.w / frame.h < ratio;
  const h = (wide ? frame.h : frame.w / ratio) * z, w = h * ratio;
  return { x: frame.x - (w - frame.w) * fx, y: frame.y - (h - frame.h) * fy, w, h };
}
function modoFacesOnPiece(faces, at) {
  return faces.map((f) => ({ x: at.x + f.x * at.w, y: at.y + f.y * at.h, w: f.w * at.w, h: f.h * at.h }));
}

// src/rastro.ts
var RASTRO_MOVE_KINDS = ["lancamento", "estrutura", "comunicacao", "canal"];
var RASTRO_MOVE_KIND_LABELS = { lancamento: "lan\xE7amento", estrutura: "estrutura", comunicacao: "comunica\xE7\xE3o", canal: "canal" };
var rastroMoveKind = (raw) => {
  const s = raw.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase();
  return s.startsWith("estr") ? "estrutura" : s.startsWith("comu") ? "comunicacao" : s.startsWith("can") ? "canal" : "lancamento";
};
var RASTRO_THEME_MIN_BRANDS = 4;
var RASTRO_WINDOW_DAYS = 105;
var RASTRO_SWEEP_DAYS = 7;
var RASTRO_LIST_PAGES_MAX = 36;
var RASTRO_SECTOR_SKIP = 0.1;
var RASTRO_SAME_FACT_DAYS = 45;
var RASTRO_SAME_FACT_YES = 0.8;
var RASTRO_SAME_FACT_NO = 0.2;
var RASTRO_BRIDGE_MIN = 4;
var RASTRO_CASE_ESTIMATE_USD = 0.75;
var RASTRO_SWEEP_ESTIMATE_USD = 1.5;
var RASTRO_SEARCH_DAYS = 28;
var RASTRO_WEB = "web";
var RASTRO_FRESH_DAYS = 14;
var RASTRO_EMBALAGEMMARCA = { key: "embalagemmarca", name: "EmbalagemMarca", list: "https://embalagemmarca.com.br/category/noticias/feed/?paged={n}", origin: "https://embalagemmarca.com.br", reader: "feed", broad: true };
var RASTRO_GIRONEWS = { key: "gironews", name: "Giro News", list: "https://gironews.com/categoria/lancamentos/page/{n}/", origin: "https://gironews.com", broad: true };
var RASTRO_ENGARRAFADOR = { key: "engarrafador", name: "Engarrafador Moderno", list: "https://engarrafadormoderno.com.br/feed/?paged={n}", origin: "https://engarrafadormoderno.com.br", reader: "feed", broad: true };
var RASTRO_SECTORS = [
  { key: "laticinios", name: "Latic\xEDnios", place: "Brasil", about: "latic\xEDnios e bebidas l\xE1cteas", vehicles: [
    { key: "milkpoint", name: "MilkPoint", list: "https://www.milkpoint.com.br/noticias-e-mercado/giro-noticias/?pg={n}", origin: "https://www.milkpoint.com.br" },
    { key: "revistalaticinios", name: "Revista Latic\xEDnios", list: "https://www.revistalaticinios.com.br/feed/?paged={n}", origin: "https://www.revistalaticinios.com.br", reader: "feed" },
    RASTRO_EMBALAGEMMARCA
  ] },
  // a primeira versão (`molhos-e-orientais`, com ketchup e atomatado) era larga demais: 15 movimentos e nenhum de shoyu
  { key: "culinaria-oriental", name: "Culin\xE1ria japonesa e oriental", place: "Brasil", about: "shoyu, molhos orientais (tar\xEA, teriyaki, molho de ostra), miss\xF4, l\xE1men e demais produtos de culin\xE1ria japonesa e oriental vendidos no varejo", vehicles: [RASTRO_GIRONEWS, RASTRO_EMBALAGEMMARCA], windowDays: 365 },
  { key: "bebidas-alcoolicas", name: "Bebidas alco\xF3licas", place: "Brasil", about: "bebidas alco\xF3licas (destilados, vodca, cacha\xE7a, gim, u\xEDsque, bebidas prontas com \xE1lcool, cerveja, vinho e espumante)", vehicles: [RASTRO_ENGARRAFADOR, RASTRO_GIRONEWS, RASTRO_EMBALAGEMMARCA] },
  // os veículos do setor são de associação e cobrem economia, varejo e eventos em geral (`broad`); quem traz marca e produto é a pesquisa na web
  { key: "festas-e-baloes", name: "Festas e bal\xF5es", place: "Brasil", about: "artigos para festas e bal\xF5es profissionais para decora\xE7\xE3o (bal\xF5es de l\xE1tex, metalizados e bubble, arcos e esculturas de bal\xE3o, g\xE1s h\xE9lio e inflador, descart\xE1veis, pain\xE9is e artigos tem\xE1ticos de festa), vendidos a decoradores, lojas de festa e ao consumidor", vehicles: [
    { key: "asbrafe", name: "Asbrafe", list: "https://www.asbrafe.com.br/blog-feed.xml", origin: "https://www.asbrafe.com.br", reader: "feed", broad: true },
    { key: "abrafesta", name: "Abrafesta", list: "https://www.abrafesta.com.br/feed/?paged={n}", origin: "https://www.abrafesta.com.br", reader: "feed", broad: true }
  ], windowDays: 365, consumer: {
    // medido em 14/out/2026 (docs/evals/consumidor-festas-2026-10-14/): termos e buscas que trouxeram o fazer e o dizer
    terms: ["arco de bal\xE3o", "bal\xE3o bubble", "decora\xE7\xE3o de festa", "bal\xE3o personalizado", "painel de festa", "ch\xE1 revela\xE7\xE3o", "mesvers\xE1rio", "festa em casa", "pegue e monte", "arco desconstru\xEDdo", "decora\xE7\xE3o de anivers\xE1rio simples", "bal\xE3o metalizado"],
    videoSearches: ["arco de bal\xE3o como fazer", "decora\xE7\xE3o de festa em casa", "pegue e monte festa", "bal\xE3o bubble personalizado", "decora\xE7\xE3o anivers\xE1rio simples com bal\xF5es", "decora\xE7\xE3o ch\xE1 revela\xE7\xE3o bal\xF5es"],
    exclude: ["unha", "unhas", "nail", "nails", "esmalte", "maquiagem", "cabelo", "perfil decorado", "compartidos", "outfit", "look"]
  } },
  // tecnologia e serviço para a operação da cozinha profissional (pedido do Tiago, 16/out/2026, para a Granobox). Não há veículo só
  // do setor: os quatro cobrem foodservice em geral (`broad`); quem traz os fornecedores de tecnologia é a pesquisa na web.
  // A Food Connection não pagina (o RSS traz só as dez últimas): serve daqui para a frente. O Food Safety Brazil ficou de fora
  // (um item por página do RSS). Os quatro responderam do servidor de produção em 16/out/2026, e nenhum robots.txt proíbe.
  { key: "tecnologia-foodservice", name: "Tecnologia para foodservice", place: "Brasil", about: "tecnologia e servi\xE7os para a opera\xE7\xE3o de cozinhas profissionais (restaurantes, redes, hot\xE9is, refei\xE7\xF5es coletivas, dark kitchens): etiquetagem e controle de validade, seguran\xE7a de alimentos e conformidade sanit\xE1ria (boas pr\xE1ticas, APPCC, fiscaliza\xE7\xE3o), gest\xE3o de estoque, produ\xE7\xE3o, fichas t\xE9cnicas e custos, rastreabilidade, sistemas de gest\xE3o e PDV para restaurantes, automa\xE7\xE3o e cozinha conectada, e as regras e exig\xEAncias novas que mudam o que a cozinha precisa controlar", vehicles: [
    { key: "foodconnection", name: "Food Connection", list: "https://foodconnection.com.br/feed/?paged={n}", origin: "https://foodconnection.com.br", reader: "feed", broad: true },
    { key: "foodbiz", name: "FoodBiz", list: "https://foodbizbrasil.com/feed/?paged={n}", origin: "https://foodbizbrasil.com", reader: "feed", broad: true },
    { key: "aberc", name: "ABERC", list: "https://www.aberc.com.br/feed/?paged={n}", origin: "https://www.aberc.com.br", reader: "feed", broad: true },
    { key: "anr", name: "ANR", list: "https://anrbrasil.org.br/feed/?paged={n}", origin: "https://anrbrasil.org.br", reader: "feed", broad: true }
  ], windowDays: 365 },
  { key: "massas", name: "Massas", place: "Brasil", about: "massas aliment\xEDcias (macarr\xE3o, massa instant\xE2nea, massa fresca e recheada, lasanha) e molhos para massa", vehicles: [RASTRO_GIRONEWS, RASTRO_EMBALAGEMMARCA], windowDays: 365 }
];
var rastroSector = (key) => RASTRO_SECTORS.find((s) => s.key === key) ?? null;
var rastroWindowDays = (r) => r.windowDays ?? RASTRO_WINDOW_DAYS;
function rastroSectorsOf(groupSectors, brandSectors) {
  const list2 = brandSectors ?? groupSectors ?? [];
  return [...new Set(list2)].filter((k) => rastroSector(k));
}
var rastroBrandKey = (b) => b.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/^grupo /, "");
var RASTRO_STATUSES = ["none", "pending", "ready", "failed"];
var RASTRO_THEME_STATUSES = ["active", "gone", "dropped"];
var RASTRO_CONSUMER_DAYS = 28;
var RASTRO_CONSUMER_VIDEOS = 15;
var RASTRO_CONSUMER_COMMENTS = 8;
var RASTRO_CONSUMER_ESTIMATE_USD = 1.8;
var RASTRO_VIDEO_OUTLIER_TIMES = 3;
var RASTRO_VIDEO_OUTLIER_MIN = 500;
var RASTRO_LOUD_VIDEOS = 5;
var RASTRO_LOUD_SHARE = 0.1;
var RASTRO_SIGNAL_KINDS = ["habit", "rising", "says"];
var RASTRO_SIGNAL_KIND_LABELS = { habit: "h\xE1bito", rising: "em alta", says: "o que dizem" };
var RASTRO_CROSS_LABELS = { together: "junto", consumer: "s\xF3 consumidor", brand: "s\xF3 marca" };
var avgOf = (xs) => xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN;
var pctOf = (a, b) => Number.isFinite(a) && Number.isFinite(b) && b > 0 ? Math.round((a - b) / b * 100) : null;
var RASTRO_RISING_YEAR = 10;
var RASTRO_RISING_RECENT = 10;
function rastroTermTrend(curve, now = Date.now()) {
  const DAY = 864e5;
  const pts = Object.entries(curve).map(([d, v]) => ({ t: Date.parse(d), v: Number(v) })).filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
  const avg = (from, to) => avgOf(pts.filter((p) => p.t >= now - from * DAY && p.t < now - to * DAY).map((p) => p.v));
  const byMonth = Array.from({ length: 12 }, (_, m) => avgOf(pts.filter((p) => new Date(p.t).getUTCMonth() === m).map((p) => p.v)));
  const best = Math.max(...byMonth.filter(Number.isFinite));
  const peakMonth = Number.isFinite(best) ? byMonth.indexOf(best) : null;
  const year = pctOf(avg(365, 0), avg(730, 365));
  const prevYear = pctOf(avg(730, 365), avg(1095, 730));
  const recent = pctOf(avg(90, 0), avg(455, 365));
  const months = new Set([0, 30, 60, 89].map((d) => new Date(now - d * DAY).getUTCMonth()));
  const inPeak = peakMonth !== null && months.has(peakMonth);
  const rising = recent !== null && recent >= RASTRO_RISING_RECENT && (year ?? 0) >= 0 || year !== null && year >= RASTRO_RISING_YEAR && (recent ?? 0) >= -5;
  return { year, prevYear, recent, peakMonth, inPeak, rising, season: !rising && inPeak && (year ?? 0) > 0 };
}

// src/modo-layout.ts
var rgb2 = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
var composeLum = (h) => {
  const [r, g, b] = rgb2(h).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
var composeContrast = (a, b) => (Math.max(composeLum(a), composeLum(b)) + 0.05) / (Math.min(composeLum(a), composeLum(b)) + 0.05);
var bare = (w) => w.toLowerCase().replace(/[.,;:!?"“”()]/g, "");
var hit = (a, b, pad = 0) => Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > pad && Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) > pad;
function composeSnap(color, brand, fallback) {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return fallback;
  const a = rgb2(color);
  return [...brand.palette.map((p) => p.hex), "#FFFFFF"].map((p) => [p, rgb2(p).reduce((s, v, i) => s + (v - a[i]) ** 2, 0)]).sort((x, y) => x[1] - y[1])[0][0];
}
function composeLayout(spec, texts, brand, type) {
  const { w: W, h: H } = spec;
  const held = spec.panels.find((p) => p.holds) ?? null;
  let squeeze = spec.tune.widen;
  if (held) {
    const right = held.x + held.w * squeeze;
    const blockers = spec.people.filter((p) => p.x > held.x + held.w * 0.35 && right - p.x > p.w * 0.34 && p.y < held.y + held.h && p.y + p.h > held.y);
    if (blockers.length) squeeze = Math.max(0.55, Math.min(squeeze, (Math.min(...blockers.map((p) => p.x + p.w * 0.08)) - held.x) / held.w));
  }
  const holder = held ? { ...held, w: held.w * squeeze } : null;
  const inner = (b) => holder ? Math.min(b.w * Math.max(squeeze, spec.tune.widen), holder.x + holder.w - W * 0.045 - b.x) : b.w * spec.tune.widen;
  const bg = holder?.color ?? "#FFFFFF";
  const dark = composeLum(bg) < 0.42;
  const ink = dark ? "#FFFFFF" : brand.palette.filter((p) => composeContrast(p.hex, bg) >= 6).sort((a, b) => composeLum(a.hex) - composeLum(b.hex))[0]?.hex ?? "#111111";
  const accent = brand.palette.map((p) => p.hex).filter((c) => composeContrast(c, bg) >= 2.2 && c.toLowerCase() !== ink.toLowerCase()).sort((a, b) => dark ? composeLum(b) - composeLum(a) : composeLum(a) - composeLum(b))[0] ?? ink;
  const out = [], problems = [];
  let cta = null, prevBottom = 0, prevDraftBottom = 0;
  const items = [];
  for (const b of spec.blocks.slice().sort((x, y) => x.y - y.y)) {
    const raw = texts[b.role];
    if (!raw?.trim()) continue;
    const t = b.role === "eyebrow" ? raw.toUpperCase() : raw;
    const key = b.role === "body" ? spec.highlight?.text.trim() ?? "" : "";
    const at = key ? t.toLowerCase().indexOf(key.toLowerCase()) : -1;
    if (at < 0) {
      items.push({ b, text: t, hl: false, part: false });
      continue;
    }
    const before = t.slice(0, at).trim(), after = t.slice(at + key.length).replace(/^[\s,.;:]+/, "").trim();
    if (before) items.push({ b, text: before, hl: false, part: true });
    items.push({ b, text: t.slice(at, at + key.length), hl: true, part: true });
    if (after) items.push({ b, text: after, hl: false, part: true });
  }
  const marks = [];
  for (const it of items) {
    const b = it.part ? { ...it.b, breaks: [] } : it.b, text = it.text;
    if (it.hl) {
      const unit = type.width(text, brand.headingFamily, 800, 1), room = inner(b);
      const size2 = Math.min(room * 0.9 / unit, b.cap / 0.7 * 2.6, W * 0.11);
      const boxColor = spec.highlight.boxColor;
      const dark2 = brand.palette.map((p) => p.hex).sort((x, y) => composeLum(x) - composeLum(y))[0] ?? "#111111";
      const top2 = out.length ? prevBottom + size2 * 0.3 : b.y + spec.tune.shiftY * H;
      marks.push({ x: b.x, y: top2, w: unit * size2 + size2 * 0.7, h: size2 * 1.42, radius: size2 * 0.28, color: boxColor });
      out.push({ role: "highlight", text, size: size2, weight: 800, color: composeContrast(dark2, boxColor) >= composeContrast("#FFFFFF", boxColor) ? dark2 : "#FFFFFF", lines: [text], x: b.x + size2 * 0.35, y: top2 + size2 * 1.04, lineHeight: size2 });
      prevBottom = top2 + size2 * 1.42 + size2 * 0.14;
      continue;
    }
    const family = b.role === "body" ? brand.bodyFamily : brand.headingFamily;
    const weight = b.role === "headline" ? 800 : b.role === "body" ? 400 : 700;
    const tracking = b.role === "eyebrow" ? 0.12 : b.role === "headline" ? -0.015 : 0;
    const words = text.split(/\s+/).filter(Boolean);
    const width = inner(b);
    let size = b.cap / 0.7, lines;
    if (b.breaks.reduce((s, n) => s + n, 0) === words.length && b.breaks.length) {
      let at = 0;
      lines = b.breaks.map((n) => words.slice(at, at += n).join(" "));
      const widest = Math.max(...lines.map((l) => type.width(l, family, weight, 1, tracking)));
      const fit = width / widest;
      size = b.role === "headline" ? fit : Math.min(size * 1.15, fit);
    } else {
      lines = type.wrap(text, family, weight, size, width, tracking);
      for (let k = 0; k < 14 && lines.length > Math.max(1, b.breaks.length); k++) {
        size *= 0.95;
        lines = type.wrap(text, family, weight, size, width, tracking);
      }
    }
    if (b.role === "headline") {
      const unit = (ls) => Math.max(...ls.map((l) => type.width(l, family, weight, 1, tracking)));
      const whole = type.width(text, family, weight, 1, tracking);
      const options = [lines, ...[2, 3, 4, 5].map((n) => type.wrap(text, family, weight, 1, whole / n * 1.18, tracking))].filter((ls) => ls.length && ls.length <= 5);
      let best = { lines, size };
      for (const ls of options) {
        const s = Math.min(width / unit(ls), W * 0.15, H * 0.42 / (ls.length * 1.05));
        if (s > best.size * 1.06 || ls === lines && s >= best.size) best = { lines: ls, size: s };
      }
      lines = best.lines;
      size = best.size * 1.08 ** -spec.tune.headStep;
    }
    const lh2 = size * (b.role === "headline" ? 1.05 : b.role === "body" ? 1.36 : 1.2);
    const prev = out[out.length - 1];
    const gap = !out.length ? 0 : it.part ? H * 0.012 : Math.max(prev?.role === "headline" ? prev.size * 0.32 : H * 0.012, b.y - prevDraftBottom);
    let top = out.length ? prevBottom + gap : b.y + spec.tune.shiftY * H;
    if (b.role === "cta") {
      const room = Math.max(...out.map((t) => Math.max(...t.lines.map((l) => type.width(l, t.role === "body" ? brand.bodyFamily : brand.headingFamily, t.weight, t.size)))), width);
      if (type.width(text, family, weight, size) + size * 2.4 > room) size = room / (type.width(text, family, weight, 1) + 2.4);
      const bh = size * 2.4, bw = type.width(text, family, weight, size) + size * 2.4;
      const left = out.length ? Math.min(...out.map((t) => t.x)) : b.x;
      const bx = spec.align === "center" ? left + (room - bw) / 2 : left;
      top = Math.max(top - size * 0.7, prevBottom + gap);
      cta = { x: bx, y: top, w: bw, h: bh, color: spec.ctaColor ?? accent };
      out.push({ role: "cta", text, size, weight, color: composeContrast("#FFFFFF", cta.color) >= 2.4 ? "#FFFFFF" : ink, lines: [text], x: bx + size * 1.2, y: top + bh / 2 + size * 0.36, lineHeight: lh2 });
      prevBottom = top + bh;
    } else {
      const base = b.role === "eyebrow" ? accent : ink;
      const flagged = (spec.emphasis ?? []).filter((e) => e.text.trim()).map((e) => ({ words: e.text.trim().split(/\s+/).map(bare), color: composeContrast(e.color, bg) >= 2 ? e.color : accent, bold: e.bold }));
      const all = lines.flatMap((l) => l.split(" "));
      const flag = all.map(() => null);
      for (const e of flagged) for (let i = 0; i + e.words.length <= all.length; i++) if (e.words.every((w, k) => w === bare(all[i + k]))) for (let k = 0; k < e.words.length; k++) flag[i + k] = e;
      let wi = 0;
      const runs = flag.some(Boolean) ? lines.map((l) => {
        const r = [];
        for (const w of l.split(" ")) {
          const f = flag[wi++];
          const color = f && b.role !== "eyebrow" ? f.color : base, wt = f?.bold ? Math.max(weight, 700) : weight;
          const last = r[r.length - 1];
          if (last && last.color === color && last.weight === wt) last.text += ` ${w}`;
          else r.push({ text: w, color, weight: wt });
        }
        return r;
      }) : void 0;
      out.push({ role: b.role, text, size, weight, color: base, lines, x: b.x, y: top + size * 0.72, lineHeight: lh2, ...runs ? { runs } : {} });
      prevBottom = top + (lines.length - 1) * lh2 + size * 0.86;
      const box = { x: b.x, y: top, w: Math.max(...lines.map((l) => type.width(l, family, weight, size, tracking))), h: prevBottom - top };
      const covers = lines.some((l, n) => {
        const line = { x: b.x, y: top + n * lh2, w: type.width(l, family, weight, size, tracking), h: size };
        return spec.people.some((p) => hit(line, p, W * 0.012));
      });
      if (!holder && covers) problems.push(`${b.role === "headline" ? "o t\xEDtulo" : b.role === "body" ? "o texto de apoio" : "o chap\xE9u"} cobre uma pessoa`);
      if (box.x + box.w > W * 0.97) problems.push("um texto passa da borda");
    }
    prevDraftBottom = it.b.y + (Math.max(1, it.b.breaks.length) - 1) * it.b.cap * 1.75 + it.b.cap * (b.role === "cta" ? 2.4 : 1.2);
  }
  const lh = Math.min(H * 0.13, Math.max(H * 0.08, spec.logo.h)) * spec.tune.logoScale;
  const logo = { ...spec.logo, y: Math.min(H * 0.97 - lh, spec.logo.y + spec.logo.h - lh), h: lh };
  if (prevBottom > logo.y - H * 0.01 && out.some((t) => t.x < logo.x + lh * 2.2 && logo.x < t.x + W * 0.3)) problems.push("o texto desce at\xE9 o logotipo");
  if (prevBottom > H * 0.97) problems.push("o texto passa do p\xE9 da pe\xE7a");
  const firstTop = out.length ? Math.min(...out.map((t) => t.y - t.size * 0.9)) : holder?.y ?? 0;
  const panels = spec.panels.map((p) => {
    if (!(p.holds && holder)) return p;
    const y = Math.min(p.y, firstTop - W * 0.05);
    return { ...p, y, w: holder.w, h: prevBottom + W * 0.055 - y };
  });
  if (holder && spec.people.some((p) => holder.x + holder.w - p.x > p.w * 0.34 && p.x > holder.x && p.y < prevBottom && p.y + p.h > holder.y)) problems.push("o painel do texto cobre uma pessoa");
  return { ...spec, panels, texts: out, marks, cta, logo, problems };
}
var COMPOSE_OPS = ["headline_bigger", "headline_smaller", "column_wider", "column_narrower", "move_up", "move_down", "logo_bigger", "logo_smaller"];
function composeTune(tune, op) {
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  switch (op) {
    case "headline_bigger":
      return { ...tune, headStep: clamp(tune.headStep - 1, -4, 6) };
    case "headline_smaller":
      return { ...tune, headStep: clamp(tune.headStep + 1, -4, 6) };
    case "column_wider":
      return { ...tune, widen: clamp(tune.widen * 1.1, 0.6, 1.5) };
    case "column_narrower":
      return { ...tune, widen: clamp(tune.widen * 0.9, 0.6, 1.5) };
    case "move_up":
      return { ...tune, shiftY: clamp(tune.shiftY - 0.03, -0.3, 0.3) };
    case "move_down":
      return { ...tune, shiftY: clamp(tune.shiftY + 0.03, -0.3, 0.3) };
    case "logo_bigger":
      return { ...tune, logoScale: clamp(tune.logoScale * 1.15, 0.7, 1.6) };
    case "logo_smaller":
      return { ...tune, logoScale: clamp(tune.logoScale * 0.87, 0.7, 1.6) };
  }
}

// src/connectors.ts
var DATA_CONNECTOR_GROUPS = ["arquivos", "site", "redes", "sistemas"];
var DATA_CONNECTOR_GROUP_LABELS = {
  arquivos: "arquivos e entregas",
  site: "site e busca",
  redes: "redes e an\xFAncios",
  sistemas: "sistemas do cliente"
};
var DATA_CONNECTOR_KEYS = ["file", "push", "ga4", "gsc", "google_ads", "meta_ig", "meta_ads", "webbo", "portal", "rd_station"];
var DATA_CONNECTORS = [
  {
    key: "file",
    name: "Planilha ou CSV",
    group: "arquivos",
    mark: "file",
    letters: "XL",
    many: true,
    line: "o relat\xF3rio que o cliente j\xE1 exporta: vendas, ocupa\xE7\xE3o, faturamento.",
    brings: ["a s\xE9rie por m\xEAs, por pra\xE7a, por canal e por produto", "s\xF3 o que voc\xEA marcar: o resto do arquivo \xE9 descartado"],
    needs: "um arquivo XLSX ou CSV, com uma linha por m\xEAs ou um m\xEAs por coluna. a Machine prop\xF5e o que cada coluna \xE9 e voc\xEA confere.",
    rhythm: "a cada envio. o mesmo relat\xF3rio, enviado de novo, entra sozinho no lugar certo."
  },
  {
    key: "push",
    name: "Entrega autom\xE1tica",
    group: "arquivos",
    mark: "push",
    letters: "EA",
    many: true,
    line: "o sistema do cliente envia o relat\xF3rio sozinho, sem ningu\xE9m subir planilha.",
    brings: ["o mesmo que a planilha, entregue por m\xE1quina", "aviso \xE0 Raro quando a entrega atrasa"],
    needs: "algu\xE9m da TI do cliente. a Machine cria um endere\xE7o e um token s\xF3 para este relat\xF3rio, com o passo a passo pronto para enviar a eles.",
    rhythm: "na frequ\xEAncia combinada (todo dia, toda semana, todo m\xEAs).",
    keeps: "o token aparece uma vez e a Machine guarda s\xF3 a confer\xEAncia dele. ningu\xE9m da Raro acessa o sistema do cliente."
  },
  {
    key: "ga4",
    name: "Google Analytics 4",
    group: "site",
    mark: "ga4",
    letters: "GA",
    many: true,
    line: "visitas do site por cidade e por canal, e o uso de cada p\xE1gina.",
    brings: ["sess\xF5es, usu\xE1rios e eventos-chave por m\xEAs, cidade e canal de origem", "visualiza\xE7\xF5es, tempo de perman\xEAncia, celular \xD7 computador", "as p\xE1ginas mais vistas de cada m\xEAs"],
    needs: "o cliente adiciona o e-mail da Raro como leitor da propriedade. n\xE3o h\xE1 senha nem chave dele.",
    rhythm: "uma vez por dia. a primeira leitura traz 25 meses."
  },
  {
    key: "gsc",
    name: "Search Console",
    group: "site",
    mark: "gsc",
    letters: "SC",
    many: true,
    line: "o que as pessoas buscam no Google para chegar ao site.",
    brings: ["cliques e exibi\xE7\xF5es na busca, com a marca e sem a marca", "os termos e as p\xE1ginas do m\xEAs, como fonte de texto"],
    needs: "o cliente adiciona o e-mail da Raro como usu\xE1rio do site no Search Console.",
    rhythm: "uma vez por dia. a primeira leitura traz 16 meses."
  },
  {
    key: "google_ads",
    name: "Google Ads",
    group: "redes",
    mark: "gads",
    letters: "AD",
    many: true,
    line: "as campanhas, o gasto e o resultado da conta de an\xFAncios do Google.",
    brings: ["campanhas, grupos e an\xFAncios, com gasto e resultado por dia", "o que o Ritmo usa para planejar e criar campanha de busca"],
    needs: "o cliente adiciona o e-mail da Raro como usu\xE1rio da conta, com acesso padr\xE3o.",
    rhythm: "de hora em hora."
  },
  {
    key: "meta_ig",
    name: "Instagram",
    group: "redes",
    mark: "instagram",
    letters: "IG",
    many: true,
    line: "a conta da pr\xF3pria marca: alcance, posts e de que cidades \xE9 a audi\xEAncia.",
    brings: ["alcance, visualiza\xE7\xF5es, salvamentos e compartilhamentos por dia e por post", "seguidores e a audi\xEAncia por cidade", "a foto e o @ da conta na pr\xE9via do MODO"],
    needs: "um login do Facebook de quem administra a p\xE1gina do cliente. um login traz todas as p\xE1ginas da pessoa.",
    rhythm: "todo dia. o que n\xE3o \xE9 lido no dia a Meta n\xE3o devolve depois.",
    keeps: "o acesso fica cifrado no servi\xE7o de dados e vale 90 dias a partir do \xFAltimo login."
  },
  {
    key: "meta_ads",
    name: "Meta Ads",
    group: "redes",
    mark: "meta",
    letters: "MA",
    many: true,
    line: "as campanhas, o saldo e o resultado dos an\xFAncios no Instagram e no Facebook.",
    brings: ["campanhas, conjuntos e an\xFAncios, com o criativo, o gasto e o resultado", "saldo da conta e quantos dias ele dura"],
    needs: "o mesmo login do Facebook do Instagram, de quem tem acesso \xE0 conta de an\xFAncio.",
    rhythm: "de hora em hora.",
    keeps: "o acesso fica cifrado no servi\xE7o de dados. pausar e mudar verba \xE9 sempre por clique de uma pessoa, no Ritmo."
  },
  {
    key: "webbo",
    name: "Webbo",
    group: "sistemas",
    mark: null,
    letters: "W",
    many: false,
    line: "para cliente que usa o Webbo: an\xFAncios, visitas do site e a voz do cliente.",
    brings: ["an\xFAncios e visitas do site por m\xEAs", "a voz do cliente, como fonte de texto", "qual an\xFAncio vende, para as campanhas do Ritmo"],
    needs: "uma chave de API criada no Webbo do cliente, em conta \u203A chaves de API.",
    rhythm: "uma vez por dia.",
    keeps: "a chave fica cifrada no servi\xE7o de dados e serve s\xF3 para leitura."
  },
  {
    key: "portal",
    name: "Portal Control",
    group: "sistemas",
    mark: null,
    letters: "PC",
    many: false,
    line: "para cliente com o site feito pela Raro: o que acontece dentro das ferramentas dele.",
    brings: ["o funil do simulador e os planos mais escolhidos", "leads por origem e formul\xE1rios por tipo", "avalia\xE7\xF5es do SAC do hospital, com a nota de cada quesito m\xEAs a m\xEAs", "s\xF3 n\xFAmeros agregados: nenhum dado pessoal sai do portal"],
    needs: "o endere\xE7o do portal e a chave de leitura que est\xE1 em MACHINE_API_KEY no servidor dele.",
    rhythm: "uma vez por dia.",
    keeps: "a chave fica cifrada no servi\xE7o de dados e serve s\xF3 para leitura."
  },
  {
    key: "rd_station",
    name: "RD Station CRM",
    group: "sistemas",
    mark: null,
    letters: "RD",
    many: false,
    line: "o que acontece com o lead depois que ele chega: vira venda, se perde, por qu\xEA.",
    brings: ["negocia\xE7\xF5es criadas, ganhas e perdidas por m\xEAs e por fonte, com o motivo de perda", "o desfecho das negocia\xE7\xF5es por campanha e por UTM, para a leitura dos an\xFAncios saber que lead vende", "s\xF3 n\xFAmeros agregados: nenhum contato nem nome de negocia\xE7\xE3o sai do servi\xE7o de dados"],
    needs: "o token da inst\xE2ncia do RD Station CRM, em Perfil \u203A Integra\xE7\xF5es, gerado por algu\xE9m com acesso a todas as negocia\xE7\xF5es.",
    rhythm: "uma vez por dia.",
    keeps: "o token fica cifrado no servi\xE7o de dados e serve s\xF3 para leitura."
  }
];

// src/creation.ts
var CREATION_ROOM_KINDS = ["calendar", "deck", "posts"];
var CREATION_ROOM_KIND_LABELS = { calendar: "Cronograma", deck: "Apresenta\xE7\xE3o", posts: "Pe\xE7as avulsas" };
var CREATION_FORMATS = ["slide", "feed", "story"];
var CREATION_FORMAT_LABELS = { slide: "Slide", feed: "Feed", story: "Story" };
var CREATION_FORMAT_RATIO = { slide: 16 / 9, feed: 4 / 5, story: 9 / 16 };
var CREATION_PIECE_KINDS = ["post", "carousel", "story", "reels", "deck"];
var CREATION_PIECE_KIND_LABELS = { post: "Post", carousel: "Carrossel", story: "Story", reels: "Reels", deck: "Apresenta\xE7\xE3o" };
var CREATION_PIECE_FORMAT = { post: "feed", carousel: "feed", story: "story", reels: "story", deck: "slide" };
var CREATION_PIECE_BOARDS_MAX = { post: 1, carousel: 10, story: 5, reels: 6, deck: 20 };
var CREATION_PIECE_STATUSES = ["idea", "text", "art", "ready"];
var CREATION_PIECE_STATUS_LABELS = { idea: "pauta", text: "texto", art: "arte de estudo", ready: "pronto para o cliente" };
var CREATION_LAYOUTS = ["cover", "statement", "numbers", "chart", "list", "image-split", "image-full", "quote", "closing"];
var CREATION_LAYOUT_LABELS = {
  cover: "Abertura",
  statement: "Frase",
  numbers: "N\xFAmeros",
  chart: "Gr\xE1fico",
  list: "Lista",
  "image-split": "Imagem ao lado",
  "image-full": "Imagem inteira",
  quote: "Cita\xE7\xE3o",
  closing: "Fecho"
};
var CREATION_PIECES_MAX = 40;
var CREATION_ITEMS_MAX = 6;
var CREATION_STATS_MAX = 4;
var CREATION_TEXT_MAX = { eyebrow: 60, title: 160, text: 700, item: 160, notes: 1200, imageWanted: 300, pieceTitle: 120, objective: 300, why: 400, caption: 2200 };
var CREATION_LOCK_MS = 9e4;
var CREATION_TALK_READ = 30;
var CREATION_TURN_ESTIMATE_USD = 0.1;
var CREATION_IMAGE_ESTIMATE_USD = 0.07;
function creationEmpty() {
  return { eyebrow: "", title: "", text: "", items: [], stats: [], chart: null, image: null, imageWanted: "", notes: "", bg: null };
}
function creationPieceEmpty(title = "") {
  return { title, date: "", time: "", objective: "", why: "", caption: "", notes: "" };
}
var str2 = (v, max) => typeof v === "string" ? v.replace(/\r/g, "").trim().slice(0, max) : "";
var isHex = (v) => typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v);
function clamp01(v) {
  const n = typeof v === "number" && Number.isFinite(v) ? v : 0.5;
  return Math.max(0, Math.min(1, n));
}
function normalizeCreationContent(raw, prev) {
  const r = raw && typeof raw === "object" ? raw : {};
  const base = prev ?? creationEmpty();
  const has = (k) => Object.prototype.hasOwnProperty.call(r, k);
  const L = CREATION_TEXT_MAX;
  const items = has("items") && Array.isArray(r.items) ? r.items.map((x) => str2(x, L.item)).filter(Boolean).slice(0, CREATION_ITEMS_MAX) : base.items;
  const stats = has("stats") && Array.isArray(r.stats) ? r.stats.map((s) => ({ code: typeof s?.code === "string" && /^F\d{2}$/.test(s.code) ? s.code : null, label: str2(s?.label, 90), value: str2(s?.value, 30), compare: str2(s?.compare, 90) })).filter((s) => s.label || s.value).slice(0, CREATION_STATS_MAX) : base.stats;
  let image = base.image;
  if (has("image")) {
    const i = r.image;
    image = i && typeof i.fileId === "string" ? {
      fileId: i.fileId,
      assetId: typeof i.assetId === "string" ? i.assetId : null,
      source: i.source === "product" || i.source === "generated" ? i.source : "gallery",
      label: str2(i.label, 120),
      focus: { x: clamp01(i.focus?.x), y: clamp01(i.focus?.y) },
      study: i.study === true
    } : null;
  }
  return {
    eyebrow: has("eyebrow") ? str2(r.eyebrow, L.eyebrow) : base.eyebrow,
    title: has("title") ? str2(r.title, L.title) : base.title,
    text: has("text") ? str2(r.text, L.text) : base.text,
    items,
    stats,
    chart: has("chart") ? r.chart && typeof r.chart === "object" ? r.chart : null : base.chart,
    image,
    imageWanted: has("imageWanted") ? str2(r.imageWanted, L.imageWanted) : base.imageWanted,
    notes: has("notes") ? str2(r.notes, L.notes) : base.notes,
    bg: has("bg") ? isHex(r.bg) ? r.bg.toLowerCase() : null : base.bg
  };
}
function normalizeCreationPiece(raw, prev) {
  const r = raw && typeof raw === "object" ? raw : {};
  const base = prev ?? creationPieceEmpty();
  const has = (k) => Object.prototype.hasOwnProperty.call(r, k);
  const L = CREATION_TEXT_MAX;
  const date = has("date") ? typeof r.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(r.date) ? r.date : "" : base.date;
  const time = has("time") ? typeof r.time === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(r.time) ? r.time : "" : base.time;
  return {
    title: has("title") ? str2(r.title, L.pieceTitle) : base.title,
    date,
    time,
    objective: has("objective") ? str2(r.objective, L.objective) : base.objective,
    why: has("why") ? str2(r.why, L.why) : base.why,
    caption: has("caption") ? str2(r.caption, L.caption) : base.caption,
    notes: has("notes") ? str2(r.notes, L.notes) : base.notes
  };
}
function creationLayoutFor(layout, format) {
  if (format === "story" && layout === "image-split") return "image-full";
  return layout;
}
var CREATION_DECISIONS = ["follow", "adjust"];
var CREATION_DECISION_LABELS = { follow: "Segue assim", adjust: "Pedir ajuste" };
var CREATION_DECISION_NOTE_MAX = 1200;
function creationForClient(p) {
  return {
    id: p.id,
    position: p.position,
    kind: p.kind,
    info: { title: p.info.title, date: p.info.date, time: p.info.time, why: p.info.why, caption: p.info.caption },
    boards: p.boards.map((b) => {
      const { notes: _n, imageWanted: _w, ...content } = b.content;
      return { id: b.id, position: b.position, format: b.format, layout: b.layout, content };
    })
  };
}

// src/protected-areas.ts
var PROTECT_REASONS = ["trade_secret", "legible_text", "third_party_brand", "person", "other"];
var PROTECT_REASON_LABELS = {
  trade_secret: "segredo industrial",
  legible_text: "texto leg\xEDvel",
  third_party_brand: "marca de terceiro",
  person: "pessoa sem autoriza\xE7\xE3o",
  other: "outro motivo"
};
var PROTECT_TREATMENTS = ["blur", "pixel", "cover", "forbid"];
var PROTECT_TREATMENT_LABELS = { blur: "desfocar", pixel: "pixelar", cover: "cobrir", forbid: "n\xE3o pode aparecer" };
var PROTECT_TREATMENT_DONE = { blur: "desfocada", pixel: "pixelada", cover: "coberta", forbid: "fora da pe\xE7a" };
var PROTECT_STATUSES = ["active", "suggested", "dismissed"];
var PROTECT_POINTS_MAX = 120;
var PROTECT_MARGIN_MAX = 0.025;
var PROTECT_MARGIN_DEFAULT = 8e-3;
var PROTECT_AREAS_MAX = 40;
var PROTECT_CROP_HEAVY = 0.33;
var PROTECT_RENDER_STATUSES = ["none", "pending", "ready", "failed"];
var clamp012 = (n) => Math.min(1, Math.max(0, n));
var round = (n) => Math.round(n * 1e5) / 1e5;
function cleanProtectShape(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw;
  const kind = r.kind === "rect" ? "rect" : r.kind === "polygon" ? "polygon" : null;
  if (!kind || !Array.isArray(r.points)) return null;
  const points = r.points.filter((p) => Array.isArray(p) && p.length === 2 && p.every((n) => typeof n === "number" && Number.isFinite(n))).slice(0, PROTECT_POINTS_MAX).map(([x, y]) => [round(clamp012(x)), round(clamp012(y))]);
  if (kind === "rect") {
    if (points.length < 2) return null;
    const b = protectBox({ kind, points });
    if (b.w < 2e-3 || b.h < 2e-3) return null;
    return { kind, points: [[b.x, b.y], [b.x + b.w, b.y], [b.x + b.w, b.y + b.h], [b.x, b.y + b.h]].map(([x, y]) => [round(x), round(y)]) };
  }
  if (points.length < 3 || polygonArea(points) < 1e-5) return null;
  return { kind, points };
}
function protectBox(s) {
  let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
  for (const [x, y] of s.points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}
function polygonArea(P) {
  let s = 0;
  for (let i = 0; i < P.length; i++) {
    const [x1, y1] = P[i], [x2, y2] = P[(i + 1) % P.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s / 2);
}
function inside(P, x, y) {
  let c = false;
  for (let i = 0, j = P.length - 1; i < P.length; j = i++) {
    const [xi, yi] = P[i], [xj, yj] = P[j];
    if (yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi) c = !c;
  }
  return c;
}
function segDist(px, py, [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay, l = dx * dx + dy * dy;
  const t = l ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l)) : 0;
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
}
function inProtectedArea(a, x, y, aspect) {
  const P = a.shape.points;
  if (inside(P, x, y)) return true;
  if (a.margin <= 0) return false;
  const sx = aspect >= 1 ? 1 : aspect, sy = aspect >= 1 ? 1 / aspect : 1;
  const Q = P.map(([px, py]) => [px * sx, py * sy]);
  for (let i = 0; i < Q.length; i++) if (segDist(x * sx, y * sy, Q[i], Q[(i + 1) % Q.length]) < a.margin) return true;
  return false;
}
function protectCropReport(areas, crop, aspect) {
  const live = areas.filter((a) => a.status === "active");
  const N = 40;
  let covered = 0;
  for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) {
    const x = crop.x + (i + 0.5) / N * crop.w, y = crop.y + (j + 0.5) / N * crop.h;
    if (live.some((a) => inProtectedArea(a, x, y, aspect))) covered++;
  }
  const per = live.map((a) => {
    const b = protectBox(a.shape), M = 22;
    let tot = 0, inn = 0;
    for (let i = 0; i < M; i++) for (let j = 0; j < M; j++) {
      const x = b.x + (i + 0.5) / M * b.w, y = b.y + (j + 0.5) / M * b.h;
      if (!inside(a.shape.points, x, y)) continue;
      tot++;
      if (x >= crop.x && x <= crop.x + crop.w && y >= crop.y && y <= crop.y + crop.h) inn++;
    }
    return { id: a.id, label: a.label, treatment: a.treatment, share: tot ? inn / tot : 0 };
  }).filter((z) => z.share > 0);
  const share = covered / (N * N);
  const refused = per.filter((z) => z.treatment === "forbid");
  return { share, per, refused, verdict: refused.length ? "refused" : share > PROTECT_CROP_HEAVY ? "heavy" : "ok" };
}
function protectKey(areas) {
  const live = areas.filter((a) => a.status === "active").map((a) => `${a.id}|${a.treatment}|${a.margin}|${a.shape.kind}|${a.shape.points.map((p) => p.join(",")).join(";")}`).sort();
  if (!live.length) return "";
  let h = 2166136261;
  const s = live.join("\n");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${live.length}:${(h >>> 0).toString(36)}`;
}
function protectOverlap(a, b) {
  const p = protectBox(a), q = protectBox(b);
  const w = Math.max(0, Math.min(p.x + p.w, q.x + q.w) - Math.max(p.x, q.x)), h = Math.max(0, Math.min(p.y + p.h, q.y + q.h) - Math.max(p.y, q.y));
  const small = Math.min(p.w * p.h, q.w * q.h);
  return small > 0 ? w * h / small : 0;
}
function simplifyStroke(P, eps) {
  if (P.length < 3) return P;
  let dmax = 0, idx = 0;
  const end = P.length - 1;
  for (let i = 1; i < end; i++) {
    const d = segDist(P[i][0], P[i][1], P[0], P[end]);
    if (d > dmax) {
      dmax = d;
      idx = i;
    }
  }
  if (dmax > eps) {
    const a = simplifyStroke(P.slice(0, idx + 1), eps), b = simplifyStroke(P.slice(idx), eps);
    return a.slice(0, -1).concat(b);
  }
  return [P[0], P[end]];
}
function assetUseFile(a, use) {
  const status = a.protectStatus ?? "none";
  if (status === "none") return a.fileId;
  if (status !== "ready") return null;
  return (use === "piece" ? a.pieceFileId : a.modelFileId) ?? null;
}
var PROTECT_NOT_READY = "a foto tem \xE1rea protegida e a vers\xE3o protegida ainda est\xE1 sendo feita: tente de novo em alguns segundos";

// src/repos.ts
var GITHUB_ORG = "machine-raro";
var slug = (text, max = 40) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, max).replace(/-+$/, "");
function clientRepoUrl(input) {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  const m = raw.match(/^(?:https?:\/\/(?:www\.)?github\.com\/|git@github\.com:)?(?:([A-Za-z0-9-]{1,39})\/)?([A-Za-z0-9._-]{1,100}?)(?:\.git)?\/?$/);
  if (!m || m[2] === "." || m[2] === "..") return null;
  return `https://github.com/${m[1] ?? GITHUB_ORG}/${m[2]}`;
}
function demandRepoPath(d) {
  if (d.repoPath) return d.repoPath;
  if (!d.number) return null;
  const name = slug(d.shortName || d.title || "", 40);
  return `${String(d.number).padStart(4, "0")}${name ? `-${name}` : ""}`;
}
var DEMAND_REPO_PATH_RE = /^(?!.*\.\.)[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+){0,3}$/;
function demandRepo(repository, d) {
  const url = clientRepoUrl(repository);
  if (!url) return null;
  const path = demandRepoPath(d);
  return {
    url,
    name: url.replace("https://github.com/", ""),
    path,
    tree: path ? `${url}/tree/main/${path}` : null,
    custom: Boolean(d.repoPath),
    tagPrefix: d.number ? String(d.number).padStart(4, "0") : null
  };
}

// src/pricing.ts
var CREDIT_BRL = 100;
var SERVICE_MARKUP_DEFAULT = 2.2;
var FEE_BASE_SHARE = 0.4;
var CREDIT_CARRYOVER = 0.2;
var PASSTHROUGH_FEE = 0.15;
var FEE_CYCLE_MONTHS = 3;
var SERVICE_MAX = 60;
var SERVICE_KINDS = ["base", "menu", "modo"];
var SERVICE_KIND_LABELS = { base: "Base", menu: "Menu da carteira", modo: "Assento do MODO" };
var MODO_SEAT_DISCOUNTS = [{ from: 30, pct: 25 }, { from: 10, pct: 15 }];
var modoSeatDiscount = (seats) => MODO_SEAT_DISCOUNTS.find((d) => seats >= d.from)?.pct ?? 0;
var SERVICE_UNITS = ["cycle", "month", "project", "piece"];
var SERVICE_UNIT_LABELS = { cycle: "por ciclo", month: "por m\xEAs", project: "por projeto", piece: "por pe\xE7a" };
var SERVICE_DEFAULTS = [
  { key: "rotina-de-conteudo", name: "Rotina de conte\xFAdo", description: "12 posts por m\xEAs: pauta, texto, arte e publica\xE7\xE3o", kind: "base", unit: "month", hours: { redacao: 12, direcao_de_arte: 16, social_media: 12 }, externalCost: 0, aiUsd: 2 },
  { key: "atendimento-e-conta", name: "Atendimento e gest\xE3o da conta", description: "o dia a dia com o cliente, prioridades e escopo", kind: "base", unit: "month", hours: { atendimento: 12, gerente_de_contas: 4 }, externalCost: 0, aiUsd: 1 },
  { key: "planejamento-do-ciclo", name: "Planejamento e relat\xF3rio do ciclo", description: "objetivo do trimestre, carteira proposta e o relat\xF3rio de fechamento", kind: "base", unit: "cycle", hours: { planejamento: 10, gerente_de_contas: 6 }, externalCost: 0, aiUsd: 3 },
  { key: "pesquisa-com-consumidor", name: "Pesquisa com consumidor", description: "Rastro, entrevistas e a leitura do que muda na estrat\xE9gia", kind: "menu", unit: "project", hours: { planejamento: 24, gerente_de_contas: 6 }, externalCost: 0, aiUsd: 3 },
  { key: "campanha-sazonal", name: "Campanha sazonal", description: "conceito, KV e os desdobramentos da campanha", kind: "menu", unit: "project", hours: { planejamento: 8, redacao: 10, direcao_de_arte: 20, arte_final: 12 }, externalCost: 0, aiUsd: 2 },
  { key: "gestao-de-anuncios", name: "Gest\xE3o de an\xFAncios", description: "subir, acompanhar e ajustar a m\xEDdia paga (a verba \xE9 repasse)", kind: "menu", unit: "month", hours: { gestao_de_trafego: 12 }, externalCost: 0, aiUsd: 1 },
  { key: "volca", name: "Volc\xE0", description: "produto da Raro: o que entrega e as horas, a definir", kind: "menu", unit: "project", hours: {}, externalCost: 0, aiUsd: 0 },
  { key: "estudo-de-marca", name: "Estudo de marca (BIS)", description: "identidade, voz, p\xFAblico e o sistema visual da marca", kind: "menu", unit: "project", hours: { planejamento: 24, direcao_de_arte: 16 }, externalCost: 0, aiUsd: 3 },
  { key: "video-curto", name: "V\xEDdeo curto", description: "reel ou v\xEDdeo de at\xE9 60 s: roteiro e edi\xE7\xE3o", kind: "menu", unit: "piece", hours: { redacao: 2, edicao_de_video: 4 }, externalCost: 0, aiUsd: 0.5 },
  { key: "peca-avulsa", name: "Pe\xE7a avulsa", description: "post, story ou banner fora da rotina", kind: "menu", unit: "piece", hours: { direcao_de_arte: 2 }, externalCost: 0, aiUsd: 0.2 },
  { key: "impresso", name: "Impresso", description: "folheto, mapa, sacola: arte e fechamento para a gr\xE1fica (a impress\xE3o \xE9 repasse)", kind: "menu", unit: "piece", hours: { direcao_de_arte: 3, arte_final: 2 }, externalCost: 0, aiUsd: 0.2 },
  { key: "implantacao-do-modo", name: "Implanta\xE7\xE3o do MODO", description: "moldes das pe\xE7as, BIS pronto para o MODO, regras de quem pede e quem aprova, e o treinamento", kind: "menu", unit: "project", hours: { direcao_de_arte: 16, planejamento: 12, atendimento: 8, gerente_de_contas: 4 }, externalCost: 0, aiUsd: 5 },
  { key: "modo-colaborador", name: "MODO \xB7 colaborador", description: "quem pede pe\xE7as (RH, comercial, unidades): cerca de 10 pe\xE7as por m\xEAs", kind: "modo", unit: "month", hours: { atendimento: 0.25 }, externalCost: 0, aiUsd: 5, priceBrl: 190 },
  { key: "modo-responsavel", name: "MODO \xB7 respons\xE1vel", description: "quem aprova e usa mais (marketing, gestor de \xE1rea): cerca de 30 pe\xE7as por m\xEAs", kind: "modo", unit: "month", hours: { atendimento: 0.5 }, externalCost: 0, aiUsd: 15, priceBrl: 390 }
];
function servicePrice(s, rates, usdBrl) {
  let hours = 0, hoursCost = 0;
  const missing = [];
  for (const [fn, h] of Object.entries(s.hours)) {
    if (!(h > 0)) continue;
    hours += h;
    const rate = rates[fn];
    if (rate == null || !(rate > 0)) missing.push(fn);
    else hoursCost += h * rate;
  }
  const ai = s.aiUsd * usdBrl;
  const cost = hoursCost + s.externalCost + ai;
  const markup = s.markup && s.markup > 0 ? s.markup : SERVICE_MARKUP_DEFAULT;
  const price = s.priceBrl != null && s.priceBrl > 0 ? s.priceBrl : cost * markup;
  const ruled = Math.ceil(price / CREDIT_BRL - 1e-9);
  return { hours, hoursCost, external: s.externalCost, ai, cost, markup, price, credits: s.credits != null ? s.credits : ruled, by: s.credits != null || s.priceBrl != null && s.priceBrl > 0 ? "fixed" : "rule", missing };
}
function feeCycle(monthly) {
  const cycle = monthly * FEE_CYCLE_MONTHS;
  const base = cycle * FEE_BASE_SHARE;
  const wallet = cycle - base;
  return { cycle, base, wallet, credits: Math.floor(wallet / CREDIT_BRL) };
}
var FEE_CYCLE_STATUSES = ["draft", "approved", "closed"];
var FEE_CYCLE_STATUS_LABELS = { draft: "em montagem", approved: "aprovada", closed: "fechada" };
var FEE_ITEM_ORIGINS = ["machine", "human"];
var FEE_PROPOSE_ESTIMATE_USD = 0.08;
var FEE_ITEMS_MAX = 30;
var feeCycleMonth = (v) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null;
function feeCycleLabel(month) {
  const [y, m] = month.split("-").map(Number);
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const end = (m - 1 + FEE_CYCLE_MONTHS - 1) % 12;
  const endYear = y + Math.floor((m - 1 + FEE_CYCLE_MONTHS - 1) / 12);
  return `${names[m - 1]}\u2013${names[end]} ${endYear === y ? y : `${y}/${String(endYear).slice(2)}`}`;
}
function feeWallet(c, items, baseCost) {
  const cycle = c.monthlyFee * FEE_CYCLE_MONTHS;
  const base = c.basePrice != null ? Math.min(cycle, c.basePrice) : cycle * c.baseShare;
  const credits = Math.floor((cycle - base) / c.creditBrl) + c.carryIn;
  const used = items.reduce((a, i) => a + i.quantity * i.credits, 0);
  const itemsCost = items.reduce((a, i) => a + i.quantity * i.cost, 0);
  const cost = baseCost == null ? null : baseCost + itemsCost;
  return { cycle, base, credits, used, left: credits - used, itemsCost, baseCost, cost, margin: cost == null ? null : cycle - cost, marginPct: cost == null || !cycle ? null : (cycle - cost) / cycle * 100 };
}
var PROSPECT_STATUSES = ["open", "won", "lost"];
var PROSPECT_STATUS_LABELS = { open: "em negocia\xE7\xE3o", won: "fechou", lost: "n\xE3o fechou" };
var FEE_MODES = ["built", "fixed"];
var BASE_SHARE_WARN = 0.5;
var BASE_SIZE_ESTIMATE_USD = 0.05;
var perCycle = (unit, q) => q * (unit === "month" ? FEE_CYCLE_MONTHS : 1);
function basePrice(lines, services) {
  const base = services.filter((s) => s.kind === "base");
  const q = (k) => Object.keys(lines).length ? lines[k] ?? 0 : 1;
  const cycle = base.reduce((a, s) => a + s.price * perCycle(s.unit, q(s.key)), 0);
  const cost = base.reduce((a, s) => a + s.cost * perCycle(s.unit, q(s.key)), 0);
  return { cycle, monthly: cycle / FEE_CYCLE_MONTHS, cost, missing: [...new Set(base.filter((s) => q(s.key) > 0).flatMap((s) => s.missing))] };
}
function prospectQuote(p, services) {
  const by = new Map(services.map((s) => [s.key, s]));
  const lines = (l, kind) => Object.entries(l).map(([k, q]) => ({ s: by.get(k), q })).filter((x) => Boolean(x.s) && x.s.kind === kind && x.q > 0);
  const menuL = lines(p.menu, "menu"), seatL = lines(p.seats, "modo");
  const base = basePrice(p.base, services);
  const monthlyFee = p.feeMode === "built" ? Math.ceil((base.monthly + p.walletCredits * CREDIT_BRL / FEE_CYCLE_MONTHS) / 100) * 100 : p.monthlyFee;
  const cycle = monthlyFee * FEE_CYCLE_MONTHS;
  const credits = Math.max(0, Math.floor((cycle - base.cycle) / CREDIT_BRL));
  const used = menuL.reduce((a, x) => a + x.s.credits * perCycle(x.s.unit, x.q), 0);
  const menuCost = menuL.reduce((a, x) => a + x.s.cost * perCycle(x.s.unit, x.q), 0);
  const missing = [.../* @__PURE__ */ new Set([...base.missing, ...[...menuL, ...seatL].flatMap((x) => x.s.missing)])];
  const seats = seatL.reduce((a, x) => a + x.q, 0);
  const discount = modoSeatDiscount(seats);
  const modoPrice = seatL.reduce((a, x) => a + x.s.price * x.q, 0) * (1 - discount / 100);
  const modoCost = seatL.reduce((a, x) => a + x.s.cost * x.q, 0);
  const passFee = p.passthrough * PASSTHROUGH_FEE;
  const cycleRevenue = cycle + (modoPrice + passFee) * FEE_CYCLE_MONTHS;
  const cycleCost = base.cost + menuCost + modoCost * FEE_CYCLE_MONTHS;
  return {
    monthly: { fee: monthlyFee, modo: modoPrice, passthrough: p.passthrough + passFee, total: monthlyFee + modoPrice + p.passthrough + passFee },
    seats,
    discount,
    missing,
    base: { ...base, share: cycle ? base.cycle / cycle : 0, short: cycle < base.cycle },
    fee: { cycle, credits, used, left: credits - used, menuCost },
    modo: { price: modoPrice, cost: modoCost, margin: modoPrice - modoCost },
    cycle: { revenue: cycleRevenue, cost: cycleCost, margin: cycleRevenue - cycleCost, marginPct: cycleRevenue ? (cycleRevenue - cycleCost) / cycleRevenue * 100 : 0 },
    minFee: Math.ceil(base.monthly / 100) * 100
  };
}
var PROSPECT_COMPETITORS_MAX = 8;
var ANNUAL_PROPOSAL_ESTIMATE_USD = 0.15;

// src/google-keywords.ts
var GADS_KEYWORD_MATCH_LABELS = { exact: "exata", phrase: "de frase", broad: "ampla", unknown: "tipo n\xE3o informado" };
var GADS_SCOPE_LABELS = { ad_group: "no grupo", campaign: "na campanha", list: "em lista", account: "na conta inteira" };
var GADS_KEYWORDS_DAYS = 30;
var GADS_KEYWORDS_STALE_HOURS = 24;
var GADS_TERMS_REVIEW_MAX = 80;
var GADS_TERMS_REVIEW_ESTIMATE_USD = 0.08;
var GADS_NEGATIVES_APPLY_MAX = 40;
var LIVE = (s) => s === "ACTIVE";
function gadsTokens(text) {
  return text.toLocaleLowerCase("pt-BR").replace(/[[\]"+]/g, " ").split(/\s+/).filter(Boolean);
}
function negativeMatches(neg, text) {
  const n = gadsTokens(neg.text);
  const t = gadsTokens(text);
  if (n.length === 0 || t.length === 0) return false;
  if (neg.match === "exact") return n.length === t.length && n.every((w, i) => w === t[i]);
  if (neg.match === "phrase") {
    for (let i = 0; i + n.length <= t.length; i++) if (n.every((w, j) => w === t[i + j])) return true;
    return false;
  }
  if (neg.match === "broad") {
    const set = new Set(t);
    return n.every((w) => set.has(w));
  }
  return false;
}
function negativeInScope(neg, at, lists) {
  if (neg.scope === "ad_group") return neg.adGroupId === at.adGroupId;
  if (neg.scope === "campaign") return neg.campaignId === at.campaignId;
  if (neg.scope === "account") return true;
  const l = lists.find((x) => x.id === neg.listId);
  return Boolean(l && (l.account || l.campaignIds.includes(at.campaignId)));
}
function gadsConflicts(read) {
  const missing = ["groupNegatives", "campaignNegatives", "listItems", "listLinks", "accountLists"].filter((k) => k in read.failed);
  if (missing.length) return { conflicts: null, reason: "o Google n\xE3o devolveu todas as negativas: sem elas n\xE3o d\xE1 para conferir o que est\xE1 bloqueado" };
  const camp = new Map(read.campaigns.map((c) => [c.id, c]));
  const lists = new Map(read.lists.map((l) => [l.id, l]));
  const live = read.positives.filter((p) => LIVE(p.status) && LIVE(p.groupStatus) && LIVE(camp.get(p.campaignId)?.status));
  const out = [];
  for (const p of live) {
    for (const n of read.negatives) {
      if (!negativeInScope(n, p, read.lists) || !negativeMatches(n, p.text)) continue;
      out.push({
        positive: { id: p.id, text: p.text, match: p.match, adGroupId: p.adGroupId, adGroupName: p.adGroupName, campaignId: p.campaignId },
        negative: { id: n.id, text: n.text, match: n.match, scope: n.scope, list: n.listId ? lists.get(n.listId)?.name ?? null : null },
        full: p.match !== "broad"
      });
    }
  }
  const ids = new Set(read.negatives.map((n) => n.id));
  if (out.some((c) => c.positive.id === c.negative.id) || read.positives.some((p) => ids.has(p.id) && read.negatives.some((n) => n.id === p.id && n.adGroupId === p.adGroupId)))
    return { conflicts: null, reason: "a leitura misturou palavra-chave e negativa: refa\xE7a a leitura antes de confiar nos bloqueios" };
  if (out.length >= 3 && out.length === read.negatives.length) return { conflicts: null, reason: "o n\xFAmero de bloqueios \xE9 igual ao de negativas, sinal de leitura errada: refa\xE7a a leitura antes de confiar nos bloqueios" };
  return { conflicts: out, reason: null };
}
function negativeWouldBlock(neg, campaignId, read) {
  return read.positives.filter((p) => p.campaignId === campaignId && LIVE(p.status) && LIVE(p.groupStatus) && negativeMatches(neg, p.text));
}
function termExcluded(t, read) {
  if (t.status === "EXCLUDED" || t.status === "ADDED_EXCLUDED") return true;
  return read.negatives.some((n) => negativeInScope(n, t, read.lists) && negativeMatches(n, t.term));
}
function negativeCovers(neg, campaignId, read) {
  return read.terms.filter((t) => t.campaignId === campaignId && !termExcluded(t, read) && negativeMatches(neg, t.term));
}
function gadsWasteTerms(read) {
  return read.terms.filter((t) => t.metrics.cost > 0 && t.metrics.conversions === 0 && !termExcluded(t, read) && t.status !== "ADDED").sort((a, b) => b.metrics.cost - a.metrics.cost);
}
var r2 = (v) => Math.round(v * 100) / 100;
function gadsKeywordSummary(read) {
  const camp = new Map(read.campaigns.map((c) => [c.id, c]));
  const live = read.positives.filter((p) => LIVE(p.status) && LIVE(p.groupStatus) && LIVE(camp.get(p.campaignId)?.status));
  const { conflicts, reason } = gadsConflicts(read);
  const neg = { ad_group: 0, campaign: 0, list: 0, account: 0 };
  for (const n of read.negatives) neg[n.scope]++;
  const waste = gadsWasteTerms(read);
  const termCost = r2(read.terms.reduce((s, t) => s + t.metrics.cost, 0));
  const wasteCost = r2(waste.reduce((s, t) => s + t.metrics.cost, 0));
  const kw = new Set(read.positives.map((p) => gadsTokens(p.text).join(" ")));
  const converting = read.terms.filter((t) => t.metrics.conversions > 0 && t.status !== "ADDED" && !termExcluded(t, read) && !kw.has(gadsTokens(t.term).join(" "))).sort((a, b) => b.metrics.conversions - a.metrics.conversions || a.metrics.cost - b.metrics.cost);
  return {
    since: read.since,
    until: read.until,
    campaigns: new Set(read.positives.map((p) => p.campaignId)).size,
    positives: read.positives.length,
    livePositives: live.length,
    negatives: neg,
    unattachedLists: read.lists.filter((l) => !l.account && l.campaignIds.length === 0).map((l) => ({ id: l.id, name: l.name, items: l.items })),
    conflicts,
    conflictsReason: reason,
    zeroImpression: "keywordMetrics" in read.failed ? null : live.filter((p) => p.metrics && p.metrics.impressions === 0),
    lowQuality: live.filter((p) => p.qualityScore !== null && p.qualityScore <= 3 && (p.metrics?.cost ?? 0) > 0).sort((a, b) => (b.metrics?.cost ?? 0) - (a.metrics?.cost ?? 0)),
    terms: { count: read.terms.length, cost: termCost, conversions: r2(read.terms.reduce((s, t) => s + t.metrics.conversions, 0)), capped: read.termsCapped },
    waste: { count: waste.length, cost: wasteCost, share: termCost > 0 ? Math.round(wasteCost / termCost * 1e3) / 10 : null, top: waste.slice(0, 50) },
    converting: converting.slice(0, 30),
    failed: Object.keys(read.failed)
  };
}
var gadsSuggestionKey = (campaignId, text, match) => `${campaignId}:${match}:${gadsTokens(text).join(" ")}`;

// src/meta-creative.ts
var AD_FATIGUE_MIN_DAYS = 21;
var AD_FATIGUE_MIN_IMPRESSIONS = 2e3;
var AD_FATIGUE_MIN_CLICKS = 20;
var AD_FATIGUE_CTR_DROP = 0.2;
var AD_FATIGUE_COST_UP = 1.4;
var AD_FATIGUE_MIN_RESULTS = 5;
var AD_FATIGUE_LOOKBACK_DAYS = 60;
var pct = (v) => `${(v * 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
var brl = (v) => `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function adFatigue(days, resultLabel) {
  const live = days.filter((d) => d.impressions > 0).sort((a2, b2) => a2.day.localeCompare(b2.day));
  if (live.length < AD_FATIGUE_MIN_DAYS) return null;
  const first = live.slice(0, 7);
  const last = live.slice(-7);
  const sum = (xs) => xs.reduce((t, d) => ({ spend: t.spend + d.spend, impressions: t.impressions + d.impressions, clicks: t.clicks + d.clicks, result: t.result + d.result }), { spend: 0, impressions: 0, clicks: 0, result: 0 });
  const a = sum(first), b = sum(last);
  if (a.impressions < AD_FATIGUE_MIN_IMPRESSIONS || b.impressions < AD_FATIGUE_MIN_IMPRESSIONS || a.clicks < AD_FATIGUE_MIN_CLICKS || b.clicks < AD_FATIGUE_MIN_CLICKS) return null;
  const ctrA = a.clicks / a.impressions, ctrB = b.clicks / b.impressions;
  const drop = 1 - ctrB / ctrA;
  const withResults = a.result >= AD_FATIGUE_MIN_RESULTS && b.result >= AD_FATIGUE_MIN_RESULTS;
  const cprA = a.result > 0 ? a.spend / a.result : null, cprB = b.result > 0 ? b.spend / b.result : null;
  const up = withResults && cprA && cprB ? cprB / cprA : null;
  if (withResults ? up === null || up < AD_FATIGUE_COST_UP : drop < AD_FATIGUE_CTR_DROP) return null;
  const r22 = (v) => Math.round(v * 1e4) / 1e4;
  const label = resultLabel ?? "resultado";
  const br = (day) => `${day.slice(8, 10)}/${day.slice(5, 7)}`;
  const moved = [];
  if (Math.abs(drop) >= 0.1) moved.push(`a taxa de cliques ${drop > 0 ? "caiu" : "subiu"} de ${pct(ctrA)} para ${pct(ctrB)}`);
  else moved.push(`a taxa de cliques ficou parecida (${pct(ctrA)} e ${pct(ctrB)})`);
  if (withResults) {
    const convA = a.result / a.clicks, convB = b.result / b.clicks;
    if (Math.abs(1 - convB / convA) >= 0.1) moved.push(`a convers\xE3o por clique (${label}) ${convB < convA ? "caiu" : "subiu"} de ${pct(convA)} para ${pct(convB)}`);
    const cpcA = a.spend / a.clicks, cpcB = b.spend / b.clicks;
    if (Math.abs(cpcB / cpcA - 1) >= 0.1) moved.push(`o clique ${cpcB > cpcA ? "encareceu" : "barateou"} de ${brl(cpcA)} para ${brl(cpcB)}`);
  }
  return {
    base: { from: first[0].day, to: first[first.length - 1].day, ctr: r22(ctrA), costPerResult: cprA !== null ? Math.round(cprA * 100) / 100 : null },
    recent: { from: last[0].day, to: last[last.length - 1].day, ctr: r22(ctrB), costPerResult: cprB !== null ? Math.round(cprB * 100) / 100 : null },
    ctrDrop: Math.round(drop * 1e3) / 1e3,
    costUp: up !== null ? Math.round(up * 100) / 100 : null,
    days: live.length,
    text: `${up !== null && cprA !== null && cprB !== null ? `O custo por resultado (${label}) foi de ${brl(cprA)} (${br(first[0].day)} a ${br(first[first.length - 1].day)}) para ${brl(cprB)} (${br(last[0].day)} a ${br(last[last.length - 1].day)})` : `De ${br(first[0].day)} a ${br(first[first.length - 1].day)} para ${br(last[0].day)} a ${br(last[last.length - 1].day)}`}: ${moved.join("; ")}. \xC9 sinal de que esta pe\xE7a rende menos do que rendia; n\xE3o prova a causa (pode ser o leil\xE3o ou a \xE9poca).`
  };
}
function adVideoStats(t) {
  const v = t.video;
  if (!v || t.impressions <= 0 || v.plays <= 0) return null;
  const q = [v.p25, v.p50, v.p75, v.p100];
  const incoherent = q.some((x, i) => i > 0 && x > q[i - 1]) || v.p25 > v.plays;
  const r = (x) => Math.round(x * 1e3) / 1e3;
  const base = Math.max(t.views3s, v.p25);
  return {
    hook: t.views3s > 0 ? r(t.views3s / t.impressions) : null,
    hold: v.thruplay > 0 ? r(v.thruplay / t.impressions) : null,
    curve: incoherent || base <= 0 ? null : [r(v.p25 / base), r(v.p50 / base), r(v.p75 / base), r(v.p100 / base)],
    incoherent,
    impressions: t.impressions
  };
}
function sumVideo(rows) {
  const xs = rows.filter((x) => Boolean(x));
  if (!xs.length) return null;
  return xs.reduce((t, x) => ({ plays: t.plays + x.plays, p25: t.p25 + x.p25, p50: t.p50 + x.p50, p75: t.p75 + x.p75, p100: t.p100 + x.p100, thruplay: t.thruplay + x.thruplay }), { plays: 0, p25: 0, p50: 0, p75: 0, p100: 0, thruplay: 0 });
}
function medianOf(xs) {
  const v = xs.filter((x) => x !== null).sort((a, b) => a - b);
  if (v.length < 3) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

// src/materials.ts
var MATERIAL_SLIDE_KINDS = ["cover", "statement", "split", "columns", "cards", "flow", "cycles", "table", "numbers", "compare", "price", "ramp", "groups", "calendar", "entries", "closing"];
var MATERIAL_SLIDE_LABELS = {
  cover: "capa",
  statement: "frase grande (objetivo)",
  split: "n\xFAmero grande com texto e caixa de destaque",
  columns: "colunas (2 a 4)",
  cards: "cart\xF5es com nota (radar)",
  flow: "fluxo em tr\xEAs passos",
  cycles: "ciclos (2 a 4 colunas com a\xE7\xF5es)",
  table: "tabela de duas colunas",
  numbers: "n\xFAmeros grandes",
  compare: "hoje \xD7 proposta",
  price: "investimento",
  ramp: "rampa de valores",
  groups: "grupos com listas",
  calendar: "calend\xE1rio do m\xEAs",
  entries: "conte\xFAdos (cart\xF5es com data)",
  closing: "pr\xF3ximos passos e contato"
};
var MATERIAL_SLIDE_FORMAT = {
  cover: "title, highlight (o fim do t\xEDtulo, em it\xE1lico), subtitle (uma frase), tagline (linha pequena embaixo, opcional), label (opcional)",
  statement: "text (a frase), highlight (o fim, em it\xE1lico), complement (opcional)",
  split: 'number (curto: "1986", "1.320+"), numberCaption, text, box { title, text }',
  columns: "items [2 a 4] { label, title (opcional), text, hi (true = caixa de destaque, no m\xE1ximo 1) }; roomy (true = letra maior, para 3 colunas de pouco texto)",
  cards: 'items [2 a 8] { name, place, score ("48" ou "\u2014"), summary (1 ou 2 frases), signal (um dado medido, curto), own (true = a pr\xF3pria marca) }',
  flow: "steps [3] { label, title, text, hi (opcional) }",
  cycles: 'items [2 a 4] { when ("meses 1 a 3"), title, actions [2 a 5], effect, measure }',
  table: "rows [3 a 7] { label, value }",
  numbers: "items [2 a 4] { value (curto), caption }, text (opcional)",
  compare: "left { label, value (opcional), items [] }, right { label, value (opcional), items [] }",
  price: 'value ("R$ 10,7 mil"), period ("por m\xEAs"), text, items [at\xE9 9] (o que entra), aside (o que fica \xE0 parte, opcional)',
  ramp: "steps [2 a 4] { when, value, text }",
  groups: "groups [2 a 5] { title, items [] }, box { label, text } (opcional)",
  calendar: 'month ("AAAA-MM"), entries [] { day (1 a 31), title (curto), tag (opcional: formato ou canal) }',
  entries: 'entries [1 a 6] { date ("12/11" ou "AAAA-MM-DD"), title, format (opcional), text }',
  closing: "steps [3 a 4] (pr\xF3ximos passos), contact { name, phone, site } (opcional)"
};
var MATERIAL_TONES = ["dark", "light", "accent"];
var MATERIAL_IDENTITIES = ["raro", "client"];
var MATERIAL_IDENTITY_LABELS = { raro: "a identidade da RARO", client: "a identidade do BIS publicado da marca" };
var MATERIAL_SLIDES_MAX = 30;
var MATERIAL_RECIPES_MAX = 60;
var MATERIAL_TEXT_MAX = 900;
var MATERIAL_RENDER_SECONDS = 30;
var RARO_RULES = [
  "Tom seguro, pr\xF3ximo e objetivo, sem jarg\xE3o de ag\xEAncia e sem exagero.",
  "Nunca prometa n\xFAmero (vendas, crescimento, metas): efeito esperado \xE9 dire\xE7\xE3o; meta s\xF3 depois do diagn\xF3stico.",
  'Hip\xF3tese \xE9 marcada como hip\xF3tese ("pelo que vimos at\xE9 aqui", "o primeiro ciclo vai testar").',
  "N\xE3o invente dado do cliente nem do concorrente: o que n\xE3o se confirmou n\xE3o entra como fato. Cite a fonte no campo source.",
  "N\xE3o use escassez nem urg\xEAncia. N\xE3o cite not\xEDcia negativa sobre a empresa. Nunca ponha custo, margem ou cr\xE9dito.",
  "Encurte o texto em vez de esperar que caiba: t\xEDtulo em uma ou duas linhas, pre\xE7o numa linha s\xF3."
];
var MATERIAL_RECIPE_DEFAULTS = [
  {
    key: "proposta-comercial",
    name: "Proposta comercial",
    identity: "raro",
    description: "A proposta que a RARO manda para uma conta nova ou numa renova\xE7\xE3o: o desafio, o que j\xE1 sabemos, o radar dos concorrentes, os 12 meses em 4 ciclos e o investimento.",
    rules: [
      ...RARO_RULES,
      "Antes de escrever, entenda o trabalho de verdade: o \xF3bvio raramente \xE9 o job (na Vitapelli, n\xE3o era exporta\xE7\xE3o; era chegar no artes\xE3o).",
      'Use os dados da Machine: machine_cliente, machine_radar (concorrentes, \xCDndice de Raridade, sinais medidos) e machine_proposta (s\xF3 o que vem em "para_o_cliente").',
      'Os 4 ciclos s\xE3o trimestres: "meses 1 a 3" a "meses 10 a 12", cada um com a\xE7\xF5es, efeito esperado e como medir.',
      "A capa usa o tom dark; o objetivo do ano e o investimento usam o tom accent; alterne dark e light no resto."
    ].join("\n"),
    slides: [
      { kind: "cover", purpose: 't\xEDtulo com o fim em it\xE1lico; subtitle = a parceria para qu\xEA; tagline "12 meses \xB7 4 ciclos \xB7 RARO\u2122"', required: true },
      { kind: "numbers", purpose: "s\xF3 em renova\xE7\xE3o: o que a rela\xE7\xE3o j\xE1 entregou, com a fonte" },
      { kind: "split", purpose: 'o que ouvimos: o desafio nas palavras do cliente; box = "O trabalho" (o job da RARO)' },
      { kind: "columns", purpose: "o que j\xE1 sabemos: a empresa, o mercado e a oportunidade (hi, marcada como hip\xF3tese), com as fontes", repeat: true },
      { kind: "cards", purpose: "o radar da machine.raro\u2122: a pr\xF3pria marca (own) e os concorrentes, com o \xCDndice de Raridade e um sinal medido", repeat: true },
      { kind: "statement", purpose: "o objetivo do ano", required: true },
      { kind: "flow", purpose: "o que a machine.raro\u2122 junta, organiza e quem ela orienta" },
      { kind: "cycles", purpose: "os 12 meses em 4 ciclos", required: true },
      { kind: "table", purpose: "como medimos: objetivo, situa\xE7\xE3o inicial, a\xE7\xE3o, indicador, avalia\xE7\xE3o (exemplo ilustrativo)" },
      { kind: "compare", purpose: "s\xF3 em renova\xE7\xE3o: hoje \xD7 proposta" },
      { kind: "price", purpose: "o investimento e a base da parceria" },
      { kind: "ramp", purpose: "quando o valor sobe numa renova\xE7\xE3o: a rampa por ciclo" },
      { kind: "closing", purpose: "pr\xF3ximos passos concretos e o contato comercial", required: true }
    ]
  },
  {
    key: "cronograma",
    name: "Cronograma de conte\xFAdo",
    identity: "client",
    description: "A apresenta\xE7\xE3o do cronograma do m\xEAs para o cliente aprovar: a ideia do m\xEAs, o calend\xE1rio e cada conte\xFAdo com data, formato e o porqu\xEA.",
    rules: [
      "Tom de quem apresenta o pr\xF3prio trabalho ao cliente: claro, direto, sem jarg\xE3o.",
      "Os conte\xFAdos v\xEAm das pe\xE7as da demanda no Ritmo (machine_demandas): data, t\xEDtulo, formato e a defesa de cada um. N\xE3o invente conte\xFAdo que n\xE3o est\xE1 l\xE1.",
      "A identidade \xE9 a do BIS publicado da marca: n\xE3o descreva cores nem fontes, a Machine aplica.",
      "Um slide de conte\xFAdos leva at\xE9 6; com mais, repita o slide.",
      "Nunca escreva custo ou valor no cronograma."
    ].join("\n"),
    slides: [
      { kind: "cover", purpose: "o m\xEAs e a marca; subtitle = a ideia do m\xEAs", required: true },
      { kind: "statement", purpose: "a ideia que amarra o m\xEAs" },
      { kind: "columns", purpose: "os focos do m\xEAs (2 a 4)" },
      { kind: "calendar", purpose: "o m\xEAs inteiro, com cada conte\xFAdo no seu dia", required: true },
      { kind: "entries", purpose: "cada conte\xFAdo: data, t\xEDtulo, formato e o porqu\xEA", required: true, repeat: true },
      { kind: "closing", purpose: "o que precisamos do cliente para seguir (aprova\xE7\xE3o, materiais, datas)", required: true }
    ]
  }
];
var nonEmpty = (v) => typeof v === "string" && v.trim().length > 0;
var arr = (v) => Array.isArray(v) ? v : [];
function materialProblems(recipe, content) {
  const out = [];
  if (!nonEmpty(content?.title)) out.push("falta o title do material (o nome que vai no rodap\xE9)");
  const slides = arr(content?.slides);
  if (!slides.length) return [...out, "o material n\xE3o tem slides"];
  if (slides.length > MATERIAL_SLIDES_MAX) out.push(`s\xE3o ${slides.length} slides: o m\xE1ximo \xE9 ${MATERIAL_SLIDES_MAX}`);
  const allowed = new Set(recipe.slides.map((s) => s.kind));
  const count = /* @__PURE__ */ new Map();
  slides.forEach((s, i) => {
    const at = `slide ${i + 1} (${s?.kind ?? "sem tipo"})`;
    if (!s || !MATERIAL_SLIDE_KINDS.includes(s.kind)) {
      out.push(`${at}: tipo desconhecido; os tipos s\xE3o ${MATERIAL_SLIDE_KINDS.join(", ")}`);
      return;
    }
    if (!allowed.has(s.kind)) out.push(`${at}: a receita "${recipe.name}" n\xE3o usa este tipo`);
    count.set(s.kind, (count.get(s.kind) ?? 0) + 1);
    if (s.tone && !MATERIAL_TONES.includes(s.tone)) out.push(`${at}: tone vale dark, light ou accent`);
    const long = Object.entries(s).find(([, v]) => typeof v === "string" && v.length > MATERIAL_TEXT_MAX);
    if (long) out.push(`${at}: o campo ${long[0]} passa de ${MATERIAL_TEXT_MAX} caracteres: encurte`);
    const items = arr(s.items);
    const range = (list2, min, max, what) => {
      if (list2.length < min || list2.length > max) out.push(`${at}: ${what} vai de ${min} a ${max}; vieram ${list2.length}`);
    };
    switch (s.kind) {
      case "cover":
        if (!nonEmpty(s.title)) out.push(`${at}: falta title`);
        break;
      case "statement":
        if (!nonEmpty(s.text)) out.push(`${at}: falta text`);
        break;
      case "split":
        if (!nonEmpty(s.text)) out.push(`${at}: falta text`);
        break;
      case "columns":
        range(items, 2, 4, "items");
        if (items.filter((x) => x?.hi).length > 1) out.push(`${at}: s\xF3 um item pode ter hi`);
        break;
      case "cards":
        range(items, 2, 8, "items");
        if (items.some((x) => !nonEmpty(x?.name))) out.push(`${at}: todo cart\xE3o precisa de name`);
        break;
      case "flow":
        range(arr(s.steps), 3, 3, "steps");
        break;
      case "cycles":
        range(items, 2, 4, "items");
        if (items.some((x) => arr(x?.actions).length > 5)) out.push(`${at}: no m\xE1ximo 5 a\xE7\xF5es por ciclo`);
        break;
      case "table":
        range(arr(s.rows), 2, 8, "rows");
        break;
      case "numbers":
        range(items, 2, 4, "items");
        break;
      case "compare":
        if (!s.left || !s.right) out.push(`${at}: faltam left e right`);
        break;
      case "price":
        if (!nonEmpty(s.value)) out.push(`${at}: falta value`);
        if (arr(s.items).length > 9) out.push(`${at}: no m\xE1ximo 9 itens`);
        break;
      case "ramp":
        range(arr(s.steps), 2, 4, "steps");
        break;
      case "groups":
        range(arr(s.groups), 2, 5, "groups");
        break;
      case "calendar":
        if (!/^\d{4}-\d{2}$/.test(String(s.month ?? ""))) out.push(`${at}: month vai como AAAA-MM`);
        break;
      case "entries":
        range(arr(s.entries), 1, 6, "entries");
        break;
      case "closing":
        range(arr(s.steps), 1, 5, "steps");
        break;
    }
  });
  for (const r of recipe.slides) {
    const n = count.get(r.kind) ?? 0;
    if (r.required && n === 0) out.push(`falta o slide obrigat\xF3rio "${MATERIAL_SLIDE_LABELS[r.kind]}" (${r.kind}): ${r.purpose}`);
    if (!r.repeat && n > 1 && recipe.slides.filter((x) => x.kind === r.kind).length < n) out.push(`o tipo ${r.kind} aparece ${n} vezes, e a receita prev\xEA ${recipe.slides.filter((x) => x.kind === r.kind).length}`);
  }
  return out;
}
function materialHasValues(content) {
  return (Array.isArray(content?.slides) ? content.slides : []).some((s) => s?.kind === "price" || s?.kind === "ramp" || s?.kind === "compare" && Boolean(s.left?.value || s.right?.value));
}
function materialRecipeGuide(recipe) {
  return [
    `RECEITA: ${recipe.name}. ${recipe.description}`,
    `IDENTIDADE: ${MATERIAL_IDENTITY_LABELS[recipe.identity]} (a Machine aplica; n\xE3o descreva cores nem fontes).`,
    "SLIDES, NA ORDEM (obrigat\xF3rio = tem de existir; repete = pode aparecer mais de uma vez):",
    ...recipe.slides.map((s, i) => `${i + 1}. ${s.kind}${s.required ? " (obrigat\xF3rio)" : ""}${s.repeat ? " (repete)" : ""}: ${s.purpose}. Campos: ${MATERIAL_SLIDE_FORMAT[s.kind]}`),
    "Todo slide aceita: label, title, highlight, tone (dark, light, accent), note, source.",
    "REGRAS DE ESCRITA:",
    recipe.rules
  ].join("\n");
}

// src/index.ts
var ORGANIZATION_STATUSES = ["active", "archived"];
var MEMBERSHIP_ROLES = ["owner", "partner", "analyst", "maker", "network", "client_admin", "client"];
var ROLE_RANK = {
  owner: 40,
  partner: 30,
  analyst: 20,
  maker: 18,
  network: 12,
  client_admin: 11,
  client: 10
};
var MEMBERSHIP_ROLE_LABELS = {
  owner: "s\xF3cio-fundador",
  partner: "s\xF3cio",
  analyst: "equipe",
  maker: "produ\xE7\xE3o",
  network: "parceiro (rede)",
  client_admin: "cliente \xB7 aprova",
  client: "cliente"
};
var INTERNAL_ROLES = ["owner", "partner", "analyst", "maker"];
var CLIENT_ROLES = ["client_admin", "client"];
function isClientRole(role) {
  return CLIENT_ROLES.includes(role);
}
function hasRoleAtLeast(role, minimum) {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
function isInternalRole(role) {
  return INTERNAL_ROLES.includes(role);
}
var COMMERCIAL_STATUSES = ["prospect", "ver", "live", "fazer", "continuous", "paused", "closed"];
var COMMERCIAL_STATUS_LABELS = { prospect: "prospect", ver: "em VER", live: "Machine ativa", fazer: "em FAZER", continuous: "cont\xEDnuo", paused: "pausado", closed: "encerrado" };
var ORGANIZATION_SIZES = ["micro", "small", "medium", "large"];
var ORGANIZATION_SIZE_LABELS = { micro: "micro", small: "pequena", medium: "m\xE9dia", large: "grande" };
var CHANNEL_KINDS = [
  "instagram",
  "tiktok",
  "youtube",
  "facebook",
  "linkedin",
  "whatsapp",
  "website",
  "ecommerce",
  "marketplace",
  "pdv",
  "ooh",
  "press",
  "events",
  "b2b_catalog",
  "packaging",
  "email",
  "sales_team",
  "other"
];
var CHANNEL_KIND_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  website: "Site",
  ecommerce: "E-commerce",
  marketplace: "Marketplace",
  pdv: "Ponto de venda",
  ooh: "OOH / m\xEDdia exterior",
  press: "Imprensa",
  events: "Eventos",
  b2b_catalog: "Cat\xE1logo B2B",
  packaging: "Embalagem",
  email: "E-mail / CRM",
  sales_team: "Equipe de vendas",
  other: "Outro"
};
var INVITATION_STATUSES = ["pending", "accepted", "expired", "revoked"];
var PROJECT_TYPES = ["ver", "live", "continuous", "make", "other"];
var QUESTION_SOURCES = ["machine", "client_mandate", "edit", "inherited"];
var QUESTION_SOURCE_LABELS = { machine: "encontrada no VER \xB7 martelo de s\xF3cio", client_mandate: "pedido expl\xEDcito do cliente", edit: "reescrita", inherited: "herdada do ciclo anterior" };
var CONVERSATION_KINDS = ["briefing", "interview"];
var CONVERSATION_DEFAULT_GOALS = {
  briefing: `A marca, n\xE3o um detalhe. Cobrir, nesta ordem:
1. Origem: por que a marca surgiu, onde, quando, por quem. O que o fundador viu que os outros n\xE3o viam.
2. Hoje: o que a marca faz, para quem, como \xE9 vista por quem compra e por quem vende.
3. Momento: o que mudou nos \xFAltimos tempos, o que incomoda, o que trava.
4. O que j\xE1 foi tentado e o que aconteceu.
5. Onde a pessoa quer que a marca chegue nos pr\xF3ximos meses, e o que seria sucesso.
6. O que ela acha que ningu\xE9m v\xEA.`,
  interview: `A viv\xEAncia desta pessoa com a empresa, n\xE3o a opini\xE3o dela sobre marketing. Cobrir:
1. Como ela conheceu e por que escolheu (ou deixou de escolher).
2. Uma situa\xE7\xE3o recente concreta, boa ou ruim.
3. O que a frustra e o que a encanta, com exemplos.
4. Como ela compara com alternativas.
5. O que faria ela indicar para algu\xE9m.`
};
var CONVERSATION_KIND_LABELS = { briefing: "briefing por voz", interview: "entrevista por link" };
var CONVERSATION_STATUSES = ["open", "in_progress", "done", "expired"];
var CONVERSATION_STATUS_LABELS = { open: "aguardando", in_progress: "em andamento", done: "conclu\xEDda", expired: "expirada" };
var LIVE_CADENCES = ["monthly", "biweekly", "quarterly"];
var LIVE_CADENCE_LABELS = { monthly: "mensal", biweekly: "quinzenal", quarterly: "trimestral" };
var LIVE_CADENCE_DAYS = { monthly: 30, biweekly: 14, quarterly: 91 };
var PROJECT_STATUSES = ["draft", "active", "paused", "closed"];
var CYCLE_STATUSES = ["planned", "active", "closed"];
var VER_STAGES = [
  "observar",
  "tornar_visivel",
  "identificar_o_comum",
  "contrariar",
  "encontrar",
  "provar"
];
var VER_STAGE_LABELS = {
  observar: "Observar",
  tornar_visivel: "Tornar vis\xEDvel",
  identificar_o_comum: "Identificar o comum",
  contrariar: "Contrariar",
  encontrar: "Encontrar",
  provar: "Provar"
};
var CLIENT_STAGES = ["ver", "o_raro", "fazer", "medir"];
var SOURCE_TYPES = [
  "interview",
  "document",
  "spreadsheet",
  "image",
  "video",
  "audio",
  "url",
  "field_note",
  "research",
  "analytics",
  "crm",
  "social",
  "review",
  "meeting",
  "other"
];
var SOURCE_PROCESSING_STATUSES = [
  "uploaded",
  "queued",
  "processing",
  "processed",
  "failed"
];
var STATEMENT_KINDS = [
  "fact",
  "observation",
  "opinion",
  "signal",
  "inference",
  "hypothesis",
  "decision",
  "learning"
];
var EVIDENCE_TYPES = ["quote", "data", "image", "behavior", "document", "other"];
var QUESTION_STATUSES = ["open", "investigating", "answered", "obsolete"];
var HYPOTHESIS_STATUSES = [
  "proposed",
  "investigating",
  "supported",
  "weakened",
  "rejected",
  "parked"
];
var RELATION_KINDS = [
  "supports",
  "contradicts",
  "derived_from",
  "related_to",
  "answers",
  "tests",
  "caused_by",
  "raises",
  "implements",
  "measures"
];
var HUMAN_STATUSES = ["under_review", "accepted", "rejected"];
var GENERATOR_TYPES = ["human", "ai", "rule"];
var SIGNAL_KINDS = ["change", "recurrence", "anomaly", "fact", "tension", "contradiction"];
var SIGNAL_KIND_LABELS = {
  change: "mudan\xE7a",
  recurrence: "recorr\xEAncia",
  anomaly: "anomalia",
  fact: "fato",
  tension: "tens\xE3o",
  contradiction: "contradi\xE7\xE3o"
};
var RARITY_DIMENSIONS = [
  "relevance",
  "evidence",
  "distinctiveness",
  "ownership",
  "copy_difficulty",
  "economic_potential",
  "uncertainty"
];
var RARITY_DIMENSION_LABELS = {
  relevance: "relev\xE2ncia",
  evidence: "evid\xEAncia",
  distinctiveness: "distintividade",
  ownership: "propriedade",
  copy_difficulty: "dific. de copiar",
  economic_potential: "potencial",
  uncertainty: "incerteza"
};
var ENTITY_TYPES = [
  "source",
  "evidence",
  "observation",
  "question",
  "signal",
  "hypothesis",
  "decision",
  "session",
  "solution",
  "claim",
  "initiative",
  "effect",
  "learning",
  "synthesis"
];
var ENTITY_TYPE_LABELS = {
  source: "fonte",
  evidence: "evid\xEAncia",
  observation: "observa\xE7\xE3o",
  question: "pergunta",
  signal: "sinal",
  hypothesis: "hip\xF3tese",
  decision: "decis\xE3o",
  session: "sess\xE3o",
  solution: "solu\xE7\xE3o",
  claim: "afirma\xE7\xE3o",
  initiative: "iniciativa",
  effect: "efeito",
  learning: "aprendizado",
  synthesis: "s\xEDntese"
};
var RELATION_KIND_LABELS = {
  supports: "sustenta",
  contradicts: "contradiz",
  derived_from: "deriva de",
  related_to: "relaciona-se com",
  answers: "responde",
  tests: "testa",
  caused_by: "causado por",
  raises: "levanta",
  implements: "implementa",
  measures: "mede"
};
var WALL_ITEM_KINDS = ["ref", "note", "image", "quote"];
var SERENDIPITY_FEEDBACK = ["useful", "interesting", "forced", "irrelevant"];
var AI_FEEDBACK = SERENDIPITY_FEEDBACK;
var AI_FEEDBACK_LABELS = { useful: "\xFAtil", interesting: "interessante", forced: "for\xE7ado", irrelevant: "irrelevante" };
var SUGGESTION_KINDS = ["evidence", "signal", "observation", "question", "contradiction", "relation", "hypothesis", "cycle_question", "point"];
var SUGGESTION_STATUSES = ["pending", "accepted", "rejected"];
var SESSION_KINDS = ["kickoff", "immersion", "interview", "field_visit", "store_observation", "product_analysis", "sales_talk", "consumer_talk", "distributor_talk", "confrontation", "decision", "debrief", "other"];
var SESSION_KIND_LABELS = { kickoff: "kickoff", immersion: "imers\xE3o", interview: "entrevista", field_visit: "visita (f\xE1brica, opera\xE7\xE3o)", store_observation: "observa\xE7\xE3o de loja", product_analysis: "an\xE1lise de produto", sales_talk: "conversa com vendedores", consumer_talk: "conversa com consumidores", distributor_talk: "conversa com distribuidores", confrontation: "confronto", decision: "decis\xE3o", debrief: "devolutiva", other: "outra" };
var SESSION_STATUSES = ["planned", "done", "cancelled"];
var SOLUTION_STATUSES = ["draft", "reviewed", "approved", "published"];
var SOLUTION_STATUS_LABELS = { draft: "rascunho da Machine", reviewed: "revisada", approved: "aprovada por s\xF3cio", published: "publicada na sala" };
var CLAIM_KINDS = ["finding", "principle", "recommendation"];
var CLAIM_KIND_LABELS = { finding: "o que vimos", principle: "o que passa a valer", recommendation: "o que fazer" };
var OUTCOME_PATHS = ["client_executes", "raro_executes", "hybrid", "continuous", "live", "closed"];
var OUTCOME_PATH_LABELS = { client_executes: "o cliente executa", raro_executes: "a Raro executa", hybrid: "execu\xE7\xE3o h\xEDbrida", continuous: "CONT\xCDNUO", live: "Machine ativa (LIVE)", closed: "encerra" };
var INITIATIVE_KINDS = ["initiative", "experiment"];
var INITIATIVE_STATUSES = ["planned", "in_progress", "done", "measured", "dropped"];
var INITIATIVE_STATUS_LABELS = { planned: "planejada", in_progress: "em andamento", done: "feita", measured: "medida", dropped: "abandonada" };
var EXECUTORS = ["raro", "client", "hybrid"];
var EXECUTOR_LABELS = { raro: "Raro", client: "cliente", hybrid: "h\xEDbrido" };
var DELIVERABLE_STATUSES = ["briefing", "production", "review", "client_review", "approved", "live", "dropped"];
var DELIVERABLE_STATUS_LABELS = { briefing: "briefing", production: "em produ\xE7\xE3o", review: "revis\xE3o Raro", client_review: "com o cliente", approved: "aprovada", live: "no ar", dropped: "descartada" };
var DELIVERABLE_BOARD = ["briefing", "production", "review", "client_review", "approved", "live"];
var DELIVERABLE_CLIENT_VISIBLE = ["client_review", "approved", "live"];
var DELIVERABLE_FORMATS = ["post", "story", "video", "reel", "catalog", "page", "email", "print", "packaging", "event", "presentation", "script", "action", "other"];
var DELIVERABLE_FORMAT_LABELS = { post: "post", story: "story", video: "v\xEDdeo", reel: "reel", catalog: "cat\xE1logo", page: "p\xE1gina", email: "e-mail", print: "impresso", packaging: "embalagem", event: "evento", presentation: "apresenta\xE7\xE3o", script: "roteiro", action: "a\xE7\xE3o", other: "outro" };
var DELIVERABLE_ACTION_FORMATS = ["action"];
var VERSION_KINDS = ["image", "pdf", "video", "audio", "link", "other"];
var RENDER_STATUSES = ["pending", "ready", "failed", "none"];
var REVIEW_ROUND_KINDS = ["internal", "client"];
var REVIEW_ROUND_KIND_LABELS = { internal: "revis\xE3o interna", client: "revis\xE3o do cliente" };
var REVIEW_ROUND_STATUSES = ["open", "approved", "changes_requested", "cancelled"];
var REVIEW_ROUND_STATUS_LABELS = { open: "em revis\xE3o", approved: "aprovada", changes_requested: "com altera\xE7\xF5es pedidas", cancelled: "cancelada" };
var REVIEW_DECISIONS = ["approved", "changes_requested"];
var REVIEW_AUDIENCES = ["internal", "client"];
var COMMENT_STATUSES = ["open", "resolved"];
var MOCKUP_FRAMES = ["none", "instagram_feed", "instagram_story", "instagram_reel", "tiktok", "facebook_feed", "linkedin_feed", "youtube", "whatsapp", "email", "website", "pdv"];
var MOCKUP_FRAME_LABELS = { none: "sem moldura", instagram_feed: "Instagram \xB7 feed", instagram_story: "Instagram \xB7 story", instagram_reel: "Instagram \xB7 reel", tiktok: "TikTok", facebook_feed: "Facebook \xB7 feed", linkedin_feed: "LinkedIn", youtube: "YouTube", whatsapp: "WhatsApp", email: "e-mail", website: "site", pdv: "ponto de venda" };
function defaultMockupFrame(channelKind, format) {
  if (channelKind === "instagram") return format === "story" ? "instagram_story" : format === "reel" || format === "video" ? "instagram_reel" : "instagram_feed";
  if (channelKind === "tiktok") return "tiktok";
  if (channelKind === "facebook") return "facebook_feed";
  if (channelKind === "linkedin") return "linkedin_feed";
  if (channelKind === "youtube") return "youtube";
  if (channelKind === "whatsapp") return "whatsapp";
  if (channelKind === "email" || format === "email") return "email";
  if (channelKind === "website" || channelKind === "ecommerce" || format === "page") return "website";
  if (channelKind === "pdv") return "pdv";
  return "none";
}
var COST_CATEGORIES = ["production", "people", "media", "venue", "equipment", "material", "fees", "other"];
var COST_CATEGORY_LABELS = { production: "produ\xE7\xE3o", people: "pessoas", media: "m\xEDdia", venue: "local", equipment: "equipamento", material: "material", fees: "honor\xE1rios", other: "outro" };
var COST_KINDS = ["estimate", "actual"];
var COST_KIND_LABELS = { estimate: "estimado", actual: "realizado" };
var CLIENT_DECISIONS = ["approved", "declined"];
var CLIENT_DECISION_LABELS = { approved: "aprovada pelo cliente", declined: "cliente declinou" };
var NOTIFICATION_KINDS = ["measurement_due", "measurement_overdue", "initiative_client_decision", "solution_published", "session_summary", "deliverable_client_review", "deliverable_client_decision", "review_requested", "review_decided", "review_comment", "live_cycle_rolled", "conversation_done", "conversation_trouble", "data_delivery", "collector_limit", "connector_down", "demand_requested", "demand_waiting_client", "demand_note", "demand_step", "demand_publish", "demand_route", "demand_approval", "modo_approval", "modo_decision", "modo_mold_missing", "ad_balance", "ad_alert", "modo_ready", "modo_debug", "forwarded", "creation_decision", "image_contract"];
var NOTIFICATION_KIND_LABELS = { measurement_due: "aferi\xE7\xE3o em breve", measurement_overdue: "aferi\xE7\xE3o vencida", initiative_client_decision: "decis\xE3o do cliente", solution_published: "Solu\xE7\xE3o publicada", session_summary: "resumo de sess\xE3o", deliverable_client_review: "pe\xE7a para aprovar", deliverable_client_decision: "pe\xE7a: decis\xE3o do cliente", review_requested: "revis\xE3o pedida", review_decided: "revis\xE3o decidida", review_comment: "coment\xE1rio na pe\xE7a", live_cycle_rolled: "LIVE: novo per\xEDodo", conversation_done: "conversa conclu\xEDda", conversation_trouble: "conversa com problema", data_delivery: "entrega de dados", collector_limit: "limite da coleta", connector_down: "conex\xE3o ca\xEDda", demand_requested: "pedido do cliente", demand_waiting_client: "demanda esperando voc\xEA", demand_note: "resposta numa demanda", demand_step: "sua vez numa demanda", demand_publish: "publica\xE7\xE3o", demand_route: "percurso para confirmar", demand_approval: "aprova\xE7\xE3o de demanda", modo_approval: "material do MODO para aprovar", modo_decision: "decis\xE3o sobre um material do MODO", modo_mold_missing: "falta um molde no BIS", ad_balance: "saldo de an\xFAncios", ad_alert: "aviso de m\xEDdia", modo_ready: "material pronto no MODO", modo_debug: "relato de ajuste no MODO", forwarded: "encaminhado para voc\xEA", creation_decision: "o cliente decidiu num estudo", image_contract: "contrato de uso de imagem" };
var FORWARD_NOTE_MAX = 280;
var FORWARD_PEOPLE_MAX = 12;
var FORWARD_BLOCKED = ["/client", "/conversa", "/entrevista", "/entrega", "/login", "/auth"];
function forwardHref(href) {
  const h = href.trim();
  if (!/^\/[^/\\]/.test(h) || h.length > 500 || /[\s<>"\\]/.test(h)) return null;
  const path = h.split(/[?#]/)[0];
  return FORWARD_BLOCKED.some((b) => path === b || path.startsWith(`${b}/`)) ? null : h;
}
var RITMO_NOTIFICATION_GROUPS = [
  { key: "requested", label: "Pedido novo do cliente", hint: "quando algu\xE9m do cliente abre um pedido", kinds: ["demand_requested"] },
  { key: "step", label: "Sua vez numa demanda", hint: "uma etapa do percurso chegou em voc\xEA", kinds: ["demand_step"] },
  { key: "route", label: "Percurso para conferir", hint: "a Machine prop\xF4s o percurso de uma demanda", kinds: ["demand_route"] },
  { key: "note", label: "Mensagem numa demanda", hint: "o cliente respondeu ou a equipe avisou", kinds: ["demand_note", "demand_waiting_client"] },
  { key: "approval", label: "Aprova\xE7\xF5es", hint: "conte\xFAdo enviado para aprovar e a decis\xE3o do cliente", kinds: ["demand_approval", "creation_decision"] },
  { key: "publish", label: "Hora de publicar", hint: "conte\xFAdo que precisa ser publicado \xE0 m\xE3o", kinds: ["demand_publish"] },
  { key: "media", label: "M\xEDdia paga", hint: "saldo baixo, an\xFAncio reprovado, campanha sem entrega", kinds: ["ad_balance", "ad_alert"] },
  { key: "collect", label: "Conex\xF5es e coleta", hint: "conta desconectada, entrega de dados, limite do coletor", kinds: ["connector_down", "data_delivery", "collector_limit"] }
];
var MEASUREMENT_KINDS = ["quantitative", "qualitative", "signal"];
var MEASUREMENT_KIND_LABELS = { quantitative: "quantitativo", qualitative: "qualitativo", signal: "sinal intermedi\xE1rio" };
var EXPERIMENT_OUTCOMES = ["supports", "weakens", "inconclusive"];
var EXPERIMENT_OUTCOME_LABELS = { supports: "sustenta a hip\xF3tese", weakens: "enfraquece a hip\xF3tese", inconclusive: "inconclusivo" };
var VERDICTS = ["useful", "interesting", "irrelevant", "forced", "confirmed", "rejected", "follow", "investigate", "discard"];
var VERDICT_LABELS = { useful: "\xFAtil", interesting: "interessante", irrelevant: "irrelevante", forced: "for\xE7ado", confirmed: "confirmado", rejected: "rejeitado", follow: "seguir", investigate: "investigar", discard: "descartar" };
var HYPOTHESIS_VERDICTS = ["follow", "investigate", "discard"];
var DECISION_STATUSES = ["recorded", "in_progress", "done", "reverted"];
var DECISION_STATUS_LABELS = { recorded: "registrada", in_progress: "em execu\xE7\xE3o", done: "conclu\xEDda", reverted: "revertida" };
var EFFECT_SOURCES = ["sales", "crm", "analytics", "ecommerce", "social", "survey", "nps", "field", "qualitative", "other"];
var EFFECT_SOURCE_LABELS = { sales: "vendas", crm: "CRM", analytics: "analytics", ecommerce: "e-commerce", social: "social", survey: "pesquisa", nps: "NPS", field: "campo", qualitative: "qualitativo", other: "outro" };
var LEARNING_SCOPES = ["project", "raro", "anonymized"];
var LEARNING_SCOPE_LABELS = { project: "s\xF3 este projeto", raro: "mem\xF3ria Raro (interno)", anonymized: "transfer\xEDvel (abstra\xEDdo)" };
var SYNTHESIS_STATUSES = ["draft", "reviewed", "published"];
var VER_STAGE_GUIDE = {
  observar: { question: "O que existe?", does: "colocar material bruto na Machine: entrevistas, campo, dados, concorrentes." },
  tornar_visivel: { question: "O que j\xE1 est\xE1 aqui, mas ainda n\xE3o foi visto?", does: "marcar evid\xEAncias, deixar a Machine apontar padr\xF5es e tens\xF5es, nomear sinais." },
  identificar_o_comum: { question: "O que todo mundo nesse mercado faz e diz?", does: "observa\xE7\xF5es sobre o comum; linguagem repetida; o que o cliente faz igual aos outros." },
  contrariar: { question: "O que pode estar errado?", does: "registrar contradi\xE7\xF5es, remar contra as primeiras ideias, abrir perguntas." },
  encontrar: { question: "O que s\xF3 este cliente pode fazer?", does: "formular hip\xF3teses, julgar nas dimens\xF5es do O RARO, escolher o que merece prova." },
  provar: { question: "Como vamos saber?", does: "desenhar experimentos que podem enfraquecer a hip\xF3tese; registrar decis\xF5es." }
};
var CLIENT_SECTIONS = ["seeing", "wall", "raro", "make", "results", "library"];
var CLIENT_SECTION_LABELS = { seeing: "O que estamos vendo", wall: "Parede", raro: "O Raro", make: "Fazer", results: "Resultados", library: "Biblioteca" };
var CLIENT_SECTION_ENTITIES = {
  seeing: ["synthesis", "signal", "observation", "evidence", "question"],
  wall: ["evidence", "signal", "observation", "hypothesis", "source"],
  raro: ["hypothesis"],
  make: ["initiative", "decision", "solution"],
  results: ["effect", "learning", "initiative"],
  library: ["source"]
};
var PUBLICATION_STATUSES = ["draft", "published", "hidden"];
var PUBLICATION_STATUS_LABELS = { draft: "rascunho", published: "publicado", hidden: "oculto" };
var REACTION_KINDS = ["makes_sense", "doubt", "want_more"];
var REACTION_KIND_LABELS = { makes_sense: "faz sentido", doubt: "tenho d\xFAvida", want_more: "quero saber mais" };
var CLIENT_STAGE_LABELS = { ver: "VER", o_raro: "O RARO\u2122", fazer: "FAZER", medir: "MEDIR" };
var DOMAIN_EVENTS = {
  ORGANIZATION_CREATED: "organization.created",
  PROJECT_CREATED: "project.created",
  PROJECT_UPDATED: "project.updated",
  CYCLE_STARTED: "cycle.started",
  CYCLE_CLOSED: "cycle.closed",
  FILE_STORED: "file.stored",
  SOURCE_CREATED: "source.created",
  SOURCE_PROCESSED: "source.processed",
  EVIDENCE_CREATED: "evidence.created",
  EVIDENCE_VALIDATED: "evidence.validated",
  OBSERVATION_CREATED: "observation.created",
  QUESTION_CREATED: "question.created",
  SIGNAL_DETECTED: "signal.detected",
  HYPOTHESIS_CREATED: "hypothesis.created",
  HYPOTHESIS_CHALLENGED: "hypothesis.challenged",
  EXPERIMENT_STARTED: "experiment.started",
  EXPERIMENT_COMPLETED: "experiment.completed",
  SESSION_HELD: "session.held",
  SOLUTION_APPROVED: "solution.approved",
  INITIATIVE_CREATED: "initiative.created",
  INITIATIVE_CLIENT_DECISION: "initiative.client_decision",
  DELIVERABLE_MOVED: "deliverable.moved",
  DELIVERABLE_CLIENT_DECISION: "deliverable.client_decision",
  REVIEW_ROUND_OPENED: "review.round_opened",
  LIVE_CYCLE_ROLLED: "live.cycle_rolled",
  CONVERSATION_DONE: "conversation.done",
  QUESTION_FRAMED: "cycle.question_framed",
  REVIEW_DECIDED: "review.decided",
  MEASUREMENT_REMINDER: "measurement.reminder",
  JUDGMENT_RECORDED: "judgment.recorded",
  DECISION_RECORDED: "decision.recorded",
  EFFECT_DETECTED: "effect.detected",
  LEARNING_CREATED: "learning.created"
};
var WALL_EVENTS = [
  "new_signal",
  "new_field_observation",
  "new_hypothesis",
  "new_contradiction",
  "experiment_result",
  "interesting_connection",
  "weekly_learning",
  "radar_alert",
  "question_opened"
];
var WALL_MODES = ["ambient", "focus", "project", "serendipity"];
var WALL_MODE_LABELS = { ambient: "ambient", focus: "foco", project: "projeto", serendipity: "serendipidade" };
var WALL_EVENT_LABELS = {
  new_signal: "novo sinal",
  new_field_observation: "do campo",
  new_hypothesis: "nova hip\xF3tese",
  new_contradiction: "contradi\xE7\xE3o",
  experiment_result: "resultado de experimento",
  interesting_connection: "conex\xE3o",
  weekly_learning: "aprendizado",
  radar_alert: "radar",
  question_opened: "pergunta aberta"
};
var WALL_SCREENS = ["left", "right", "single"];
var AUDIT_ACTIONS = [
  "auth.login",
  "auth.bootstrap",
  "user.created",
  "organization.created",
  "organization.updated",
  "membership.created",
  "membership.updated",
  "membership.removed",
  "invitation.created",
  "invitation.accepted",
  "invitation.revoked",
  "auth.password_reset_requested",
  "auth.password_reset",
  "user.updated",
  "channel.created",
  "channel.updated",
  "channel.deleted",
  "brand.created",
  "brand.updated",
  "brand.deleted",
  "future.updated",
  "future.read",
  "sector.updated",
  "sector.read",
  "rastro.swept",
  "rastro.client_sectors",
  "rastro_theme.updated",
  "rastro_theme.merged",
  "rastro_theme.discarded",
  "rastro_theme.restored",
  "rastro_theme.hypothesis",
  "rastro.consumer_read",
  "rastro_signal.hypothesis",
  "rastro_case.created",
  "asset.created",
  "asset.updated",
  "asset.deleted",
  "ai_limit.updated",
  "product.created",
  "product.updated",
  "product.deleted",
  "product_line.created",
  "product_line.updated",
  "product_line.deleted",
  "network_person.created",
  "network_person.updated",
  "network_person.deleted",
  "client_creator.created",
  "client_creator.updated",
  "client_creator.deleted",
  "campaign.created",
  "campaign.updated",
  "campaign.deleted",
  "ad.paused",
  "ad.resumed",
  "ad.budget_changed",
  "ad.created",
  "ad.negatives_added",
  "network_campaign.created",
  "network_campaign.updated",
  "network_campaign.deleted",
  "lab_room.created",
  "lab_room.updated",
  "lab_room.deleted",
  "lab_round.created",
  "lab_round.updated",
  "asset_creator.created",
  "asset_creator.updated",
  "asset_creator.deleted",
  "asset_person.contract_sent",
  "asset_person.contract_signed",
  "asset_person.created",
  "asset_person.updated",
  "asset_person.deleted",
  "bis.drafted",
  "bis.published",
  "bis_item.created",
  "bis_mold.created",
  "bis_reference.created",
  "bis.construction_opened",
  "bis.construction_reread",
  "bis_reference.applied",
  "bis_reference.dismissed",
  "bis_reference.deleted",
  "bis_mold.finished",
  "bis_mold.deleted",
  "modo_material.created",
  "modo_material.decided",
  "bis_item.updated",
  "bis_item.deleted",
  "competitor.created",
  "competitor.updated",
  "competitor.deleted",
  "mystery_shop.created",
  "mystery_shop.updated",
  "mystery_shop.deleted",
  "point.created",
  "point.imported",
  "point.updated",
  "point.deleted",
  "presence.set",
  "presence.deleted",
  "place.scouted",
  "dispute.set",
  "point_group.created",
  "point_group.deleted",
  "data.imported",
  "data_source.deleted",
  "data_source.updated",
  "data_source.connected",
  "data_source.token_issued",
  "data_source.token_revoked",
  "data_source.pulled",
  "data_source.voice_read",
  "initiative.measured",
  "positioning.updated",
  "visual.requested",
  "visual.reviewed",
  "project.created",
  "project.updated",
  "cycle.created",
  "cycle.updated",
  "file.stored",
  "file.deleted",
  "source.created",
  "source.updated",
  "source.reprocessed",
  "source.deleted",
  "evidence.created",
  "evidence.updated",
  "evidence.validated",
  "evidence.deleted",
  "question.created",
  "question.updated",
  "question.deleted",
  "observation.created",
  "observation.updated",
  "observation.deleted",
  "signal.created",
  "signal.updated",
  "signal.deleted",
  "hypothesis.created",
  "hypothesis.updated",
  "hypothesis.deleted",
  "relation.created",
  "relation.deleted",
  "wall.item_added",
  "wall.item_moved",
  "wall.item_removed",
  "ai.make_visible",
  "ai.challenge",
  "ai.ask",
  "ai.connect",
  "ai.session",
  "suggestion.accepted",
  "suggestion.rejected",
  "suggestion.promoted",
  "ai.feedback",
  "initiative.created",
  "initiative.updated",
  "initiative.client_decision",
  "creation.room_created",
  "creation.room_deleted",
  "creation.restored",
  "creation.shared",
  "creation.decided",
  "creation.image_sent",
  "demand.created",
  "demand.updated",
  "demand.deleted",
  "demand.note_added",
  "demand.pending_added",
  "demand.pending_answered",
  "demand.pending_dropped",
  "demand.route_confirmed",
  "demand.step_done",
  "demand.step_returned",
  "demand.step_skipped",
  "demand.approval_sent",
  "demand.approval_decided",
  "demand.step_reopened",
  "team_function.created",
  "team_function.updated",
  "board_view.created",
  "board_view.updated",
  "board_view.deleted",
  "board_week.updated",
  "deliverable.created",
  "deliverable.updated",
  "deliverable.moved",
  "deliverable.deleted",
  "deliverable.version_added",
  "deliverable.client_decision",
  "cost.created",
  "cost.updated",
  "cost.deleted",
  "review.round_opened",
  "review.decided",
  "review.comment_created",
  "review.comment_updated",
  "review.comment_deleted",
  "cycle.rolled",
  "cycle.question_versioned",
  "conversation.created",
  "conversation.discarded",
  "conversation.restored",
  "conversation_profile.generated",
  "conversation_profile.read",
  "conversation.done",
  "ai.frame",
  "ai.fill",
  "ai.review",
  "cycle.scope_updated",
  "cycle.team_updated",
  "project.contract_updated",
  "notification.read",
  "notification.forwarded",
  "initiative.deleted",
  "session.created",
  "session.updated",
  "session.deleted",
  "solution.created",
  "solution.updated",
  "solution.approved",
  "claim.created",
  "claim.updated",
  "claim.deleted",
  "judgment.recorded",
  "decision.created",
  "decision.updated",
  "decision.deleted",
  "effect.created",
  "effect.updated",
  "effect.deleted",
  "learning.created",
  "learning.updated",
  "learning.deleted",
  "cycle.synthesized",
  "synthesis.updated",
  "publication.created",
  "publication.updated",
  "publication.deleted",
  "comment.created",
  "comment.deleted",
  "reaction.toggled",
  "wall.session_created",
  "wall.session_updated",
  "serendipity.scanned",
  "serendipity.feedback",
  "personal_token.created",
  "personal_token.revoked",
  "oauth.client_registered",
  "oauth.authorized",
  "oauth.revoked",
  "mcp.called",
  "bis_item.proposed",
  "drive.file_uploaded",
  "bis_study.requested",
  "material.rendered",
  "material_recipe.created",
  "material_recipe.updated",
  "demand.drive_upload",
  "demand.item_updated",
  "demand.revision_added",
  "demand.revision_item"
];
function slugify(input) {
  return input.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);
}
var EMBEDDING_DIMENSIONS = 1536;
var JOB_STATUSES = ["queued", "running", "done", "failed"];
var JOB_TYPES = {
  SOURCE_PROCESS: "source.process",
  /** Renderiza uma versão de peça para revisão (PDF → páginas, vídeo → pôster). */
  DELIVERABLE_RENDER: "deliverable.render",
  /** Refaz o perfil de um concorrente (site + fontes associadas): linguagem, abordagem, valor de marca, raridade. */
  COMPETITOR_PROFILE: "competitor.profile",
  /** Pesquisa na web quem disputa no entorno de um ponto (ou numa praça) e deixa as unidades achadas como sugestões. */
  PLACE_SCOUT: "place.scout",
  /** Lê um arquivo de endereços (planilha ou PDF) e organiza as unidades para a pessoa conferir antes de virarem pontos. */
  POINTS_IMPORT: "points.import",
  /** Gera (ou regera) uma visualização editorial: brief → prompt → API de imagem → arquivo. */
  VISUAL_GENERATE: "visual.generate",
  /** Lê o território visual de uma marca (cliente ou concorrente): paleta, fontes, tipo de design e assets, do site e dos conteúdos. */
  TERRITORY_EXTRACT: "territory.extract",
  /** Lê uma visita de cliente oculto: argumentos do vendedor e preços, a partir das propostas, fotos, prints e áudios. */
  MYSTERY_SHOP_ANALYZE: "mystery_shop.analyze",
  /** Compara as visitas de cliente oculto de um concorrente no tempo: o que mudou em preço, discurso, funcionalidade e oferta. */
  COMPETITOR_EVOLUTION: "competitor.evolution",
  /** Lê a posição do cliente e dos concorrentes nas dimensões de posicionamento (as que não são preço). */
  POSITIONING_READ: "positioning.read",
  /** Lê os posts recentes de um concorrente no Instagram (via Apify): tom, temas, campanhas, repercussão. Avisa o que é novo. */
  COMPETITOR_SOCIAL: "competitor.social",
  /** Confere o site e as landing pages de um concorrente: print, comparação com a captura anterior, e o que mudou vira fonte e sinal. */
  COMPETITOR_PAGES: "competitor.pages",
  /** Lê as avaliações recentes das unidades de um concorrente no Google Maps: o que elogiam, do que reclamam, para onde vai. */
  COMPETITOR_REVIEWS: "competitor.reviews",
  /** Lê os anúncios ativos de um concorrente na Biblioteca de Anúncios da Meta: o que está empurrando, com que oferta e argumento, e o que entrou e saiu. */
  COMPETITOR_ADS: "competitor.ads",
  /** Mapeia o site de um concorrente (mapa do site + links internos, só texto): o que há nele, que páginas vale vigiar, e o que entrou de novo. */
  COMPETITOR_SITEMAP: "competitor.sitemap",
  /** Lê o território de mensagem de um cliente: que argumentos cada marca sustenta, o que está lotado, o que tem dono, o que está vazio, e a distância entre o que cada uma promete e o que os clientes dela dizem. */
  MESSAGE_TERRITORY: "message_territory.read",
  /** Rascunha o BIS de um cliente (ou de uma marca): propõe itens de identidade, públicos, voz, visual, comportamento, criação e regras a partir do que a machine já sabe. */
  BIS_DRAFT: "bis.draft",
  /** Lê um documento de referência subido no BIS (qualquer formato) e compara com a cópia de trabalho: o que diverge, complementa, é novo ou confirma. */
  BIS_REFERENCE_READ: "bis.reference_read",
  /** Responde na conversa de um documento de referência do BIS e refina os achados. */
  BIS_REFERENCE_TURN: "bis.reference_turn",
  /** Lê a visão de futuro da marca: o que a diretoria disse (entrevistas e anotações), onde converge, onde diverge e a distância para o que a marca publica hoje. */
  FUTURE_READ: "future.read",
  SECTOR_READ: "sector.read",
  ASSET_READ: "asset.read",
  ASSET_FACES: "asset.faces",
  /** Faz as duas versões de uma foto com área protegida (a da peça, com o tratamento de cada área, e a da IA, com as áreas tapadas) e relê a foto pela versão da IA. */
  ASSET_PROTECT: "asset.protect",
  ASSET_EDIT: "asset.edit",
  ASSET_COMPOSE: "asset.compose",
  MODO_COMPOSE: "modo.compose",
  /** Lê um anexo da conversa do MODO (imagem, planilha, PDF) com os leitores das fontes. */
  MODO_ATTACHMENT_READ: "modo.attachment_read",
  /** Gera uma proposta de imagem numa rodada do LAB (o pedido da pessoa, o contexto da sala e as referências escolhidas). */
  /** Procura os produtos do catálogo numa imagem da galeria que já foi lida (cadastro de produto novo, ou galeria anterior ao catálogo). */
  ASSET_PRODUCTS: "asset.products",
  /** Procura os produtos do catálogo nos posts da própria marca e nas versões de peça que ainda não foram olhados. */
  PRODUCT_SCAN: "product.scan",
  /** Confere uma imagem gerada (cena montada, versão por IA) contra o BIS publicado do cliente, antes de quem gerou aprovar. */
  ASSET_AUDIT: "asset.audit",
  /** Avalia a aderência de uma pessoa do Network a um cliente: Fit Score com explicação e leitura de risco. */
  CREATOR_FIT: "creator.fit",
  /** Monta a proposta de squad de uma campanha: quem, em que papel e por quanto, dentro do orçamento. */
  CAMPAIGN_SQUAD: "campaign.squad",
  /** Lê o perfil público de uma pessoa do Network (Instagram, TikTok): números conferidos e o que ela faz, pelas legendas. */
  NETWORK_PROFILE: "network.profile",
  /** Monta o panorama de uma demanda do Ritmo: o que a Machine sabe da marca posto ao lado do pedido, antes de ir para a fila. */
  DEMAND_PANORAMA: "demand.panorama",
  /** Propõe o percurso de uma demanda dentro da Raro: que funções entram, em que ordem, e o briefing de cada uma. */
  DEMAND_ROUTE: "demand.route",
  /** Lê um arquivo anexado a uma demanda (áudio transcrito, imagem descrita, vídeo, documento) para ele entrar no contexto. */
  DEMAND_FILE_READ: "demand.file_read",
  /** Revisa a entrega de uma etapa: português e regras do BIS publicado. É a revisão, que deixou de ser etapa de gente. */
  DEMAND_STEP_REVIEW: "demand.step_review",
  /** Lê o pedido de ajuste do cliente contra a marca e o combinado, e diz que etapa reabrir. */
  DEMAND_CHANGE_REVIEW: "demand.change_review",
  DEMAND_REVISION_READ: "demand.revision_read",
  /** Propõe os itens de uma demanda que se desdobra em várias peças (um cronograma de posts): data, tema e formato de cada um. */
  DEMAND_ITEMS: "demand.items",
  /** Busca o arquivo (ou os arquivos da pasta) de um link do Google Drive colado numa demanda, para virar anexo lido. */
  DEMAND_LINK_FETCH: "demand.link_fetch",
  CLIENT_DRIVE_FOLDERS: "client.drive_folders",
  SOCIAL_TAGS: "social.tags",
  SOCIAL_INBOX: "social.inbox",
  SOCIAL_COMMENTS: "social.comments",
  ADS_ANALYZE: "ads.analyze",
  ADS_ACCOUNT_READ: "ads.account_read",
  CAMPAIGN_ROOM_TURN: "campaign.room_turn",
  CAMPAIGN_ROOM_FILE: "campaign.room_file",
  ADS_GOOGLE_DRAFT: "ads.google_draft",
  /** A Machine lê os termos de busca do Google que gastam sem converter e sugere negativas (conferidas pela regra). */
  ADS_TERMS_REVIEW: "ads.terms_review",
  RD_VOICE: "rd.voice",
  BOARD_WEEK: "board.week",
  DEMAND_ITEM_PUBLISH: "demand.item_publish",
  DEMAND_ROOM_TURN: "demand.room_turn",
  /** A Machine na sala de criação: lê o pedido da conversa e escreve ou reescreve as pranchas livres. */
  CREATION_TURN: "creation.turn",
  /** Uma imagem de estudo para uma prancha da sala de criação. */
  CREATION_IMAGE: "creation.image",
  DEMAND_ROOM_DOC: "demand.room_doc",
  DEMAND_ROOM_REVIEW: "demand.room_review",
  DEMAND_DRIVE_FOLDER: "demand.drive_folder",
  DEMAND_DRIVE_SYNC: "demand.drive_sync",
  DEMAND_DRIVE_APPROVED: "demand.drive_approved",
  /** Revisa o texto e a arte de UM item da demanda: português e regras da marca. */
  DEMAND_ITEM_REVIEW: "demand.item_review",
  /** Pesquisa na web sobre uma pessoa do Network a partir do nome: quem é, onde atua, os perfis dela; completa o cartão. */
  NETWORK_RESEARCH: "network.research",
  LAB_GENERATE: "lab.generate",
  /** Pesquisa na web pedida por alguém dentro de uma sala do LAB. */
  LAB_RESEARCH: "lab.research",
  /** Exploração em texto pedida na sala: lê o que a machine sabe do cliente e devolve achados, caminhos e o que falta. */
  LAB_EXPLORE: "lab.explore",
  /** Resposta da Machine na conversa da rodada: curta, com o que ela sabe do cliente, e pergunta de volta quando falta. */
  LAB_TALK: "lab.talk",
  /** Prateleira pedida na sala: acha as páginas de produto de cada marca, pega a imagem da embalagem e lê o que cada uma mostra. */
  LAB_SHELF: "lab.shelf",
  /** Rastro: lê a lista de notícias dos veículos de um setor, classifica o que é novo e refaz temas, famílias e leituras. */
  RASTRO_SWEEP: "rastro.sweep",
  /** Rastro: investiga um tema na web (o produto de cada marca, números publicados, quem já fazia) e guarda as fotos. */
  RASTRO_CASE: "rastro.case",
  /** Rastro: lê o consumidor de um setor (busca no Google, vídeos e comentários do TikTok) e cruza com os temas de marca. */
  RASTRO_CONSUMER: "rastro.consumer"
};
var AI_FEATURES = [
  "embed",
  "transcribe",
  "search",
  "ask",
  "classify",
  "summarize",
  "extract",
  "connect",
  "challenge",
  "make_visible",
  "synthesize",
  "serendipity",
  "find",
  "solution",
  "session_agenda",
  "session_summary",
  "frame",
  "interview",
  "fill",
  "review",
  "transcript_review",
  "describe_media",
  "competitor_profile",
  "visual_brief",
  "visual_generate",
  "visual_territory",
  "mystery_shop",
  "competitor_evolution",
  "positioning",
  "scout",
  "data_mapping",
  "channel_use",
  "decide",
  "bis",
  "future",
  "sector",
  "asset",
  "asset_edit",
  "lab",
  "creators",
  "ritmo",
  "modo",
  "rastro"
];
var AI_USER_MONTHLY_USD = { owner: 150, partner: 150, analyst: 60, maker: 30, network: 10, client_admin: 15, client: 5 };
var AI_PENDING_MAX = 4;
var AI_IMAGE_ESTIMATE_USD = 0.07;
var aiUsageState = (spentUsd, limitUsd) => spentUsd >= limitUsd ? "over" : spentUsd >= limitUsd * 0.8 ? "near" : "ok";
function aiMonthStart(now = /* @__PURE__ */ new Date()) {
  const br = new Date(now.getTime() - 3 * 36e5);
  return new Date(Date.UTC(br.getUTCFullYear(), br.getUTCMonth(), 1, 3, 0, 0));
}
var AI_RUN_STATUSES = ["ok", "error", "skipped"];
var SEARCH_MATCH_KINDS = ["fulltext", "semantic", "both"];
function inferSourceType(input) {
  if (input.url) return "url";
  const mime = (input.mimeType ?? "").toLowerCase();
  const ext = (input.fileName ?? "").toLowerCase().split(".").pop() ?? "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (["xlsx", "xls", "csv", "tsv", "numbers"].includes(ext) || mime.includes("spreadsheet") || mime === "text/csv") return "spreadsheet";
  if (["pdf", "docx", "doc", "pptx", "ppt", "txt", "md", "rtf"].includes(ext) || mime.includes("pdf") || mime.includes("word") || mime.includes("presentation") || mime.startsWith("text/")) return "document";
  return "other";
}
var CONTEXT_LEVELS = ["fragile", "partial", "good", "solid"];
var CONTEXT_LEVEL_LABELS = { fragile: "fr\xE1gil", partial: "parcial", good: "boa", solid: "s\xF3lida" };
function contextBase(input) {
  const len = (s) => (s ?? "").trim().length;
  const o = input.organization;
  const m = input.material;
  const client = (len(o.description) >= 200 ? 10 : len(o.description) >= 60 ? 5 : 0) + (len(o.segment) ? 3 : 0) + (len(o.city) ? 2 : 0) + ((o.contacts ?? 0) > 0 ? 3 : 0) + (len(o.website) ? 2 : 0);
  const heard = m.briefingConversations > 0 || m.kickoffSessions > 0;
  const briefing = (len(input.project.brief) >= 200 ? 12 : len(input.project.brief) >= 60 ? 6 : 0) + (heard ? 8 : 0);
  const objectiveDefined = len(input.cycle?.objective) >= 20;
  const questionDefined = len(input.cycle?.question) >= 20;
  const objective = (objectiveDefined ? 8 : 0) + (questionDefined ? 12 : 0);
  const material = Math.min(14, m.sourcesProcessed * 2) + Math.min(8, m.sourceTypes * 2) + Math.min(6, m.sessionsDone * 3) + Math.min(6, m.conversationsDone * 3) + Math.min(6, Math.floor(m.evidenceAccepted / 2));
  const score = client + briefing + objective + material;
  const briefingDefined = briefing >= 12;
  const gaps = [];
  if (!briefingDefined) gaps.push({ key: "briefing", text: heard ? "o briefing do cliente ainda n\xE3o est\xE1 escrito no projeto" : len(input.project.brief) >= 60 ? "o briefing do cliente est\xE1 raso: ningu\xE9m do cliente foi ouvido em briefing ou kickoff" : "o briefing do cliente n\xE3o est\xE1 definido: ningu\xE9m do cliente foi ouvido e o projeto n\xE3o diz o que chegou", action: "gerar um briefing por voz ou registrar o que chegou" });
  if (!objectiveDefined) gaps.push({ key: "cycle_objective", text: "o ciclo n\xE3o tem objetivo", action: "definir o objetivo no escopo do ciclo" });
  if (!questionDefined) gaps.push({ key: "cycle_question", text: "o ciclo ainda n\xE3o tem pergunta: a Machine l\xEA o material sem saber o que procura", action: "rodar ENQUADRAR quando houver material" });
  if (client < 12) gaps.push({ key: "client_profile", text: "o cadastro do cliente est\xE1 raso (descri\xE7\xE3o, segmento, cidade, contatos)", action: "completar o cadastro, pode ser falando" });
  if (m.sourcesProcessed < 5) gaps.push({ key: "material", text: `pouco material: ${m.sourcesProcessed} fonte(s) lida(s)`, action: "subir fontes: entrevistas, fotos, v\xEDdeos, planilhas, conversas" });
  else if (m.sourceTypes < 3) gaps.push({ key: "variety", text: `material de ${m.sourceTypes} tipo(s) s\xF3`, action: "variar: campo, n\xFAmeros, falas" });
  if (m.sessionsDone + m.conversationsDone === 0) gaps.push({ key: "voices", text: "nenhuma voz ouvida ainda (sess\xE3o ou conversa)", action: "marcar uma sess\xE3o ou mandar um link de conversa" });
  const level = score >= 85 ? "solid" : score >= 65 ? "good" : score >= 35 ? "partial" : "fragile";
  return { score, level, briefingDefined, objectiveDefined, questionDefined, gaps, parts: [{ key: "client", label: "cliente", score: client, max: 20 }, { key: "briefing", label: "briefing", score: briefing, max: 20 }, { key: "objective", label: "objetivo do ciclo", score: objective, max: 20 }, { key: "material", label: "material", score: material, max: 40 }] };
}
function contextBaseLine(c) {
  return `base de contexto ${c.score}/100 (${CONTEXT_LEVEL_LABELS[c.level]})${c.gaps.length ? `: ${c.gaps.map((g) => g.text).join("; ")}` : ""}`;
}
var COMPETITOR_KINDS = ["direct", "indirect", "reference"];
var COMPETITOR_KIND_LABELS = { direct: "concorrente direto", indirect: "concorrente indireto", reference: "refer\xEAncia", self: "a pr\xF3pria marca" };
var ownBrandSubject = (name) => `${name} \xB7 ATEN\xC7\xC3O: esta \xE9 a PR\xD3PRIA MARCA do cliente da Raro, n\xE3o um concorrente. Leia com a mesma r\xE9gua, descrevendo como ela se comunica hoje, sem trat\xE1-la como rival nem comparar com ela mesma. Onde o texto disser "concorrente", entenda "a marca"`;
var SOCIAL_PROOF_KINDS = ["clients", "numbers", "awards", "press", "ratings", "partners"];
var SOCIAL_PROOF_LABELS = { clients: "clientes", numbers: "n\xFAmeros", awards: "pr\xEAmios", press: "imprensa", ratings: "avalia\xE7\xF5es", partners: "parceiros" };
var REACH_LABELS = { local: "local", regional: "regional", national: "nacional", international: "internacional" };
function dossierLines(r) {
  if (!r) return [];
  const d = r.dossier;
  const list2 = (label, items) => items.length ? [`${label}: ${items.join(" | ")}`] : [];
  return [
    ...d.promise ? [`promessa (palavras dele): "${d.promise.text}"`] : [],
    ...list2("diferenciais que anuncia", d.differentials.map((x) => x.text)),
    ...list2("produtos", d.products.map((x) => `${x.name} (${x.what}${x.price ? `, ${x.price}` : ""})`)),
    ...list2("para quem vende", d.audience.map((x) => x.text)),
    ...d.reach.scope || d.reach.places.length ? [`onde atende: ${[d.reach.scope ? REACH_LABELS[d.reach.scope] : null, d.reach.places.slice(0, 12).join(", ") || null, d.reach.note].filter(Boolean).join(" \xB7 ")}`] : [],
    ...list2("pre\xE7o publicado", d.pricing.map((x) => `${x.item}: ${x.price}`)),
    ...list2("provas sociais", d.socialProof.map((x) => `${SOCIAL_PROOF_LABELS[x.kind]}: ${x.text}`)),
    ...list2("depoimentos de clientes dele", d.testimonials.map((x) => `"${x.quote}"${x.who ? ` (${x.who})` : ""}`)),
    ...list2("reclama\xE7\xF5es", d.complaints.map((x) => x.text)),
    ...list2("como vende", d.channels.map((x) => x.text)),
    ...list2("momento", d.moment.map((x) => `${x.text}${x.date ? ` (${x.date})` : ""}`)),
    ...[d.company.founded && `fundada em ${d.company.founded}`, d.company.size, d.company.headquarters && `sede em ${d.company.headquarters}`].filter(Boolean).length ? [`empresa: ${[d.company.founded && `fundada em ${d.company.founded}`, d.company.size, d.company.headquarters && `sede em ${d.company.headquarters}`].filter(Boolean).join(" \xB7 ")}`] : []
  ];
}
var SITE_MAP_MAX_PAGES = 50;
var WATCHED_PAGES_MAX = 12;
var PAGE_IMPORTANCE = ["baseline", "none", "low", "high"];
var SOCIAL_NETWORKS = ["instagram", "facebook", "tiktok", "youtube", "linkedin"];
var SOCIAL_NETWORK_LABELS = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube", linkedin: "LinkedIn" };
var WATCH_LEVELS = ["close", "base", "off"];
var WATCH_LEVEL_LABELS = { close: "de perto", base: "base", off: "pausado" };
var WATCH_CHANNELS = ["site", "sitemap", "ads", "instagram", "facebook", "tiktok", "youtube", "linkedin", "reviews", "web"];
var WATCH_CHANNEL_LABELS = { site: "Site e landing pages", sitemap: "Mapa do site", ads: "An\xFAncios (Meta, Google, LinkedIn)", instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube", linkedin: "LinkedIn", reviews: "Avalia\xE7\xF5es do Google Maps", web: "Web (dossi\xEA)" };
var POSTS_PER_READ_OPTIONS = [6, 12, 24];
var REVIEWS_PER_READ_OPTIONS = [20, 50, 100];
var watchDaysOf = (w, channel) => w[`${channel}Days`];
var WATCH_PRESETS = { close: { siteDays: 1, sitemapDays: 30, adsDays: 7, instagramDays: 7, facebookDays: 7, tiktokDays: 7, youtubeDays: 14, linkedinDays: 7, reviewsDays: 7, webDays: 30, postsPerRead: 12, reviewsPerRead: 50 }, base: { siteDays: 14, sitemapDays: 90, adsDays: 30, instagramDays: 60, facebookDays: 60, tiktokDays: 60, youtubeDays: 60, linkedinDays: 60, reviewsDays: 30, webDays: 90, postsPerRead: 12, reviewsPerRead: 50 }, off: { siteDays: 0, sitemapDays: 0, adsDays: 0, instagramDays: 0, facebookDays: 0, tiktokDays: 0, youtubeDays: 0, linkedinDays: 0, reviewsDays: 0, webDays: 0, postsPerRead: 12, reviewsPerRead: 50 } };
var WATCH_FREQUENCIES = [{ days: 0, label: "n\xE3o ler" }, { days: 1, label: "todo dia" }, { days: 7, label: "toda semana" }, { days: 14, label: "a cada 2 semanas" }, { days: 30, label: "todo m\xEAs" }, { days: 60, label: "a cada 2 meses" }, { days: 90, label: "a cada 3 meses" }];
var WATCH_COST_USD = { site: 0.12, sitemap: 0.3, ads: 0.35, instagram: 0.25, facebook: 0.2, tiktok: 0.25, youtube: 0.2, linkedin: 0.2, reviews: 0.1, web: 0.65 };
var SITE_CHANGE_RATE = 0.15;
function watchOf(w) {
  const level = w?.level && WATCH_LEVELS.includes(w.level) ? w.level : "base";
  const days = (v, fallback) => typeof v === "number" && WATCH_FREQUENCIES.some((f) => f.days === v) ? v : fallback;
  const pick = (v, options, fallback) => options.includes(v) ? v : fallback;
  const p = WATCH_PRESETS[level];
  return { level, siteDays: days(w?.siteDays, p.siteDays), sitemapDays: days(w?.sitemapDays, p.sitemapDays), adsDays: days(w?.adsDays, p.adsDays), instagramDays: days(w?.instagramDays, p.instagramDays), facebookDays: days(w?.facebookDays, p.facebookDays), tiktokDays: days(w?.tiktokDays, p.tiktokDays), youtubeDays: days(w?.youtubeDays, p.youtubeDays), linkedinDays: days(w?.linkedinDays, p.linkedinDays), reviewsDays: days(w?.reviewsDays, p.reviewsDays), webDays: days(w?.webDays, p.webDays), postsPerRead: pick(w?.postsPerRead, POSTS_PER_READ_OPTIONS, 12), reviewsPerRead: pick(w?.reviewsPerRead, REVIEWS_PER_READ_OPTIONS, 50) };
}
function watchMonthlyCostUsd(w, has) {
  const per = (days, cost) => days > 0 ? 30 / days * cost : 0;
  let total = 0;
  for (const ch of WATCH_CHANNELS) {
    if (has[ch] === false) continue;
    const scale = ch === "site" ? SITE_CHANGE_RATE * Math.max(1, has.pages ?? 1) : ch === "web" ? 1 : ch === "reviews" ? w.reviewsPerRead / 50 : w.postsPerRead / 12;
    total += per(watchDaysOf(w, ch), WATCH_COST_USD[ch] * scale);
  }
  return Math.round(total * 100) / 100;
}
function socialOf(v) {
  if (!v || typeof v !== "object") return {};
  return "network" in v ? { [v.network]: v } : v;
}
var MULTI_MAX = 3;
var listOf = (v) => (v ?? "").split(/[,;\n]+/).map((t) => t.trim()).filter(Boolean);
var websitesOf = (v) => listOf(v).slice(0, MULTI_MAX);
var mainWebsite = (v) => websitesOf(v)[0] ?? null;
function socialHandles(network, v) {
  return [...new Set(listOf(v).map((one) => oneSocialHandle(network, one)).filter(Boolean))].slice(0, MULTI_MAX);
}
var socialHandle = (network, v) => socialHandles(network, v)[0] ?? "";
function oneSocialHandle(network, v) {
  if (network === "instagram") return instagramHandle(v);
  const t = (v ?? "").trim();
  if (network === "facebook") {
    const id = /facebook\.com\/profile\.php\?id=(\d+)/i.exec(t);
    if (id) return `profile.php?id=${id[1]}`;
    const m2 = /(?:facebook|fb)\.com\/([A-Za-z0-9.\-]+)/i.exec(t);
    const h2 = (m2 ? m2[1] : t.replace(/^@/, "")).replace(/\/+$/, "");
    return /^[A-Za-z0-9.\-]{2,80}$/.test(h2) ? h2 : "";
  }
  if (network === "youtube") {
    const m2 = /youtube\.com\/(@[\w.-]+|channel\/[\w-]+|c\/[\w.-]+|user\/[\w.-]+)/i.exec(t);
    if (m2) return m2[1];
    return /^@?[\w.-]{2,60}$/.test(t) ? `@${t.replace(/^@/, "")}` : "";
  }
  if (network === "linkedin") {
    const m2 = /linkedin\.com\/(?:company|school|showcase)\/([^/?#]+)/i.exec(t);
    const h2 = m2 ? decodeURIComponent(m2[1]) : t.replace(/^@/, "");
    return /^[\w.%-]{2,100}$/.test(h2) ? h2 : "";
  }
  const m = /tiktok\.com\/@([A-Za-z0-9._]+)/.exec(t);
  const h = (m ? m[1] : t.replace(/^@/, "")).replace(/\/+$/, "");
  return /^[A-Za-z0-9._]{2,30}$/.test(h) ? h : "";
}
function socialProfileUrl(network, handle) {
  return network === "facebook" ? `https://www.facebook.com/${handle}` : network === "instagram" ? `https://www.instagram.com/${handle}/` : network === "tiktok" ? `https://www.tiktok.com/@${handle}` : network === "youtube" ? `https://www.youtube.com/${handle}` : `https://www.linkedin.com/company/${handle}/`;
}
function instagramHandle(v) {
  const t = (v ?? "").trim();
  const m = /instagram\.com\/([A-Za-z0-9._]+)/.exec(t);
  const h = (m ? m[1] : t.replace(/^@/, "")).replace(/\/+$/, "");
  return /^[A-Za-z0-9._]{1,30}$/.test(h) ? h : "";
}
var adAdvertiserKey = (platform, pageKey, pageName) => pageKey ?? `${platform}:nome:${adPageKey(pageName)}`;
var adPageKey = (name) => (name ?? "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/\s+/g, " ").trim();
var AD_PLATFORMS = ["meta", "google", "linkedin"];
var AD_PLATFORM_LABELS = { meta: "Meta (Facebook e Instagram)", google: "Google (busca, YouTube e display)", linkedin: "LinkedIn" };
function adsOf(v) {
  if (!v || typeof v !== "object") return {};
  return "readAt" in v ? { meta: v } : v;
}
var ARGUMENT_STRENGTHS = ["core", "present", "weak"];
var ARGUMENT_STRENGTH_LABELS = { core: "\xE9 o centro do que ela diz", present: "aparece", weak: "s\xF3 de passagem" };
var ARGUMENT_STATUSES = ["crowded", "owned", "contested", "open"];
var ARGUMENT_STATUS_LABELS = { crowded: "lotado", owned: "tem dono", contested: "disputado", open: "vazio" };
function argumentStatus(holders, brands) {
  const n = holders.filter((h) => h.strength !== "weak").length;
  if (n === 0) return "open";
  if (n === 1) return "owned";
  return brands >= 3 && n / brands >= 0.6 ? "crowded" : "contested";
}
var PROMISE_GAPS = ["confirms", "contradicts", "unknown"];
var PROMISE_GAP_LABELS = { confirms: "os clientes confirmam", contradicts: "os clientes contradizem", unknown: "sem base para dizer" };
function rarityIndexOf(scores) {
  const vals = RARITY_DIMENSIONS.map((d) => scores?.[d] == null ? null : d === "uncertainty" ? 100 - scores[d] : scores[d]).filter((v) => v !== null);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
}
var normName = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
function detectCompetitors(text, competitors) {
  const hay = ` ${normName(text)} `;
  const out = [];
  for (const c of competitors) {
    const domain = (c.website ?? "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] ?? "";
    const handles = Object.values(c.socials ?? {}).map((h) => String(h ?? "").replace(/^.*\//, "").replace(/^@/, ""));
    const terms = [c.name, ...c.aliases ?? [], domain, domain.split(".")[0] ?? "", ...handles].map((t) => normName(String(t).trim())).filter((t) => t.length >= 3);
    const hit2 = [...new Set(terms)].find((t) => new RegExp(`(^|[^a-z0-9])${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(hay));
    if (hit2) out.push({ competitor: c, matched: hit2 });
  }
  return out;
}
var VISUAL_MODES = ["ENTITY", "SIGNAL", "CATEGORY", "HYPOTHESIS"];
var VISUAL_STATUSES = ["pending", "generated", "approved", "rejected", "archived", "failed"];
var VISUAL_ENTITY_TYPES = ["competitor", "organization", "hypothesis", "signal", "project"];
var VISUAL_FEEDBACK = ["great", "too_generic", "too_advertising", "invented_information", "off_context"];
var VISUAL_FEEDBACK_LABELS = { great: "\xF3timo", too_generic: "muito gen\xE9rico", too_advertising: "muito publicit\xE1rio", invented_information: "inventou informa\xE7\xE3o", off_context: "n\xE3o representa o contexto" };
var VISUAL_REGENERATION = {
  too_generic: "Abandone a met\xE1fora anterior. Procure um detalhe material ou processo espec\xEDfico presente no contexto. A nova imagem deve ter uma composi\xE7\xE3o substancialmente diferente.",
  too_advertising: "A imagem anterior parecia publicidade. Tire qualquer encena\xE7\xE3o, brilho ou composi\xE7\xE3o de an\xFAncio. Prefira registro seco, luz dura, objeto ou processo sem glamour.",
  invented_information: "A imagem anterior inventou algo que parecia fato (lugar, produto, pessoa, embalagem ou marca). Fique mais abstrata e mais material, menos espec\xEDfica. Nada que possa ser lido como registro documental.",
  off_context: "A imagem anterior n\xE3o representava o contexto. Releia o contexto e parta de outro atributo ou territ\xF3rio, com outra met\xE1fora e outra composi\xE7\xE3o."
};
var TERRITORY_ASSET_KINDS = ["logo", "icon", "social", "screenshot", "frame", "image"];
var TERRITORY_ASSET_LABELS = { logo: "logo", icon: "\xEDcone", social: "imagem de compartilhamento", screenshot: "tela do site", frame: "quadro de v\xEDdeo", image: "imagem" };
var MYSTERY_SHOP_CHANNELS = ["whatsapp", "phone", "video_call", "in_person", "email", "site_form", "other"];
var MYSTERY_SHOP_CHANNEL_LABELS = { whatsapp: "WhatsApp", phone: "telefone", video_call: "chamada de v\xEDdeo", in_person: "presencial", email: "e-mail", site_form: "formul\xE1rio do site", other: "outro" };
var PRICE_PERIODS = ["monthly", "yearly", "one_time", "per_unit", "other"];
var PRICE_PERIOD_LABELS = { monthly: "por m\xEAs", yearly: "por ano", one_time: "uma vez", per_unit: "por unidade", other: "outro" };
var EVOLUTION_AXES = ["price", "discourse", "features", "offer"];
var EVOLUTION_AXIS_LABELS = { price: "pre\xE7o", discourse: "discurso", features: "funcionalidade", offer: "oferta e condi\xE7\xF5es" };
function unitsBand(units) {
  if (units == null) return "volume n\xE3o informado";
  if (units <= 1) return "1 unidade";
  if (units <= 5) return "2 a 5 unidades";
  if (units <= 20) return "6 a 20 unidades";
  return "mais de 20 unidades";
}
var POSITIONING_DIMENSION_KINDS = ["price", "score", "measured"];
var POSITIONING_ORIGINS = ["machine", "human", "mystery_shop", "radar"];
var POSITIONING_ORIGIN_LABELS = { machine: "leitura da Machine", human: "ajuste de pessoa", mystery_shop: "cliente oculto", radar: "medido pelo radar" };
var DEFAULT_POSITIONING_DIMENSIONS = [
  { key: "price", kind: "price", label: "pre\xE7o", description: "pre\xE7o por unidade ao m\xEAs da oferta principal, no cen\xE1rio escolhido.", lowLabel: "mais barato", highLabel: "mais caro" },
  { key: "total_cost", kind: "price", label: "custo total para operar", description: "plano de entrada mais as mensalidades de equipamento necess\xE1rias para operar (impressora, tablet, frete), por unidade ao m\xEAs. itens por consumo e compras \xFAnicas ficam de fora.", lowLabel: "custa menos para operar", highLabel: "custa mais para operar" },
  { key: "quality", kind: "score", label: "qualidade percebida", description: "o quanto quem compra percebe o produto e o servi\xE7o como bons, confi\xE1veis e bem acabados: o que entrega, como entrega, prova de resultado, suporte, acabamento da marca.", lowLabel: "percebida como b\xE1sica", highLabel: "percebida como superior" }
];
var MEASURED_POSITIONING_DIMENSIONS = [
  { key: "reviews_score", kind: "measured", label: "nota dos clientes", description: "m\xE9dia das estrelas das avalia\xE7\xF5es mais recentes no Google Maps (as que o radar leu). \xE9 a voz de quem avaliou, n\xE3o de todos os clientes.", lowLabel: "clientes avaliam mal", highLabel: "clientes avaliam bem", unit: "estrelas", decimals: 1 },
  { key: "ad_pressure", kind: "measured", label: "press\xE3o de m\xEDdia", description: "an\xFAncios ativos somados na Meta, no Google e no LinkedIn. conta criativos no ar, n\xE3o verba: investimento n\xE3o \xE9 p\xFAblico.", lowLabel: "anuncia pouco", highLabel: "anuncia muito", unit: "an\xFAncios ativos", decimals: 0 },
  { key: "social_engagement", kind: "measured", label: "resposta nas redes", description: "mediana de curtidas + coment\xE1rios por post no Instagram (ou na rede principal lida).", lowLabel: "pouca resposta", highLabel: "muita resposta", unit: "por post", decimals: 0 },
  { key: "social_rhythm", kind: "measured", label: "ritmo de publica\xE7\xE3o", description: "posts por semana no Instagram (ou na rede principal lida), pelos mais recentes.", lowLabel: "publica pouco", highLabel: "publica muito", unit: "posts por semana", decimals: 1 }
];
var measuredDimension = (key) => MEASURED_POSITIONING_DIMENSIONS.find((d) => d.key === key) ?? null;
function resolvePriceReference(a) {
  const values = (a.plans ?? []).map((p) => p.monthly).filter((v) => v != null && v > 0).sort((x, y) => x - y);
  const monthlyRange = values.length > 1 && values[0] !== values[values.length - 1] ? { min: values[0], max: values[values.length - 1] } : null;
  if (a.unitMonthlyReference != null || a.monthlyReference != null) return { monthlyReference: a.monthlyReference ?? a.unitMonthlyReference ?? null, unitMonthlyReference: a.unitMonthlyReference ?? a.monthlyReference ?? null, monthlyRange, fromPlans: false };
  if (values.length === 0) return { monthlyReference: null, unitMonthlyReference: null, monthlyRange: null, fromPlans: false };
  return { monthlyReference: values[0], unitMonthlyReference: values[0], monthlyRange, fromPlans: true };
}
function resolveTotalCost(a) {
  const ref = a.unitMonthlyReference ?? a.monthlyReference;
  if (a.monthlyWithEquipment != null) return { value: a.monthlyWithEquipment, why: "plano de entrada mais equipamento cobrado \xE0 parte por m\xEAs" };
  if (a.equipment === "included" && ref != null) return { value: ref, why: "o plano de entrada j\xE1 inclui o equipamento" };
  if (a.equipment === "outside") return { value: null, why: "o equipamento \xE9 comprado por fora, sem valor na proposta" };
  return { value: null, why: "a proposta n\xE3o diz quanto custa o equipamento para operar" };
}
var IMAGE_TIERS = ["definitive", "fast"];
var VISUAL_PURPOSES = ["final", "exploration"];
function imageTierFor(input) {
  if (input.purpose === "exploration") return "fast";
  return input.mode === "ENTITY" || input.mode === "HYPOTHESIS" ? "definitive" : "fast";
}
var PLACE_KINDS = ["state", "region", "city", "district"];
var PLACE_KIND_LABELS = { state: "estado", region: "regi\xE3o", city: "cidade", district: "bairro" };
var BR_STATES = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
var POINT_OWNERS = ["client", "competitor", "third_party"];
var POINT_OWNER_LABELS = { client: "unidade do cliente", competitor: "unidade de concorrente", third_party: "ponto de terceiro" };
var POINT_CHANNELS = ["supermarket", "cash_and_carry", "mall", "gallery", "street_store", "bakery", "liquor_store", "bar", "restaurant", "convenience", "wholesale", "online", "other"];
var POINT_CHANNEL_LABELS = {
  supermarket: "supermercado",
  cash_and_carry: "atacarejo",
  mall: "shopping",
  gallery: "galeria",
  street_store: "loja de rua",
  bakery: "padaria",
  liquor_store: "adega",
  bar: "bar",
  restaurant: "restaurante",
  convenience: "conveni\xEAncia",
  wholesale: "atacado",
  online: "online",
  other: "outro"
};
var PRESENCE_SITUATIONS = ["strong", "present", "weak", "target", "absent"];
var PRESENCE_SITUATION_LABELS = { strong: "forte", present: "presente", weak: "fraco", target: "alvo", absent: "ausente" };
var PRESENCE_SITUATION_HINTS = {
  strong: "lidera ou incomoda quem lidera",
  present: "est\xE1 l\xE1, sem destaque",
  weak: "est\xE1 l\xE1 e perde",
  target: "ainda n\xE3o est\xE1: a diretoria quer entrar",
  absent: "n\xE3o est\xE1 e n\xE3o \xE9 prioridade"
};
var STRENGTH_SOURCES = ["perceived", "measured"];
var STRENGTH_SOURCE_LABELS = { perceived: "percebida", measured: "medida" };
var PRESENCE_STAKES = ["consumer", "tenant", "traffic", "shelf", "distribution"];
var PRESENCE_STAKE_LABELS = { consumer: "consumidor", tenant: "lojista", traffic: "fluxo de gente", shelf: "espa\xE7o de g\xF4ndola", distribution: "distribui\xE7\xE3o" };
function placeSlug(parts) {
  return parts.filter((x) => Boolean(x && x.trim())).map((x) => slugify(x)).join("/");
}
var DISPUTE_PRIORITIES = ["high", "normal", "low"];
var DISPUTE_PRIORITY_LABELS = { high: "priorit\xE1ria", normal: "normal", low: "baixa" };
var METRIC_UNITS = ["brl", "units", "liters", "kg", "percent", "count", "other"];
var METRIC_UNIT_LABELS = { brl: "R$", units: "unidades", liters: "litros", kg: "kg", percent: "%", count: "contagem", other: "outro" };
var DATA_COLUMN_ROLES = ["ignore", "period", "city", "uf", "point", "channel", "product", "metric", "period_value"];
var DATA_COLUMN_ROLE_LABELS = {
  ignore: "ignorar",
  period: "per\xEDodo (m\xEAs)",
  city: "cidade",
  uf: "UF",
  point: "ponto ou unidade",
  channel: "canal",
  product: "produto",
  metric: "valor (m\xE9trica)",
  period_value: "valor de um m\xEAs (coluna = m\xEAs)"
};
function metricKey(label) {
  return slugify(label).replace(/-/g, "_").slice(0, 48) || "valor";
}
function measuredLevel(rank, of) {
  if (of < 3) return null;
  const third = Math.ceil(of / 3);
  return rank <= third ? "strong" : rank > of - third ? "weak" : "present";
}
var DEVIATION_THRESHOLD_PCT = 15;
var DEVIATION_FLOOR = { count: 100, units: 100, liters: 100, kg: 100, brl: 1e3, percent: 0, other: 0 };
var RD_VOICE_KINDS = ["question", "objection", "blocker", "trigger"];
var RD_VOICE_KIND_LABELS = { question: "pergunta", objection: "obje\xE7\xE3o", blocker: "onde trava", trigger: "o que faz fechar" };
var RD_VOICE_DAYS = 7;
var RD_VOICE_WINDOW_DAYS = 60;
var RD_VOICE_MAX_TALKS = 100;
var RD_VOICE_ESTIMATE_USD = 0.4;
var DATA_ADDRESS_ROLES = ["site", "campaign", "store", "product", "other"];
var DATA_ADDRESS_ROLE_LABELS = { site: "site", campaign: "campanha", store: "loja", product: "produto", other: "outro" };
var INGEST_TOKEN_PREFIX = "mri_";
function personalColumn(name) {
  return /\b(cpf|rg|e ?mail|telefone|celular|whatsapp|fone|nascimento|paciente|beneficiario|titular|nome (do |da )?(cliente|paciente|pessoa|beneficiario|titular|usuario))\b/.test(name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim());
}
function personalValue(value) {
  return /\b\d{3}\.?\d{3}\.?\d{3}-\d{2}\b/.test(value) || /[^\s@]+@[^\s@]+\.[a-z]{2,}/i.test(value) || /\(?\b\d{2}\)?\s?9\d{4}-?\d{4}\b/.test(value);
}
function isClosedMonth(period, now = /* @__PURE__ */ new Date()) {
  return period < `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-01`;
}
var CONVERSATION_DEPTHS = ["quick", "standard", "deep"];
var CONVERSATION_DEPTH = {
  quick: { label: "r\xE1pida", hint: "5 a 7 perguntas, uns 5 minutos. s\xF3 o essencial, sem aprofundar.", questions: [5, 7], maxTurns: 8, minutes: 5, followUps: 0 },
  standard: { label: "padr\xE3o", hint: "10 a 14 perguntas, uns 12 minutos. cobre o roteiro com um aprofundamento por assunto.", questions: [10, 14], maxTurns: 14, minutes: 12, followUps: 1 },
  deep: { label: "profunda", hint: "18 a 24 perguntas, uns 25 minutos. vai atr\xE1s de exemplo e de motivo em cada assunto.", questions: [18, 24], maxTurns: 26, minutes: 25, followUps: 2 }
};
function conversationDepthOf(maxTurns) {
  const n = maxTurns ?? 14;
  return n <= 9 ? "quick" : n <= 17 ? "standard" : "deep";
}
var CONVERSATION_INTRO_BODY_MAX = 170;
var CONVERSATION_INTRO_TOPICS_MAX = 3;
function conversationIntroIsLong(intro) {
  return Boolean(intro && (intro.body.length > CONVERSATION_INTRO_BODY_MAX || intro.topics.length > CONVERSATION_INTRO_TOPICS_MAX));
}
var ACTIVITY_NOUNS = {
  product: "um produto",
  product_line: "uma linha de produtos",
  network_person: "uma pessoa do Network",
  client_creator: "um creator de um cliente",
  lab_room: "uma sala do LAB",
  lab_round: "uma rodada do LAB",
  asset: "uma imagem da galeria",
  asset_creator: "um est\xFAdio ou fot\xF3grafo",
  asset_person: "uma pessoa da galeria",
  user: "uma pessoa",
  organization: "um cliente",
  membership: "um acesso",
  invitation: "um convite",
  channel: "um canal",
  brand: "uma marca",
  campaign: "uma campanha",
  competitor: "um concorrente",
  mystery_shop: "uma visita de cliente oculto",
  point: "um ponto",
  presence: "uma presen\xE7a",
  dispute: "uma disputa",
  point_group: "um grupo de pontos",
  data_source: "uma fonte de dados",
  project: "um projeto",
  cycle: "um ciclo",
  file: "um arquivo",
  source: "uma fonte",
  evidence: "uma evid\xEAncia",
  question: "uma pergunta",
  observation: "uma observa\xE7\xE3o",
  signal: "um sinal",
  hypothesis: "uma hip\xF3tese",
  relation: "uma rela\xE7\xE3o",
  initiative: "uma iniciativa",
  demand: "uma demanda",
  team_function: "uma fun\xE7\xE3o da casa",
  board_view: "uma vis\xE3o do Pulso",
  board_week: "a edi\xE7\xE3o da semana do Pulso",
  deliverable: "uma pe\xE7a",
  cost: "um item de or\xE7amento",
  conversation: "uma conversa",
  session: "uma sess\xE3o",
  solution: "a Solu\xE7\xE3o",
  claim: "uma afirma\xE7\xE3o da Solu\xE7\xE3o",
  decision: "uma decis\xE3o",
  effect: "um efeito",
  learning: "um aprendizado",
  synthesis: "a s\xEDntese",
  publication: "uma publica\xE7\xE3o na sala",
  comment: "um coment\xE1rio"
};
var ACTIVITY_VERBS = { created: "criou", updated: "editou", deleted: "apagou", removed: "removeu", revoked: "revogou", accepted: "aceitou", rejected: "descartou" };
var ACTIVITY_SPECIAL = {
  "point.imported": "importou pontos de um arquivo de endere\xE7os",
  "asset_person.contract_sent": "mandou um contrato de uso de imagem para assinar",
  "asset_person.contract_signed": "recebeu um contrato de uso de imagem assinado",
  "ad.paused": "pausou na conta de an\xFAncio",
  "ad.resumed": "reativou na conta de an\xFAncio",
  "ad.budget_changed": "mudou a verba de um conjunto de an\xFAncios",
  "ad.created": "criou uma campanha pausada na conta de an\xFAncio",
  "ad.negatives_added": "acrescentou palavras negativas no Google Ads",
  "demand.note_added": "anotou numa demanda",
  "demand.pending_added": "registrou o que falta do cliente numa demanda",
  "demand.pending_answered": "registrou a resposta do cliente a uma pend\xEAncia",
  "demand.pending_dropped": "dispensou uma pend\xEAncia com o cliente",
  "demand.route_confirmed": "confirmou o percurso de uma demanda",
  "demand.step_done": "concluiu uma etapa e passou adiante",
  "demand.step_returned": "devolveu uma etapa",
  "demand.step_skipped": "pulou uma etapa",
  "demand.approval_sent": "enviou uma demanda para o cliente aprovar",
  "demand.approval_decided": "registrou a decis\xE3o do cliente numa demanda",
  "demand.step_reopened": "reabriu uma etapa por causa do ajuste do cliente",
  "conversation.discarded": "descartou uma conversa",
  "conversation.restored": "trouxe de volta uma conversa descartada",
  "future.updated": "anotou o que a diretoria disse",
  "future.read": "pediu a leitura da vis\xE3o de futuro",
  "ai_limit.updated": "mudou um limite de uso de IA",
  "sector.updated": "anotou o que olhar no setor",
  "sector.read": "pediu a leitura de setor e normas",
  "rastro.swept": "pediu a varredura de um setor no Rastro",
  "rastro.client_sectors": "ligou um cliente a setores do Rastro",
  "rastro_theme.updated": "renomeou um tema do Rastro",
  "rastro_theme.merged": "uniu dois temas do Rastro",
  "rastro_theme.discarded": "descartou um tema do Rastro",
  "rastro_theme.restored": "trouxe de volta um tema do Rastro",
  "rastro_theme.hypothesis": "levou um tema do Rastro \xE0 parede como hip\xF3tese",
  "rastro.consumer_read": "pediu a leitura do consumidor de um setor no Rastro",
  "rastro_signal.hypothesis": "levou um sinal do consumidor do Rastro \xE0 parede como hip\xF3tese",
  "rastro_case.created": "pediu a investiga\xE7\xE3o de um tema do Rastro",
  "bis.drafted": "pediu o rascunho do BIS",
  "bis.published": "publicou uma vers\xE3o do BIS",
  "bis_item.created": "acrescentou um item ao BIS",
  "bis_item.updated": "mexeu num item do BIS",
  "bis_item.deleted": "tirou um item do BIS",
  "bis_reference.created": "subiu um documento de refer\xEAncia ao BIS",
  "bis.construction_opened": "abriu a constru\xE7\xE3o do BIS",
  "bis.construction_reread": "pediu \xE0 Machine que relesse o material da constru\xE7\xE3o do BIS",
  "bis_reference.applied": "levou ao BIS um achado de um documento de refer\xEAncia",
  "bis_reference.dismissed": "dispensou um achado de um documento de refer\xEAncia",
  "bis_reference.deleted": "tirou um documento de refer\xEAncia do BIS",
  "bis_mold.created": "abriu uma sala de molde no BIS",
  "bis_mold.finished": "levou um molde da sala para o BIS",
  "bis_mold.deleted": "apagou uma sala de molde do BIS",
  "modo_material.created": "p\xF4s um material para circular pelo MODO",
  "modo_material.decided": "decidiu sobre um material do MODO",
  "notification.forwarded": "encaminhou uma tela a algu\xE9m da equipe",
  "personal_token.created": "ligou a Machine ao Claude (token pessoal)",
  "personal_token.revoked": "desligou um token da Machine no Claude",
  "oauth.client_registered": "um aplicativo se registrou para pedir acesso \xE0 Machine",
  "oauth.authorized": "conectou a Machine ao Claude (login)",
  "oauth.revoked": "desligou uma conex\xE3o da Machine no Claude",
  "mcp.called": "consultou a Machine pelo Claude",
  "bis_item.proposed": "prop\xF4s um bloco do BIS",
  "drive.file_uploaded": "mandou um arquivo para o drive da marca",
  "bis_study.requested": "pediu um estudo de imagem a partir do BIS",
  "material.rendered": "gerou um material (PDF) por uma receita",
  "material_recipe.created": "criou uma receita de material",
  "material_recipe.updated": "mudou uma receita de material",
  "demand.drive_upload": "mandou arquivos para a pasta de uma demanda no Drive",
  "demand.item_updated": "corrigiu uma pe\xE7a de uma demanda",
  "demand.revision_added": "registrou uma revis\xE3o do cliente numa demanda",
  "demand.revision_item": "marcou um pedido de revis\xE3o do cliente",
  "auth.login": "entrou na Machine",
  "auth.bootstrap": "inicializou a Machine",
  "auth.password_reset_requested": "pediu uma senha nova",
  "auth.password_reset": "trocou a senha",
  "invitation.created": "convidou uma pessoa",
  "invitation.accepted": "aceitou o convite",
  "presence.set": "registrou uma presen\xE7a numa pra\xE7a",
  "dispute.set": "priorizou uma disputa",
  "place.scouted": "pediu a pesquisa do entorno",
  "data.imported": "importou dados",
  "data_source.connected": "ligou uma fonte de dados",
  "data_source.token_issued": "gerou um token de entrega de dados",
  "data_source.token_revoked": "desligou a entrega de dados",
  "data_source.pulled": "atualizou uma fonte de dados",
  "data_source.voice_read": "pediu a leitura das conversas de venda do CRM",
  "initiative.measured": "aferiu uma iniciativa pela s\xE9rie",
  "positioning.updated": "ajustou o posicionamento",
  "visual.requested": "pediu uma visualiza\xE7\xE3o",
  "visual.reviewed": "avaliou uma visualiza\xE7\xE3o",
  "file.stored": "enviou um arquivo",
  "source.reprocessed": "mandou reprocessar uma fonte",
  "evidence.validated": "validou uma evid\xEAncia",
  "wall.item_added": "p\xF4s um item na Wall",
  "wall.item_moved": "moveu um item na Wall",
  "wall.item_removed": "tirou um item da Wall",
  "wall.session_created": "abriu uma sess\xE3o de Wall",
  "wall.session_updated": "editou uma sess\xE3o de Wall",
  "ai.make_visible": "pediu \xE0 Machine para tornar vis\xEDvel",
  "ai.challenge": "pediu \xE0 Machine para remar contra",
  "ai.ask": "perguntou \xE0 Machine",
  "ai.connect": "pediu conex\xF5es \xE0 Machine",
  "ai.session": "usou a Machine numa sess\xE3o",
  "ai.frame": "pediu o ENQUADRAR",
  "ai.fill": "preencheu um formul\xE1rio com a Machine",
  "ai.review": "pediu um parecer \xE0 Machine",
  "ai.feedback": "avaliou uma resposta da Machine",
  "suggestion.accepted": "aceitou uma sugest\xE3o da Machine",
  "suggestion.rejected": "descartou uma sugest\xE3o da Machine",
  "suggestion.promoted": "promoveu uma hip\xF3tese a VER",
  "initiative.client_decision": "decidiu sobre uma iniciativa",
  "deliverable.moved": "moveu uma pe\xE7a no quadro",
  "deliverable.version_added": "enviou uma vers\xE3o nova de pe\xE7a",
  "deliverable.client_decision": "decidiu sobre uma pe\xE7a",
  "review.round_opened": "abriu uma rodada de revis\xE3o",
  "review.decided": "decidiu uma rodada de revis\xE3o",
  "review.comment_created": "comentou numa pe\xE7a",
  "review.comment_updated": "editou um coment\xE1rio de pe\xE7a",
  "review.comment_deleted": "apagou um coment\xE1rio de pe\xE7a",
  "cycle.rolled": "virou o ciclo LIVE",
  "cycle.question_versioned": "mudou a pergunta do ciclo",
  "cycle.scope_updated": "editou o escopo do ciclo",
  "cycle.team_updated": "mudou o time do ciclo",
  "cycle.synthesized": "gerou a s\xEDntese do ciclo",
  "conversation_profile.generated": "gerou um p\xFAblico a ouvir",
  "conversation_profile.read": "pediu a leitura de um p\xFAblico",
  "conversation.done": "concluiu uma conversa",
  "project.contract_updated": "editou o contrato do projeto",
  "notification.read": "leu um aviso",
  "solution.approved": "aprovou a Solu\xE7\xE3o",
  "judgment.recorded": "julgou",
  "reaction.toggled": "reagiu",
  "serendipity.scanned": "pediu a varredura de serendipidade",
  "serendipity.feedback": "avaliou uma conex\xE3o",
  "membership.created": "deu acesso a uma pessoa",
  "membership.updated": "mudou o papel de uma pessoa",
  "membership.removed": "tirou o acesso de uma pessoa"
};
var ACTIVITY_NOISE = ["notification.read", "file.stored", "reaction.toggled"];
function activityText(action, count = 1) {
  const base = ACTIVITY_SPECIAL[action] ?? (() => {
    const [entity = "", verb = ""] = action.split(".");
    return `${ACTIVITY_VERBS[verb] ?? verb.replace(/_/g, " ")} ${ACTIVITY_NOUNS[entity] ?? entity.replace(/_/g, " ")}`;
  })();
  return count > 1 ? `${base} (${count} vezes)` : base;
}
var ACTIVITY_AI_LABELS = {
  modo: "atendeu pedidos no MODO (escreveu, conferiu e respondeu sobre a marca)",
  ritmo: "montou o panorama de pedidos no Ritmo",
  bis: "rascunhou o BIS e leu documentos de refer\xEAncia",
  future: "leu a vis\xE3o de futuro",
  sector: "pesquisou o setor e as normas",
  rastro: "varreu e investigou setores no Rastro",
  asset: "leu imagens da galeria",
  asset_edit: "gerou vers\xF5es de imagens da galeria",
  lab: "gerou propostas e pesquisas no LAB",
  creators: "leu a ader\xEAncia de pessoas do network e montou squads de campanha",
  embed: "indexou trechos para a busca",
  transcribe: "transcreveu \xE1udio",
  search: "buscou na mem\xF3ria",
  ask: "respondeu a uma pergunta",
  classify: "classificou material",
  summarize: "escreveu fichas de fonte",
  extract: "extraiu evid\xEAncias",
  connect: "procurou conex\xF5es",
  challenge: "remou contra",
  make_visible: "tornou vis\xEDvel",
  synthesize: "escreveu a s\xEDntese",
  serendipity: "procurou serendipidade",
  find: "prop\xF4s hip\xF3teses (ENCONTRAR)",
  solution: "rascunhou a Solu\xE7\xE3o",
  session_agenda: "escreveu a pauta de uma sess\xE3o",
  session_summary: "resumiu uma sess\xE3o",
  frame: "prop\xF4s a pergunta do ciclo (ENQUADRAR)",
  interview: "conduziu conversas",
  transcript_review: "revisou nomes em transcri\xE7\xF5es",
  describe_media: "leu fotos e v\xEDdeos",
  fill: "preencheu formul\xE1rios",
  review: "deu parecer",
  research: "pesquisou na web",
  competitor_profile: "refez o perfil de concorrentes",
  competitor_research: "pesquisou concorrentes na web",
  competitor_watch: "acompanhou concorrentes",
  social_reading: "leu redes de concorrentes",
  ads_reading: "leu an\xFAncios de concorrentes",
  reviews_reading: "leu avalia\xE7\xF5es",
  page_watch: "conferiu p\xE1ginas de concorrentes",
  site_map: "mapeou sites de concorrentes",
  mystery_shop: "leu visitas de cliente oculto",
  positioning: "leu o posicionamento",
  message_territory: "leu o territ\xF3rio de mensagem",
  territory: "leu o territ\xF3rio visual",
  visual_brief: "pensou visualiza\xE7\xF5es",
  visual_generate: "gerou imagens",
  data_mapping: "prop\xF4s o mapeamento de um relat\xF3rio",
  channel_use: "leu como as marcas usam os canais",
  decide: "tomou decis\xF5es estruturadas (Jev)",
  place_scout: "pesquisou o entorno de uma pra\xE7a",
  audience_reading: "leu um p\xFAblico ouvido",
  data_deviation: "apontou desvio em dado"
};
var ACTIVITY_JOB_LABELS = {
  "rastro.sweep": "varrer um setor no Rastro",
  "rastro.case": "investigar um tema do Rastro na web",
  "rastro.consumer": "ler o consumidor de um setor no Rastro",
  "modo.compose": "montar uma pe\xE7a com foto no MODO",
  "demand.panorama": "montar o panorama de um pedido",
  "social.tags": "ler as marca\xE7\xF5es de uma conta",
  "ads.analyze": "analisar uma campanha de m\xEDdia",
  "ads.account_read": "ler a m\xEDdia inteira de um cliente",
  "campaign.room_turn": "responder na sala de planejamento de campanha",
  "campaign.room_file": "ler um anexo da sala de planejamento de campanha",
  "ads.google_draft": "rascunhar uma campanha de busca do Google",
  "ads.terms_review": "ler os termos de busca do Google que gastam sem converter",
  "rd.voice": "ler as conversas de venda do CRM",
  "board.week": "ler a semana de um cliente para o Pulso",
  "social.inbox": "ler as mensagens de uma conta",
  "demand.item_publish": "publicar um conte\xFAdo no Instagram",
  "demand.room_turn": "responder na sala de planejamento",
  "demand.room_doc": "escrever o documento de planejamento",
  "demand.room_review": "revisar o documento de planejamento",
  "client.drive_folders": "criar as pastas de um cliente no Drive",
  "demand.drive_folder": "criar a pasta de um job no Drive",
  "demand.drive_sync": "conferir a pasta de um job no Drive",
  "demand.drive_approved": "guardar no Drive a arte aprovada",
  "demand.route": "propor o percurso de uma demanda",
  "demand.file_read": "ler um anexo de demanda",
  "demand.step_review": "revisar a entrega de uma etapa",
  "demand.change_review": "ler um pedido de ajuste do cliente",
  "demand.revision_read": "separar os pedidos de uma revis\xE3o do cliente",
  "demand.items": "propor os itens de uma demanda",
  "demand.link_fetch": "buscar um link do Drive de uma demanda",
  "demand.item_review": "revisar um item de demanda",
  "bis.draft": "rascunhar o BIS",
  "bis.reference_read": "ler um documento de refer\xEAncia do BIS",
  "bis.reference_turn": "responder sobre um documento de refer\xEAncia do BIS",
  "future.read": "ler a vis\xE3o de futuro",
  "sector.read": "pesquisar o setor e as normas",
  "asset.read": "ler uma imagem da galeria",
  "asset.faces": "procurar rostos conhecidos na galeria",
  "asset.edit": "gerar uma vers\xE3o de uma imagem da galeria",
  "asset.compose": "montar uma cena com as pe\xE7as da galeria",
  "asset.products": "procurar os produtos numa imagem da galeria",
  "product.scan": "procurar os produtos nos posts e nas pe\xE7as",
  "asset.audit": "conferir uma imagem gerada contra o BIS",
  "creator.fit": "ler a ader\xEAncia de uma pessoa do network a uma marca",
  "campaign.squad": "montar o squad de uma campanha",
  "network.profile": "ler o perfil p\xFAblico de uma pessoa do network",
  "network.research": "pesquisar na web uma pessoa do network",
  "lab.generate": "gerar uma proposta no LAB",
  "lab.research": "pesquisar na web para uma sala do LAB",
  "lab.shelf": "montar a prateleira de uma categoria para uma sala do LAB",
  "source.process": "processar uma fonte",
  "deliverable.render": "renderizar uma pe\xE7a",
  "competitor.profile": "refazer o perfil de um concorrente",
  "competitor.social": "ler uma rede de concorrente",
  "competitor.pages": "conferir p\xE1ginas de concorrente",
  "competitor.reviews": "ler avalia\xE7\xF5es",
  "competitor.ads": "ler an\xFAncios",
  "competitor.sitemap": "mapear um site",
  "competitor.evolution": "comparar rodadas de cliente oculto",
  "mystery_shop.analyze": "ler uma visita de cliente oculto",
  "territory.extract": "ler o territ\xF3rio visual",
  "visual.generate": "gerar uma visualiza\xE7\xE3o",
  "place.scout": "pesquisar o entorno",
  "points.import": "ler um arquivo de endere\xE7os",
  "positioning.read": "ler o posicionamento",
  "message_territory.read": "ler o territ\xF3rio de mensagem"
};
var POST_PURPOSES = ["produto", "promocao", "campanha", "conteudo", "institucional", "evento", "prova", "pessoas", "outro"];
var POST_PURPOSE_LABELS = { produto: "produto e servi\xE7o", promocao: "promo\xE7\xE3o e oferta", campanha: "campanha", conteudo: "conte\xFAdo que ensina", institucional: "institucional e not\xEDcia", evento: "evento", prova: "prova social", pessoas: "vaga e bastidor", outro: "outro" };
function postPurpose(kind) {
  const k = (kind ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (/promo|oferta|desconto|brinde/.test(k)) return "promocao";
  if (/produto|servico|plano|lancamento/.test(k)) return "produto";
  if (/campanha/.test(k)) return "campanha";
  if (/depoimento|prova|case|avaliac/.test(k)) return "prova";
  if (/vaga|bastidor|equipe|recrut|cultura/.test(k)) return "pessoas";
  if (/evento|data comemorativa|acao/.test(k)) return "evento";
  if (/institucional|noticia|comunicado|premio|homenagem/.test(k)) return "institucional";
  if (/conteudo|dica|educa|informa/.test(k)) return "conteudo";
  return "outro";
}
var CHANNEL_ROLES = ["sell", "teach", "self", "proof", "people"];
var CHANNEL_ROLE_LABELS = { sell: "vender", teach: "ensinar e informar", self: "falar de si (not\xEDcia e institucional)", proof: "mostrar prova", people: "atrair e mostrar gente" };
var CHANNEL_ROLE_SHORT = { sell: "venda", teach: "conte\xFAdo", self: "not\xEDcia e institucional", proof: "prova", people: "gente" };
var ROLE_OF = { produto: "sell", promocao: "sell", campanha: "sell", conteudo: "teach", institucional: "self", evento: "self", prova: "proof", pessoas: "people", outro: null };
var CHANNEL_ROLE_MIN_POSTS = 5;
var CHANNEL_DORMANT_DAYS = 60;
function channelRole(purposes) {
  const shares = { sell: 0, teach: 0, self: 0, proof: 0, people: 0 };
  let read = 0;
  for (const p of POST_PURPOSES) {
    const n = purposes[p] ?? 0;
    read += n;
    const r = ROLE_OF[p];
    if (r) shares[r] += n;
  }
  if (read < CHANNEL_ROLE_MIN_POSTS) return { roles: [], shares, label: "pouco material para dizer", read };
  for (const r of CHANNEL_ROLES) shares[r] = Math.round(shares[r] / read * 100) / 100;
  const order = [...CHANNEL_ROLES].sort((a, b) => shares[b] - shares[a]);
  const [first, second] = order;
  if (shares[first] >= 0.5) return { roles: [first], shares, label: `para ${CHANNEL_ROLE_LABELS[first]}`, read };
  if (shares[first] >= 0.3 && shares[second] >= 0.2) return { roles: [first, second], shares, label: `para ${CHANNEL_ROLE_LABELS[first]}, e tamb\xE9m para ${CHANNEL_ROLE_LABELS[second]}`, read };
  return { roles: [], shares, label: "sem um papel dominante", read };
}
function placeClaimed(text, city) {
  const plain3 = (t2) => t2.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ");
  const t = plain3(text);
  const c = plain3(city).trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (c.length < 3) return false;
  const at = `(em|na|no|a|ao|para|de) (a |o )?(cidade de |regiao de )?${c}\\b`;
  const few = "(?:[a-z0-9]+ ){0,5}";
  return [
    `\\bcheg(a|ou|ada|ando|amos|aremos) ${few}${at}`,
    `\\binaugur[a-z]+ ${few}${at}`,
    `\\b(nova|novo|primeira|primeiro)? ?(unidade|loja|filial|sede|clinica|hospital|escritorio|ponto de atendimento) ${few}${at}`,
    `\\bagora (tambem )?(em|na|no) (a |o )?(cidade de )?${c}\\b`,
    `\\batend(e|emos|imento|endo) ${few}(em|na|no) (a |o )?(cidade de )?${c}\\b`,
    `\\b(estamos|presente|presentes) (tambem )?(em|na|no) ${c}\\b`,
    `\\babr(e|iu|imos|indo|ira) ${few}(em|na|no) ${c}\\b`,
    `\\b${c} (ganha|ganhou|recebe|recebeu|agora tem|passa a ter|ja tem)\\b`
  ].some((p) => new RegExp(p).test(t));
}
var BIS_SECTIONS = ["identity", "future", "audience", "voice", "visual", "behavior", "creative", "programs", "rules"];
var BIS_SECTION_LABELS = { identity: "identidade", future: "dire\xE7\xE3o", audience: "p\xFAblicos", voice: "voz", visual: "visual", behavior: "comportamento", creative: "cria\xE7\xE3o", programs: "programas e a\xE7\xF5es", rules: "regras" };
var BIS_SECTION_HINTS = {
  identity: "prop\xF3sito, posicionamento, promessa, personalidade, princ\xEDpios, diferenciais",
  future: "onde a marca quer chegar, do que n\xE3o abre m\xE3o e o que n\xE3o quer ser (vem da diretoria)",
  audience: "para quem a marca fala: p\xFAblicos, necessidades, como percebem a marca",
  voice: "tom, vocabul\xE1rio, mensagens, exemplos, o que evitar",
  visual: "cor, tipografia, fotografia, composi\xE7\xE3o, elementos, como aplicar",
  behavior: "como a marca age, responde e se posiciona",
  creative: "territ\xF3rios criativos, narrativas, repert\xF3rio, dire\xE7\xF5es aprovadas",
  programs: "eventos, campanhas, selos e submarcas com identidade pr\xF3pria e, \xE0s vezes, data para acabar. herdam tudo da marca e s\xF3 dizem o que \xE9 deles",
  rules: "o que orienta quem produz: obrigat\xF3rio, recomendado, permitido, proibido"
};
var BIS_FORCES = ["must", "should", "may", "never"];
var BIS_FORCE_LABELS = { must: "obrigat\xF3rio", should: "recomendado", may: "permitido", never: "proibido" };
var BIS_RULE_KINDS = ["brand", "compliance"];
var BIS_RULE_KIND_LABELS = { brand: "de marca", compliance: "de conformidade" };
var BIS_INFO_CLASSES = ["all", "internal", "restricted"];
var BIS_INFO_CLASS_LABELS = { all: "de todos", internal: "interno", restricted: "restrito" };
var BIS_INFO_CLASS_HINTS = { all: "toda pessoa do cliente l\xEA", internal: "respons\xE1vel da \xE1rea, guardi\xE3o e diretoria", restricted: "s\xF3 guardi\xE3o e diretoria" };
var BIS_DEFAULT_CLASS = { identity: "internal", future: "restricted", audience: "restricted", voice: "all", visual: "all", behavior: "all", creative: "internal", programs: "all", rules: "all" };
var BIS_ITEM_STATUSES = ["proposed", "active", "rejected"];
var BIS_BLOCK_KINDS = ["note", "statement", "attributes", "audience", "vocabulary", "grammar", "message", "palette", "typography", "logo", "icons", "pattern", "illustration", "photography", "piece", "seal", "character", "mold", "program", "channel", "rule"];
var BIS_BLOCK_LABELS = { note: "texto", statement: "declara\xE7\xE3o", attributes: "atributos", audience: "p\xFAblico", vocabulary: "vocabul\xE1rio", grammar: "gram\xE1tica da marca", message: "exemplo de mensagem", palette: "paleta", typography: "tipografia", logo: "logo", icons: "\xEDcones", pattern: "padr\xF5es e texturas", illustration: "ilustra\xE7\xE3o", photography: "dire\xE7\xE3o fotogr\xE1fica", piece: "diretriz de pe\xE7a", seal: "selo", character: "personagem", mold: "molde de pe\xE7a", program: "programa ou a\xE7\xE3o", channel: "linha editorial do canal", rule: "regra" };
var BIS_BLOCK_HINTS = {
  note: "um texto corrido, sem estrutura",
  statement: "uma afirma\xE7\xE3o da marca, com o que ela significa e por que importa",
  attributes: "uma lista com nome e sentido: pilares, valores, tra\xE7os de personalidade",
  audience: "um p\xFAblico: quem \xE9, o que precisa, como percebe a marca",
  vocabulary: "as palavras: o que usa, o que evita, o que nunca usa, e as trocas",
  grammar: "como a marca fala em cada situa\xE7\xE3o: nome, pessoa, tratamento",
  message: "uma mensagem de exemplo, onde ela vale e por que funciona",
  palette: "as cores, com o c\xF3digo e o papel de cada uma",
  typography: "as fontes, com o papel, os pesos e a substituta",
  logo: "o logo: os arquivos de cada vers\xE3o, a \xE1rea de seguran\xE7a, o tamanho m\xEDnimo e os usos errados",
  icons: "o conjunto de \xEDcones da marca, com os arquivos e o tra\xE7o",
  pattern: "padr\xF5es e texturas da marca, com os arquivos e onde entram",
  illustration: "como a marca ilustra, com exemplos",
  photography: "como a marca fotografa: luz, cor, enquadramento, quem aparece e onde, com as fotos de refer\xEAncia. \xE9 por este bloco que a Machine gera fotos de estudo no jeito da marca",
  piece: "como uma fam\xEDlia de pe\xE7a se parece (post, cartaz, embalagem, an\xFAncio): formato, composi\xE7\xE3o, hierarquia, a imagem que entra e como o texto aparece, com pe\xE7as de exemplo. \xE9 guia para quem cria e para a Machine gerar estudos; quem monta a pe\xE7a no MODO \xE9 o molde",
  mold: "como uma fam\xEDlia de pe\xE7a \xE9 montada: fundos permitidos, onde fica o logo, o t\xEDtulo, a faixa e o grafismo. quem desenha \xE9 o c\xF3digo",
  seal: "selo que anda junto do logotipo: comemorativo (55 anos), de certifica\xE7\xE3o ou de programa. tem arquivo, pode ter data para acabar, e pode valer s\xF3 para um canal, uma pe\xE7a ou um programa",
  character: "personagem ou mascote da marca: quem \xE9, como \xE9, as poses de refer\xEAncia e o que ele nunca faz. \xE9 com isto que um modelo de imagem o replica sem descaracterizar",
  program: 'um evento, campanha, selo ou submarca da marca: logo pr\xF3prio, cores, vig\xEAncia e parceiros. os outros blocos podem valer s\xF3 para ele (em "onde vale")',
  channel: "como a marca usa UM canal (Instagram, LinkedIn, e-mail): para que ele serve, como ela fala ali, o tamanho do texto e o que n\xE3o faz. \xE9 por este bloco que a mesma mensagem sai com um jeito em cada canal",
  rule: "uma regra que algu\xE9m confere olhando a pe\xE7a, com fa\xE7a e n\xE3o fa\xE7a; de conformidade, com a norma; ou um texto que sempre sai (a assinatura da legenda)"
};
var BIS_SECTION_BLOCKS = {
  identity: ["statement", "attributes", "note"],
  future: ["statement", "attributes", "note"],
  audience: ["audience", "note"],
  voice: ["vocabulary", "grammar", "message", "channel", "attributes", "note"],
  visual: ["palette", "typography", "logo", "icons", "pattern", "illustration", "photography", "piece", "seal", "character", "mold", "note"],
  behavior: ["statement", "message", "note"],
  creative: ["statement", "message", "note"],
  programs: ["program", "note"],
  rules: ["rule"]
};
var BIS_LOGO_ON_PHOTO = [{ key: "panel", label: "num espa\xE7o branco reservado" }, { key: "direct", label: "direto sobre a pe\xE7a: o logotipo j\xE1 traz a pr\xF3pria prote\xE7\xE3o" }];
var BIS_ICON_CAPS = [{ key: "round", label: "arredondada" }, { key: "butt", label: "reta" }];
var BIS_DRAWERS = [{ key: "claude", label: "Claude" }, { key: "gpt", label: "GPT" }];
var bisDrawerOf = (v) => v === "gpt" ? "gpt" : "claude";
var BIS_ILLUSTRATORS = [{ key: "gpt", label: "GPT" }, { key: "banana", label: "Nano Banana" }];
var bisIllustratorOf = (v) => v === "banana" ? "banana" : "gpt";
var BIS_ILLUSTRATION_GROUNDS = [{ key: "white", label: "branco" }, { key: "transparent", label: "transparente" }];
var ILLUSTRATION_OPTIONS = 3;
var ILLUSTRATION_ESTIMATE_USD = 0.08;
var BIS_STUDY_KINDS = ["photography", "piece"];
var BIS_STUDY_OPTIONS_MAX = 3;
var BIS_STUDY_ESTIMATE_USD = 0.08;
var BIS_STUDY_REFS_MAX = 4;
var ILLUSTRATION_SHAPES = [{ key: "landscape", label: "deitada", ratio: "3:2" }, { key: "portrait", label: "em p\xE9", ratio: "2:3" }, { key: "square", label: "quadrada", ratio: "1:1" }];
var BIS_BOARD_FILES_MAX = 3;
var BIS_BOARD_FILE_MAX = 10 * 1024 * 1024;
var BIS_BOARD_ESTIMATE_USD = 0.12;
var BIS_ICON_FAMILIES = [{ key: "both", label: "contorno e cheio" }, { key: "outline", label: "s\xF3 contorno" }, { key: "solid", label: "s\xF3 cheio" }];
var BIS_MOLD_LOGO = [{ key: "bottom-left", label: "embaixo, \xE0 esquerda" }, { key: "bottom-right", label: "embaixo, \xE0 direita" }, { key: "bottom-center", label: "embaixo, no centro" }, { key: "top-left", label: "em cima, \xE0 esquerda" }, { key: "top-right", label: "em cima, \xE0 direita" }];
var BIS_MOLD_ALIGN = [{ key: "left", label: "\xE0 esquerda" }, { key: "center", label: "centralizado" }];
var BIS_MOLD_CASE = [{ key: "normal", label: "como escrito" }, { key: "upper", label: "caixa alta" }];
var BIS_MOLD_WEIGHT = [{ key: "heavy", label: "pesado" }, { key: "bold", label: "negrito" }, { key: "regular", label: "regular" }];
var BIS_MOLD_BAR = [{ key: "none", label: "sem faixa" }, { key: "top", label: "no topo" }, { key: "left", label: "na lateral" }];
var BIS_MOLD_PATTERN = [{ key: "none", label: "sem grafismo" }, { key: "corner", label: "num canto" }, { key: "full", label: "no fundo todo, suave" }];
var BIS_MOLD_PRINT = [{ key: "digital", label: "digital (PDF comum)" }, { key: "offset", label: "offset (arquivo de gr\xE1fica, PDF/X-1a)" }, { key: "both", label: "os dois: quem exporta escolhe" }];
var BIS_MOLD_EYEBROW = [{ key: "show", label: "mostra" }, { key: "hide", label: "n\xE3o mostra" }];
var BIS_LEGAL_PLACES = [{ key: "footer", label: "rodap\xE9, em letra pequena" }, { key: "warning", label: "advert\xEAncia em destaque" }, { key: "seal", label: "selo junto da marca" }, { key: "caption", label: "no fim da legenda do post" }];
var BIS_BODY_LABELS = { note: "texto", statement: "a afirma\xE7\xE3o", attributes: "o que esta lista re\xFAne", audience: "quem \xE9", vocabulary: "o princ\xEDpio", grammar: "o princ\xEDpio", message: "a mensagem", palette: "como as cores se usam", typography: "como as fontes se usam", logo: "como o logo se usa", icons: "como os \xEDcones s\xE3o", pattern: "como os padr\xF5es entram", illustration: "como a marca ilustra", photography: "como a marca fotografa", piece: "o que \xE9 esta pe\xE7a e quando se usa", seal: "o que o selo marca", character: "quem \xE9", mold: "quando este molde \xE9 usado", program: "o que \xE9", channel: "o papel deste canal para a marca", rule: "a regra" };
var BIS_BLOCK_FIELDS = {
  note: [],
  statement: [{ key: "meaning", label: "o que significa", type: "long" }, { key: "why", label: "por que importa", type: "long" }],
  attributes: [{ key: "items", label: "atributos", type: "pairs", pairKeys: ["name", "text"], pairLabels: ["nome", "o que quer dizer"] }],
  audience: [{ key: "needs", label: "o que precisa", type: "list" }, { key: "perceives", label: "como percebe a marca", type: "long" }, { key: "speakTo", label: "como falar com ele", type: "long" }],
  vocabulary: [{ key: "use", label: "usa", type: "list" }, { key: "avoid", label: "evita", type: "list" }, { key: "never", label: "nunca usa", type: "list" }, { key: "swaps", label: "trocas", type: "pairs", pairKeys: ["from", "to"], pairLabels: ["em vez de", "diga"] }],
  grammar: [{ key: "rules", label: "situa\xE7\xF5es", type: "pairs", pairKeys: ["context", "rule"], pairLabels: ["quando", "como fala"] }],
  message: [{ key: "context", label: "onde vale", type: "text", hint: "canal, pe\xE7a ou situa\xE7\xE3o" }, { key: "why", label: "por que funciona", type: "long" }],
  palette: [{ key: "colors", label: "cores", type: "colors" }],
  typography: [{ key: "fonts", label: "fontes", type: "fonts" }, { key: "files", label: "arquivos de fonte", type: "assets", hint: 'um arquivo por peso. no nome diga a fam\xEDlia e o peso: "Filson Pro 700", "Filson Pro 400 italic". sem arquivo, a tela procura a fam\xEDlia no Google Fonts' }],
  logo: [
    { key: "versions", label: "vers\xF5es", type: "assets", hint: "principal, negativa, monocrom\xE1tica, s\xEDmbolo: um arquivo por vers\xE3o" },
    { key: "clearSpace", label: "\xE1rea de seguran\xE7a", type: "text", hint: "o respiro em volta, na medida do pr\xF3prio logo" },
    { key: "minSize", label: "tamanho m\xEDnimo", type: "text", hint: "em tela e em impresso" },
    { key: "dont", label: "usos errados", type: "list" },
    { key: "onPhoto", label: "em pe\xE7a com foto", type: "choice", options: BIS_LOGO_ON_PHOTO, hint: "se o manual diz que o logotipo dispensa fundo de prote\xE7\xE3o, ele entra direto sobre a pe\xE7a, na vers\xE3o que tem fundo pr\xF3prio" }
  ],
  icons: [
    { key: "items", label: "\xEDcones", type: "assets" },
    { key: "style", label: "o tra\xE7o", type: "long", hint: "espessura, cantos, grade, preenchido ou contorno" },
    { key: "cap", label: "ponta do tra\xE7o", type: "choice", options: BIS_ICON_CAPS, hint: "vale para os \xEDcones que a Machine desenha: o peso e a ponta s\xE3o regra, n\xE3o escolha do modelo" },
    { key: "family", label: "como a fam\xEDlia \xE9 desenhada", type: "choice", options: BIS_ICON_FAMILIES, hint: "uma marca pode ter mais de uma fam\xEDlia (essenciais, expressivos): cada uma \xE9 um bloco de \xEDcones, com a sua prancha" },
    { key: "drawer", label: "quem desenha", type: "choice", options: BIS_DRAWERS, hint: "o modelo que desenha os \xEDcones novos desta fam\xEDlia, no MODO e na sala de assets. compare os dois na sala de assets antes de decidir" },
    { key: "drawerNote", label: "por que este modelo", type: "text", hint: "a sala de assets escreve ao escolher depois de comparar" },
    { key: "reference", label: "prancha aprovada", type: "assets", hint: "a prancha do conjunto aprovado, ou a p\xE1gina do manual com o padr\xE3o de \xEDcones (imagem), uma ou duas: \xE9 o que a Machine olha antes de desenhar um \xEDcone novo" }
  ],
  pattern: [{ key: "items", label: "arquivos", type: "assets" }, { key: "usage", label: "onde entram", type: "long" }],
  mold: [
    { key: "template", label: "arquivo de composi\xE7\xE3o", type: "assets", hint: "opcional: um SVG montado pelo designer, com os campos marcados ({{headline}}, {{body}}, {{name}}\u2026). com ele, os par\xE2metros abaixo n\xE3o valem: o desenho \xE9 o do arquivo" },
    { key: "backgrounds", label: "fundos permitidos", type: "colors", hint: "at\xE9 tr\xEAs, na ordem de prefer\xEAncia. cada fundo vira uma op\xE7\xE3o; a cor do texto e a vers\xE3o do logo saem do contraste" },
    { key: "logoPosition", label: "onde fica o logo", type: "choice", options: BIS_MOLD_LOGO },
    { key: "align", label: "alinhamento do texto", type: "choice", options: BIS_MOLD_ALIGN },
    { key: "headingCase", label: "caixa do t\xEDtulo", type: "choice", options: BIS_MOLD_CASE },
    { key: "headingWeight", label: "peso do t\xEDtulo", type: "choice", options: BIS_MOLD_WEIGHT },
    { key: "bar", label: "faixa da marca", type: "choice", options: BIS_MOLD_BAR },
    { key: "pattern", label: "grafismo", type: "choice", options: BIS_MOLD_PATTERN, hint: "usa o primeiro arquivo do bloco de padr\xE3o" },
    { key: "eyebrow", label: "r\xF3tulo acima do t\xEDtulo", type: "choice", options: BIS_MOLD_EYEBROW },
    { key: "print", label: "como \xE9 impressa", type: "choice", options: BIS_MOLD_PRINT, hint: "offset s\xF3 para tiragem em gr\xE1fica; na d\xFAvida, digital" }
  ],
  seal: [
    { key: "files", label: "arquivo do selo", type: "assets", hint: "o primeiro \xE9 o que entra na pe\xE7a, ao lado do logotipo" },
    { key: "startsOn", label: "come\xE7a em", type: "text", hint: "AAAA-MM-DD. vazio = j\xE1 vale" },
    { key: "endsOn", label: "vale at\xE9", type: "text", hint: "AAAA-MM-DD. vazio = sem data para acabar. depois dela o selo sai das pe\xE7as sozinho" },
    { key: "dont", label: "usos errados", type: "list" }
  ],
  character: [
    { key: "poses", label: "poses de refer\xEAncia", type: "assets", hint: 'no nome diga a pose: "de frente, bra\xE7os cruzados", "apontando", "segurando uma moeda"' },
    { key: "look", label: "como \xE9", type: "long", hint: "a descri\xE7\xE3o que n\xE3o muda: idade aparente, cabelo, \xF3culos, roupa, cores, estilo do desenho (3D, tra\xE7o, flat). \xE9 o que vai ao modelo de imagem" },
    { key: "personality", label: "jeito", type: "long", hint: "como age e como fala, se fala" },
    { key: "do", label: "aparece assim", type: "list" },
    { key: "dont", label: "nunca", type: "list" }
  ],
  program: [
    { key: "logos", label: "logo do programa", type: "assets", hint: "a primeira vers\xE3o \xE9 a principal" },
    { key: "tagline", label: "frase", type: "text" },
    { key: "aliases", label: "outros nomes", type: "list", hint: "como as pessoas chamam. \xE9 por estes nomes, e pelo t\xEDtulo, que o MODO reconhece que o pedido \xE9 deste programa" },
    { key: "colors", label: "cores pr\xF3prias", type: "colors", hint: "s\xF3 as que n\xE3o s\xE3o da marca" },
    { key: "startsOn", label: "come\xE7a em", type: "text", hint: "AAAA-MM-DD. vazio = j\xE1 vale" },
    { key: "endsOn", label: "vale at\xE9", type: "text", hint: "AAAA-MM-DD. vazio = sem data para acabar. depois dela o programa sai das op\xE7\xF5es sozinho" },
    { key: "partners", label: "parceiros", type: "pairs", pairKeys: ["role", "name"], pairLabels: ["papel", "quem"], hint: "na ordem da r\xE9gua: realiza\xE7\xE3o, media partner, patrocinador master, patrocinadores" },
    { key: "partnerLogos", label: "logos dos parceiros", type: "assets", hint: 'no nome diga o papel e quem \xE9: "patrocinador master: Sicoob"' }
  ],
  illustration: [
    { key: "items", label: "ilustra\xE7\xF5es", type: "assets" },
    { key: "reference", label: "prancha-mestra", type: "assets", hint: "a prancha das cenas aprovadas (imagem), uma ou duas: toda ilustra\xE7\xE3o nova \xE9 feita olhando para ela, e nunca para uma gerada antes" },
    { key: "style", label: "a linguagem do desenho", type: "long", hint: "tra\xE7o, simplifica\xE7\xE3o, formas, planos, como as cores se equilibram: o que faz uma cena parecer da mesma m\xE3o" },
    { key: "people", label: "como as pessoas aparecem", type: "long", hint: "propor\xE7\xE3o, rosto (com ou sem fei\xE7\xF5es), cabelo, roupa, diversidade. vazio = siga a prancha" },
    { key: "formats", label: "formatos", type: "pairs", pairKeys: ["name", "how"], pairLabels: ["formato", "como \xE9"], hint: 'ex.: "cena principal" = uma ou duas pessoas, at\xE9 quatro cart\xF5es; "vers\xE3o para a plataforma" = uma pessoa do peito para cima, leg\xEDvel em 200 px' },
    { key: "ground", label: "fundo", type: "choice", options: BIS_ILLUSTRATION_GROUNDS, hint: "transparente entra sobre qualquer fundo, mas o que tem a cor do fundo some na cena" },
    { key: "illustrator", label: "quem desenha", type: "choice", options: BIS_ILLUSTRATORS, hint: "o modelo de imagem desta fam\xEDlia. compare os dois na sala de assets antes de decidir" },
    { key: "illustratorNote", label: "por que este modelo", type: "text", hint: "a sala de assets escreve ao escolher depois de comparar" },
    { key: "do", label: "fa\xE7a", type: "list" },
    { key: "dont", label: "n\xE3o fa\xE7a", type: "list", hint: "entra no pedido de toda ilustra\xE7\xE3o como exclus\xE3o" }
  ],
  photography: [
    { key: "reference", label: "fotos de refer\xEAncia", type: "assets", hint: "as fotos aprovadas que mostram o jeito da marca fotografar, at\xE9 quatro: toda foto de estudo \xE9 feita olhando para elas" },
    { key: "style", label: "o que faz a foto ser da marca", type: "long", hint: "o clima, o gesto, o que nunca falta: o que algu\xE9m olha para dizer que a foto \xE9 desta marca" },
    { key: "light", label: "luz, cor e tratamento", type: "long", hint: "luz natural ou de est\xFAdio, hora do dia, contraste, satura\xE7\xE3o, temperatura, gr\xE3o" },
    { key: "framing", label: "enquadramento e lente", type: "long", hint: "dist\xE2ncia, \xE2ngulo, profundidade de campo, onde fica o respiro para o texto" },
    { key: "people", label: "quem aparece e como", type: "long", hint: "quem s\xE3o, o que fazem, pose ou espont\xE2neo, rosto ou m\xE3os. vazio = siga as refer\xEAncias" },
    { key: "settings", label: "lugares, objetos e produto", type: "long", hint: "onde a foto acontece, o que est\xE1 em cena, como o produto aparece" },
    { key: "formats", label: "formatos", type: "pairs", pairKeys: ["name", "how"], pairLabels: ["formato", "como \xE9"], hint: 'ex.: "produto em uso" = m\xE3os e produto, fundo da opera\xE7\xE3o desfocado; "retrato" = uma pessoa do peito para cima' },
    { key: "do", label: "fa\xE7a", type: "list" },
    { key: "dont", label: "n\xE3o fa\xE7a", type: "list", hint: "entra no pedido de toda foto de estudo como exclus\xE3o" }
  ],
  piece: [
    { key: "examples", label: "pe\xE7as de exemplo", type: "assets", hint: "pe\xE7as prontas e aprovadas desta fam\xEDlia, at\xE9 quatro: a Machine olha para elas ao gerar um estudo" },
    { key: "format", label: "formato e medidas", type: "text", hint: '"post 1080 \xD7 1350", "cartaz A3 vertical", "r\xF3tulo 90 \xD7 60 mm"' },
    { key: "composition", label: "composi\xE7\xE3o", type: "long", hint: "onde fica cada coisa: imagem, t\xEDtulo, logo, respiro, margens" },
    { key: "hierarchy", label: "hierarquia do texto", type: "long", hint: "o que se l\xEA primeiro, segundo e terceiro, e quanto texto cabe" },
    { key: "imagery", label: "a imagem na pe\xE7a", type: "long", hint: "foto, ilustra\xE7\xE3o, produto ou grafismo: o que entra, de que tamanho e como se encaixa" },
    { key: "typeUse", label: "como o texto aparece", type: "long", hint: "fonte, peso, caixa, cor e alinhamento de t\xEDtulo, apoio e chamada" },
    { key: "do", label: "fa\xE7a", type: "list" },
    { key: "dont", label: "n\xE3o fa\xE7a", type: "list", hint: "entra no pedido de todo estudo desta pe\xE7a como exclus\xE3o" }
  ],
  channel: [
    { key: "channel", label: "canal", type: "choice", options: BIS_CHANNELS, hint: "um bloco por canal" },
    { key: "purpose", label: "para que serve", type: "long", hint: "que tipo de conte\xFAdo vai aqui e para quem. \xE9 por isto que o MODO sugere levar um conte\xFAdo tamb\xE9m para este canal" },
    { key: "tone", label: "como a marca fala aqui", type: "long", hint: "o que muda em rela\xE7\xE3o \xE0 voz geral: mais pr\xF3ximo, mais institucional, primeira pessoa\u2026" },
    { key: "length", label: "tamanho do texto", type: "text", hint: '"2 a 4 frases", "at\xE9 1.300 caracteres, com par\xE1grafos curtos"' },
    { key: "hashtags", label: "hashtags", type: "list", hint: "as que a marca usa neste canal. vazio = n\xE3o usa" },
    { key: "do", label: "fa\xE7a", type: "list" },
    { key: "dont", label: "n\xE3o fa\xE7a", type: "list" }
  ],
  rule: [
    { key: "check", label: "como conferir", type: "long", hint: "o que algu\xE9m olha na pe\xE7a para dizer se cumpriu" },
    { key: "do", label: "fa\xE7a", type: "list" },
    { key: "dont", label: "n\xE3o fa\xE7a", type: "list" },
    { key: "issuer", label: "quem emite", type: "text", only: "compliance" },
    { key: "norm", label: "norma", type: "text", only: "compliance" },
    { key: "trigger", label: "o que aciona", type: "long", hint: 'em que pe\xE7a ou situa\xE7\xE3o a regra entra: "quando o post fala de vacina\xE7\xE3o"' },
    { key: "approver", label: "quem libera", type: "text", only: "compliance" },
    { key: "mandatoryText", label: "texto que sempre sai", type: "long", hint: "entra literal, por regra: o texto legal de uma norma, ou a assinatura da legenda (o que todo post deste tipo leva no fim)" },
    { key: "mandatoryPlace", label: "onde esse texto aparece", type: "choice", options: BIS_LEGAL_PLACES, hint: "advert\xEAncia (bebida, cigarro, rem\xE9dio) vai em destaque; registro e condi\xE7\xE3o v\xE3o no rodap\xE9; assinatura de legenda vai no fim da legenda" },
    { key: "sourceUrl", label: "p\xE1gina da norma", type: "text", only: "compliance" }
  ]
};
var BIS_ASSET_BLOCKS = ["logo", "icons", "pattern", "illustration", "photography", "piece", "program", "seal", "character"];
var BIS_BLOCK_ASSET_KIND = { logo: "logo", icons: "icon", pattern: "pattern", illustration: "illustration", photography: "photo", piece: "piece", typography: "font", mold: "template", program: "logo", seal: "logo", character: "illustration" };
function bisBlockAssetIds(kind, data) {
  return BIS_BLOCK_FIELDS[kind].filter((f) => f.type === "assets").flatMap((f) => (data?.[f.key] ?? []).map((x) => x.assetId)).filter((x) => typeof x === "string");
}
var bisStr = (v, max) => typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "";
function bisHex(v) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(typeof v === "string" ? v.trim() : "");
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join("") : m[1];
  return `#${h.toUpperCase()}`;
}
function normalizeBisBlock(kind, raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const data = {};
  const issues = [];
  const rows = (v) => Array.isArray(v) ? v.filter((x) => Boolean(x) && typeof x === "object") : [];
  for (const f of BIS_BLOCK_FIELDS[kind]) {
    const v = src[f.key];
    if (f.type === "text" || f.type === "long") {
      const t = bisStr(v, f.type === "text" ? 300 : 900);
      if (t) data[f.key] = t;
    } else if (f.type === "choice") {
      const t = bisStr(v, 80).toLowerCase();
      const o = f.options?.find((x) => x.key === t || x.label.toLowerCase() === t);
      if (o) data[f.key] = o.key;
    } else if (f.type === "list") {
      const l = (Array.isArray(v) ? v : []).map((x) => bisStr(x, 240)).filter(Boolean).slice(0, 24);
      if (l.length) data[f.key] = [...new Set(l)];
    } else if (f.type === "pairs") {
      const [a, b] = f.pairKeys;
      const l = rows(v).map((x) => ({ [a]: bisStr(x[a], 160), [b]: bisStr(x[b], 500) })).filter((x) => x[a]).slice(0, 24);
      if (l.length) data[f.key] = l;
    } else if (f.type === "assets") {
      const l = rows(v).map((x) => ({ assetId: bisStr(x.assetId, 36).toLowerCase(), label: bisStr(x.label, 80) })).filter((x) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(x.assetId)).slice(0, 60);
      if (l.length) data[f.key] = l;
    } else if (f.type === "colors") {
      const l = [];
      for (const x of rows(v).slice(0, 24)) {
        const hex2 = bisHex(x.hex);
        const name = bisStr(x.name, 60);
        if (!hex2) {
          issues.push(`cor ${name || "(sem nome)"}: o c\xF3digo "${bisStr(x.hex, 20)}" n\xE3o \xE9 um HEX v\xE1lido`);
          continue;
        }
        const c = { name: name || hex2, hex: hex2, role: bisStr(x.role, 160) };
        for (const k of ["pantone", "cmyk"]) {
          const t = bisStr(x[k], 60);
          if (t) c[k] = t;
        }
        l.push(c);
      }
      if (l.length) data[f.key] = l;
    } else if (f.type === "fonts") {
      const l = rows(v).map((x) => {
        const o = { family: bisStr(x.family, 80), role: bisStr(x.role, 160) };
        for (const k of ["weights", "fallback"]) {
          const t = bisStr(x[k], 120);
          if (t) o[k] = t;
        }
        return o;
      }).filter((x) => x.family).slice(0, 12);
      if (l.length) data[f.key] = l;
    }
  }
  for (const k of ["startsOn", "endsOn"]) if (typeof data[k] === "string" && !/^\d{4}-\d{2}-\d{2}$/.test(data[k])) {
    issues.push(`a data "${data[k]}" precisa estar como AAAA-MM-DD`);
    delete data[k];
  }
  if (typeof data.sourceUrl === "string" && !/^https?:\/\//i.test(data.sourceUrl)) {
    issues.push("a p\xE1gina da norma precisa ser um endere\xE7o completo (https://\u2026)");
    delete data.sourceUrl;
  }
  return { data, issues };
}
function normalizeBisApplies(raw) {
  return canonicalBisApplies(raw).applies;
}
function bisBlockText(i) {
  const kind = i.kind ?? "note";
  const d = i.data ?? {};
  const out = [i.body];
  for (const f of BIS_BLOCK_FIELDS[kind]) {
    if (f.only && f.only !== i.ruleKind) continue;
    const v = d[f.key];
    if (!v || Array.isArray(v) && !v.length) continue;
    if (typeof v === "string") out.push(`${f.label}: ${f.options?.find((o) => o.key === v)?.label ?? v}`);
    else if (f.type === "list") out.push(`${f.label}: ${v.join("; ")}`);
    else if (f.type === "pairs") out.push(`${f.label}: ${v.map((x) => `${x[f.pairKeys[0]]}${x[f.pairKeys[1]] ? ` \u2192 ${x[f.pairKeys[1]]}` : ""}`).join("; ")}`);
    else if (f.type === "assets") out.push(`${f.label}: ${v.map((x) => x.label || "arquivo").join("; ")}`);
    else if (f.type === "colors") out.push(`${f.label}: ${v.map((c) => `${c.name} ${c.hex}${c.role ? ` (${c.role})` : ""}`).join("; ")}`);
    else out.push(`${f.label}: ${v.map((x) => `${x.family}${x.role ? ` (${x.role})` : ""}`).join("; ")}`);
  }
  return out.join("\n");
}
function bisChanges(published, working) {
  const before = new Map(published.map((i) => [i.id, i]));
  const now = new Map(working.map((i) => [i.id, i]));
  const json = (v) => JSON.stringify(v ?? null);
  const same = (a, b) => a.section === b.section && a.title === b.title && a.body === b.body && a.force === b.force && a.ruleKind === b.ruleKind && a.infoClass === b.infoClass && (a.kind ?? "note") === (b.kind ?? "note") && json(a.data ?? {}) === json(b.data ?? {}) && json(a.applies) === json(b.applies);
  const added = working.filter((i) => !before.has(i.id)).map((i) => i.id);
  const changed = working.filter((i) => before.has(i.id) && !same(before.get(i.id), i)).map((i) => i.id);
  const removed = published.filter((i) => !now.has(i.id)).map((i) => i.id);
  return { added, changed, removed, total: added.length + changed.length + removed.length };
}
var BIS_VERSION_LEVELS = ["patch", "minor", "major"];
var BIS_VERSION_LEVEL_LABELS = { major: "grande", minor: "m\xE9dio", patch: "ajuste" };
var BIS_CORE_SECTIONS = ["identity", "future", "audience", "voice"];
var BIS_CONFIG_KEYS = /* @__PURE__ */ new Set(["drawer", "drawerNote", "illustrator", "illustratorNote"]);
var bisPlain = (v) => {
  if (typeof v === "string") return v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (Array.isArray(v)) return v.map(bisPlain);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).filter(([k]) => !BIS_CONFIG_KEYS.has(k)).sort(([a], [b]) => a.localeCompare(b)).map(([k, x]) => [k, bisPlain(x)]));
  return v ?? null;
};
var bisCore = (i) => BIS_CORE_SECTIONS.includes(i.section);
var bisCoreAdd = (i) => i.section === "identity" || i.section === "future";
function bisVersionLevel(published, next) {
  if (!published.length) return { level: "major", why: "a primeira vers\xE3o" };
  const before = new Map(published.map((i) => [i.id, i]));
  const rank = (l) => BIS_VERSION_LEVELS.indexOf(l);
  let level = "patch", why = "s\xF3 ajuste de texto ou de configura\xE7\xE3o";
  const raise = (l, w) => {
    if (rank(l) > rank(level)) {
      level = l;
      why = w;
    }
  };
  const { added, changed, removed } = bisChanges(published, next);
  const now = new Map(next.map((i) => [i.id, i]));
  for (const id of added) {
    const i = now.get(id);
    raise(bisCoreAdd(i) ? "major" : "minor", `entrou "${i.title}"`);
  }
  for (const id of removed) {
    const i = before.get(id);
    raise(bisCore(i) ? "major" : "minor", `saiu "${i.title}"`);
  }
  for (const id of changed) {
    const a = before.get(id), b = now.get(id);
    const same = JSON.stringify(bisPlain({ s: a.section, t: a.title, b: a.body, f: a.force, r: a.ruleKind, c: a.infoClass, k: a.kind ?? "note", d: a.data ?? {}, p: a.applies ?? null })) === JSON.stringify(bisPlain({ s: b.section, t: b.title, b: b.body, f: b.force, r: b.ruleKind, c: b.infoClass, k: b.kind ?? "note", d: b.data ?? {}, p: b.applies ?? null }));
    if (!same) raise(bisCore(a) || bisCore(b) ? "major" : "minor", `mudou "${b.title}"`);
  }
  return { level, why };
}
function bisVersionLabels(levels) {
  let M = 0, m = 0, p = 0;
  return levels.map((l, n) => {
    if (n === 0 || l === "major") {
      M += 1;
      m = 0;
      p = 0;
    } else if (l === "minor") {
      m += 1;
      p = 0;
    } else p += 1;
    return `${M}.${m}.${p}`;
  });
}
function bisNextVersionLabel(current, level) {
  const [M = 0, m = 0, p = 0] = (current ?? "").split(".").map((x) => Number(x) || 0);
  if (!current || level === "major") return `${M + 1}.0.0`;
  return level === "minor" ? `${M}.${m + 1}.0` : `${M}.${m}.${p + 1}`;
}
function bisToPublish(published, working) {
  const before = new Map(published.map((i) => [i.id, i]));
  return working.flatMap(({ held, ...i }) => !held ? [i] : before.has(i.id) ? [before.get(i.id)] : []);
}
var CONVERSATION_PURPOSES = ["audience", "future"];
var FUTURE_VISION_GOAL = `O futuro da marca, nas palavras de quem decide. Cobrir, nesta ordem:
1. Daqui a alguns anos: como a pessoa quer que a marca seja vista, por quem, e o que teria mudado. Pedir o retrato concreto, n\xE3o o slogan.
2. O que n\xE3o muda: do que a marca n\xE3o abre m\xE3o, mesmo que custe crescimento.
3. O que ela n\xE3o quer virar: marcas, posturas ou caminhos que seriam um erro.
4. O que tira o sono: o risco que mais preocupa e o que a concorr\xEAncia pode fazer.
5. Quem ela admira: marcas, de qualquer setor, que fazem algo que esta deveria fazer, e o qu\xEA exatamente.
6. A aposta: se s\xF3 pudesse fazer uma coisa pela marca nos pr\xF3ximos doze meses, qual seria, e o que deixaria de fazer.
7. O que trava: o que hoje impede a marca de chegar l\xE1, dentro e fora da empresa.`;
var ASSET_KINDS = ["photo", "video", "illustration", "icon", "logo", "pattern", "font", "template", "piece"];
var ASSET_FILE_KINDS = ["font", "template"];
var ASSET_KIND_LABELS = { photo: "foto", video: "v\xEDdeo", illustration: "ilustra\xE7\xE3o", icon: "\xEDcone", logo: "logo", pattern: "padr\xE3o ou textura", font: "arquivo de fonte", template: "molde de composi\xE7\xE3o", piece: "pe\xE7a de exemplo" };
var GALLERY_KINDS = ["photo", "video", "illustration"];
var ASSET_ROLES = ["scene", "product", "person", "object"];
var ASSET_ROLE_LABELS = { scene: "cen\xE1rio", product: "produto", person: "pessoa", object: "objeto" };
var ASSET_ROLE_HINTS = { scene: "o lugar, vazio: cozinha, balc\xE3o, estoque", product: "o produto isolado, em fundo neutro", person: "uma pessoa em fundo neutro, numa pose", object: "um objeto de apoio: etiqueta, pote, utens\xEDlio" };
var ASSET_COMPOSE_MAX = 6;
var ASSET_KEEP_ORIGINAL = ["illustration", "icon", "logo", "pattern", "font", "template"];
var ASSET_ORIGINS = ["authored", "licensed", "client", "unknown"];
var ASSET_ORIGIN_LABELS = { authored: "autoral", licensed: "licenciada", client: "enviada pelo cliente", unknown: "n\xE3o informado" };
var ASSET_ORIGIN_HINTS = { authored: "feita pela marca ou para ela, com os direitos dela", licensed: "de banco ou de terceiro, com licen\xE7a de uso", client: "o cliente mandou e garante que pode usar", unknown: "ningu\xE9m disse ainda de onde veio" };
var ASSET_PEOPLE = ["none", "released", "event", "unreleased", "unknown"];
var ASSET_PEOPLE_LABELS = { none: "sem pessoas reconhec\xEDveis", released: "pessoas com autoriza\xE7\xE3o de imagem", event: "registro de evento (pessoas sem autoriza\xE7\xE3o por escrito)", unreleased: "pessoas sem autoriza\xE7\xE3o", unknown: "n\xE3o informado" };
var ASSET_AI_EDIT = ["allowed", "forbidden", "unknown"];
var ASSET_AI_EDIT_LABELS = { allowed: "pode receber IA", forbidden: "n\xE3o pode receber IA", unknown: "n\xE3o informado" };
var ASSET_STATUSES = ["review", "official", "archived", "draft"];
var ASSET_STATUS_LABELS = { review: "a conferir", official: "oficial", archived: "arquivada", draft: "esperando aprova\xE7\xE3o de quem gerou" };
var ASSET_EDIT_FORMATS = ["1:1", "4:5", "9:16", "16:9", "3:2"];
var ASSET_EDIT_FORMAT_LABELS = { "1:1": "quadrado", "4:5": "retrato de feed", "9:16": "story e reels", "16:9": "paisagem", "3:2": "foto cl\xE1ssica" };
var ASSET_EDIT_ANCHORS = ["start", "center", "end"];
var assetFormatRatio = (f) => {
  const [w, h] = f.split(":").map(Number);
  return w / h;
};
var assetExpired = (a, today) => Boolean(a.validUntil && a.validUntil.slice(0, 10) < today.slice(0, 10));
function assetBlockers(a, today) {
  return [
    a.origin === "unknown" ? "falta dizer de onde a imagem veio" : "",
    a.people === "unknown" ? "falta dizer se h\xE1 pessoas reconhec\xEDveis e se autorizaram" : "",
    a.people === "unreleased" ? "h\xE1 pessoas sem autoriza\xE7\xE3o de imagem" : "",
    assetExpired(a, today) ? "a licen\xE7a venceu" : ""
  ].filter(Boolean);
}
var ASSET_EVENT_WARNING = "registro de evento: as pessoas n\xE3o assinaram autoriza\xE7\xE3o de imagem. Use s\xF3 para falar deste evento; em an\xFAncio ou outra campanha, pe\xE7a a autoriza\xE7\xE3o";
function assetWarnings(a) {
  return [a.people === "none" && a.reading?.hasPeople ? "a Machine v\xEA pessoas nesta imagem: confira se s\xE3o reconhec\xEDveis" : "", a.people === "event" ? ASSET_EVENT_WARNING : "", a.origin === "licensed" && !a.licenseNote ? "licenciada sem dizer qual \xE9 a licen\xE7a" : ""].filter(Boolean);
}
function assetAiEditAllowed(a, today) {
  if (a.people === "event") return { allowed: false, why: "\xE9 registro de evento com pessoas reais: a IA n\xE3o redesenha ningu\xE9m" };
  if (a.aiEdit === "forbidden") return { allowed: false, why: "est\xE1 marcada como n\xE3o pode receber IA" };
  if (a.aiEdit === "unknown") return { allowed: false, why: "ningu\xE9m disse ainda se esta imagem pode receber IA" };
  const b = assetBlockers(a, today);
  if (b.length) return { allowed: false, why: b[0] };
  if (a.people === "none" && a.reading?.hasPeople) return { allowed: false, why: "a Machine v\xEA pessoas nesta imagem e n\xE3o h\xE1 autoriza\xE7\xE3o de imagem registrada" };
  return { allowed: true, why: "direitos em ordem e edi\xE7\xE3o por IA permitida" };
}
var CREATOR_KINDS = ["photographer", "studio", "agency", "stock", "internal"];
var CREATOR_KIND_LABELS = { photographer: "fot\xF3grafo", studio: "est\xFAdio ou produtora", agency: "ag\xEAncia", stock: "banco de imagens", internal: "equipe da marca" };
var PERSON_RELATIONS = ["employee", "leadership", "customer", "model", "influencer", "partner", "other"];
var PERSON_RELATION_LABELS = { employee: "funcion\xE1rio", leadership: "s\xF3cio ou diretoria", customer: "cliente da marca", model: "modelo contratado", influencer: "influenciador", partner: "fornecedor ou parceiro", other: "outra" };
var RELEASE_STATUSES = ["none", "pending", "signed", "revoked"];
var RELEASE_STATUS_LABELS = { none: "sem autoriza\xE7\xE3o", pending: "enviada, sem assinatura", signed: "assinada", revoked: "revogada" };
var IMAGE_CONTRACT_STATUSES = ["sent", "viewed", "signed", "declined", "expired", "canceled", "failed"];
var IMAGE_CONTRACT_STATUS_LABELS = { sent: "enviado, esperando a assinatura", viewed: "aberto pela pessoa", signed: "assinado", declined: "recusado", expired: "expirou", canceled: "cancelado", failed: "n\xE3o foi enviado" };
var IMAGE_CONTRACT_OPEN = ["sent", "viewed"];
function imageContractStatusOf(envelope, recipient) {
  if (recipient === "declined") return "declined";
  if (envelope === "completed") return "signed";
  if (envelope === "expired" || recipient === "expired") return "expired";
  if (envelope === "canceled") return "canceled";
  if (envelope === "viewed" || recipient === "viewed") return "viewed";
  return "sent";
}
var IMAGE_CONTRACT_FIELDS = {
  nome: "nome de quem aparece",
  cpf: "CPF",
  email: "e-mail",
  telefone: "telefone (WhatsApp)",
  cliente: "o cliente (contratante do uso)",
  marca: "a marca",
  abrangencia: "o que a autoriza\xE7\xE3o cobre",
  validade: "at\xE9 quando vale",
  fotos: 'quais fotos e v\xEDdeos o contrato cobre ("feitos em 16/10/2026")',
  ia: "IA em cima da imagem",
  data: "data de hoje",
  cidade: "cidade do cliente"
};
function cpfValid(raw) {
  const d = raw.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  const dig = (n) => {
    let sum = 0;
    for (let i = 0; i < n; i++) sum += Number(d[i]) * (n + 1 - i);
    const r = sum * 10 % 11;
    return r === 10 ? 0 : r;
  };
  return dig(9) === Number(d[9]) && dig(10) === Number(d[10]);
}
function releaseValid(p, today) {
  return p.releaseStatus === "signed" && !(p.releaseValidUntil && p.releaseValidUntil.slice(0, 10) < today.slice(0, 10));
}
function imageContractCovers(c, takenOn, override) {
  if (override === true || override === false) return override;
  if (!c.coversFrom) return false;
  const day = takenOn.slice(0, 10);
  return day >= c.coversFrom.slice(0, 10) && (!c.coversUntil || day <= c.coversUntil.slice(0, 10));
}
function releaseForAsset(person, contracts, takenOn, overrides) {
  const live = contracts.filter((c) => c.status === "signed" || IMAGE_CONTRACT_OPEN.includes(c.status));
  if (!live.length) return { ...person, contractId: null, contractName: null, uncovered: false };
  const covering = live.filter((c) => imageContractCovers(c, takenOn, overrides.get(c.id)));
  const signed = covering.filter((c) => c.status === "signed").sort((a, b) => (b.validUntil ?? "9999-12-31").localeCompare(a.validUntil ?? "9999-12-31"))[0];
  if (signed) return { releaseStatus: "signed", releaseValidUntil: signed.validUntil, releaseAi: signed.ai, contractId: signed.id, contractName: signed.templateName, uncovered: false };
  const waiting = covering[0];
  if (waiting) return { releaseStatus: "pending", releaseValidUntil: null, releaseAi: "unknown", contractId: waiting.id, contractName: waiting.templateName, uncovered: false };
  return { releaseStatus: "none", releaseValidUntil: null, releaseAi: "unknown", contractId: null, contractName: null, uncovered: true };
}
function assetRightsWithPeople(a, people, today) {
  if (!people.length) return a;
  const ok = people.every((p) => releaseValid(p, today));
  const ai = a.aiEdit === "forbidden" || people.some((p) => p.releaseAi === "forbidden") ? "forbidden" : a.aiEdit === "allowed" && people.every((p) => p.releaseAi === "allowed") ? "allowed" : "unknown";
  return { ...a, people: ok ? "released" : a.people === "event" ? "event" : "unreleased", aiEdit: ai };
}
var FACE_SIGNATURE_DIMENSIONS = 128;
var FACE_MATCH_MIN = 0.42;
var FACE_CLUSTER_MIN = 0.5;
var FACE_STATUSES = ["open", "suggested", "confirmed", "dismissed"];
function faceOverlap(a, b) {
  const x = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const y = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const inter = x * y;
  const union = a.w * a.h + b.w * b.h - inter;
  return union > 0 ? inter / union : 0;
}
function faceSignatureAllowed(p) {
  return p.biometricConsent && p.releaseStatus !== "revoked";
}
var SECTOR_NORM_KINDS = ["forbidden", "required", "care"];
var SECTOR_NORM_KIND_LABELS = { forbidden: "pro\xEDbe", required: "obriga", care: "pede cuidado" };
var FUTURE_PROFILE_NAME = "Diretoria: vis\xE3o de futuro";
var LAB_STATUSES = ["open", "closed"];
var LAB_ITEM_KINDS = ["proposal", "exploration", "reference", "research", "note", "message"];
var LAB_ITEM_KIND_LABELS = { proposal: "proposta", exploration: "estudo", reference: "refer\xEAncia", research: "pesquisa", note: "anota\xE7\xE3o", message: "conversa" };
var LAB_JUDGEABLE = ["proposal", "exploration"];
var LAB_EXPLORE_ESTIMATE_USD = 0.2;
var LAB_TALK_ESTIMATE_USD = 0.1;
var LAB_TALK_CONTEXT_MAX = 30;
var LAB_VISUAL_QUESTION = "Numa sala de estudo de uma ag\xEAncia, algu\xE9m escreveu este pedido. Ele pede uma IMAGEM (uma cena, uma embalagem, uma foto, uma composi\xE7\xE3o visual para ver)? Responda n\xE3o quando for uma pergunta, uma d\xFAvida, um pedido de dados, uma decis\xE3o de nome, de arquitetura de marca, de estrat\xE9gia ou de texto.";
var LAB_VISUAL_MIN = 0.3;
function labAskLooksLikeQuestion(ask) {
  const t = ask.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  const visual = /\b(imagem|imagens|foto|fotos|cena|embalage\w*|mockup|layout|arte|visual|ilustra\w*|render|composic\w*|prato|angulo|enquadra\w*|fundo|cor|cores|logo)\b/.test(t);
  const asks = t.includes("?") || /\b(temos dados|faz sentido|vale a pena|devemos|deveriamos|o que acha|qual o melhor|por que|porque)\b/.test(t);
  return asks && !visual;
}
var LAB_ITEM_STATUSES = ["pending", "ready", "failed"];
var LAB_VERDICTS = ["keep", "maybe", "drop"];
var LAB_VERDICT_LABELS = { keep: "segue", maybe: "talvez", drop: "descarta" };
var LAB_OPTIONS_MAX = 4;
var LAB_INPUTS_MAX = 4;
var LAB_PROPOSAL_ESTIMATE_USD = 0.1;
var LAB_RESEARCH_ESTIMATE_USD = 0.5;
var LAB_SHELF_ESTIMATE_USD = 1;
var LAB_SHELF_MAX = 14;
var RESEARCH_PROFILE_ESTIMATE_USD = 0.3;
var RESEARCH_DOSSIER_ESTIMATE_USD = 0.65;
function labTally(verdicts) {
  const keep = verdicts.filter((v) => v === "keep").length, drop = verdicts.filter((v) => v === "drop").length;
  return { keep, maybe: verdicts.length - keep - drop, drop, split: keep > 0 && drop > 0 };
}
var PRODUCT_KINDS = ["product", "service"];
var PRODUCT_KIND_LABELS = { product: "produto", service: "servi\xE7o" };
var PRODUCT_FIELD_LABELS = {
  product: { composition: "composi\xE7\xE3o", labelRules: "o que a embalagem tem de dizer", usage: "uso", audience: "p\xFAblico", channels: "onde vende", variants: "varia\xE7\xF5es", variant: "varia\xE7\xE3o" },
  service: { composition: "o que inclui e como funciona", labelRules: "o que a comunica\xE7\xE3o tem de dizer", usage: "quando a pessoa usa", audience: "para quem \xE9", channels: "onde e como se contrata", variants: "modalidades", variant: "modalidade" }
};
var PRODUCT_STATUSES = ["active", "development", "discontinued"];
var PRODUCT_STATUS_LABELS = { active: "em linha", development: "em desenvolvimento", discontinued: "fora de linha" };
var PRODUCT_FILE_KINDS = ["mockup", "art", "photo", "document"];
var PRODUCT_FILE_KIND_LABELS = { mockup: "mockup", art: "arte da embalagem", photo: "foto do produto", document: "documento" };
var SHELF_STATUSES = ["current", "old", "noise"];
var SHELF_STATUS_LABELS = { current: "atual", old: "vers\xE3o antiga", noise: "n\xE3o \xE9 dele" };
var ASSET_PRODUCT_MIN = 0.6;
function dimensionsText(d) {
  if (!d) return null;
  const n = (v) => v && v > 0 ? `${String(Math.round(v * 10) / 10).replace(".", ",")} cm` : null;
  const parts = [n(d.widthCm) && `${n(d.widthCm)} de largura`, n(d.heightCm) && `${n(d.heightCm)} de altura`, n(d.depthCm) && `${n(d.depthCm)} de profundidade`].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}
var parseCm = (v) => {
  const n = typeof v === "number" ? v : Number(String(v ?? "").trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 && n < 1e3 ? Math.round(n * 10) / 10 : null;
};
var ASSET_AUDIT_VERDICTS = ["ok", "attention", "violation"];
var ASSET_AUDIT_VERDICT_LABELS = { ok: "dentro", attention: "aten\xE7\xE3o", violation: "fere a regra" };
function assetAuditBlockers(audit) {
  if (!audit || audit.status !== "ready" || audit.justification) return [];
  return (audit.findings ?? []).filter((f) => f.verdict === "violation" && f.ruleKind === "compliance");
}
var NETWORK_KINDS = ["creator", "influencer", "ugc", "specialist", "chef", "photographer", "athlete", "architect", "journalist", "artist", "press", "partner", "ambassador", "model", "other"];
var NETWORK_KIND_LABELS = { creator: "creator", influencer: "influenciador", ugc: "UGC creator", specialist: "especialista", chef: "chef", photographer: "fot\xF3grafo", athlete: "atleta", architect: "arquiteto", journalist: "jornalista", artist: "artista", press: "imprensa", partner: "parceiro", ambassador: "embaixador", model: "modelo", other: "outro" };
var NETWORK_STATUSES = ["active", "paused", "avoid"];
var NETWORK_STATUS_LABELS = { active: "ativo", paused: "em pausa", avoid: "n\xE3o trabalhar" };
var NETWORK_PLATFORMS = ["instagram", "tiktok", "youtube", "linkedin", "facebook", "x", "kwai", "twitch", "pinterest", "podcast", "blog", "other"];
var NETWORK_PLATFORM_LABELS = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", linkedin: "LinkedIn", facebook: "Facebook", x: "X", kwai: "Kwai", twitch: "Twitch", pinterest: "Pinterest", podcast: "podcast", blog: "blog ou site", other: "outro" };
var NETWORK_TIERS = ["nano", "micro", "mid", "macro", "mega"];
var NETWORK_TIER_LABELS = { nano: "nano (at\xE9 10 mil)", micro: "micro (10 a 100 mil)", mid: "m\xE9dio (100 a 500 mil)", macro: "macro (500 mil a 1 milh\xE3o)", mega: "mega (mais de 1 milh\xE3o)" };
function networkTier(platforms) {
  const top = Math.max(0, ...(platforms ?? []).map((p) => p.followers ?? 0));
  return top <= 0 ? null : top < 1e4 ? "nano" : top < 1e5 ? "micro" : top < 5e5 ? "mid" : top < 1e6 ? "macro" : "mega";
}
var formatFollowers = (n) => !n ? "" : n >= 1e6 ? `${String(Math.round(n / 1e5) / 10).replace(".", ",")} mi` : n >= 1e3 ? `${String(Math.round(n / 100) / 10).replace(".", ",")} mil` : String(n);
var NETWORK_EVENT_KINDS = ["contact", "negotiation", "delivery", "result", "problem", "feedback", "note"];
var NETWORK_EVENT_KIND_LABELS = { contact: "contato", negotiation: "negocia\xE7\xE3o", delivery: "entrega", result: "resultado", problem: "problema", feedback: "avalia\xE7\xE3o da equipe", note: "anota\xE7\xE3o" };
var NETWORK_SENTIMENTS = ["positive", "neutral", "negative"];
var NETWORK_SENTIMENT_LABELS = { positive: "bom", neutral: "neutro", negative: "ruim" };
var CREATOR_STAGES = ["discovered", "evaluating", "shortlist", "contact", "negotiation", "hired", "production", "approval", "published", "measured", "dropped"];
var CREATOR_STAGE_LABELS = { discovered: "descobertos", evaluating: "avaliando", shortlist: "shortlist", contact: "contato", negotiation: "negocia\xE7\xE3o", hired: "contratado", production: "produ\xE7\xE3o", approval: "aprova\xE7\xE3o", published: "publicado", measured: "medido", dropped: "fora" };
var CREATOR_RISK_LEVELS = ["low", "medium", "high"];
var CREATOR_RISK_LABELS = { low: "baixo", medium: "m\xE9dio", high: "alto" };
var fitBand = (score) => score >= 80 ? "strong" : score >= 60 ? "good" : "weak";
function creatorConflicts(brandsWorked, rivals) {
  const norm = (t) => ` ${t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;
  const worked = brandsWorked.map(norm);
  return rivals.filter((r) => [r.name, ...r.aliases ?? []].map(norm).filter((t) => t.trim().length >= 3).some((t) => worked.some((w) => w.includes(t)))).map((r) => r.name);
}
var CAMPAIGN_STATUSES = ["planning", "running", "done"];
var CAMPAIGN_STATUS_LABELS = { planning: "planejando", running: "no ar", done: "encerrada" };
var CAMPAIGN_RESULT_FIELDS = [
  { key: "cost", label: "custo, R$" },
  { key: "views", label: "visualiza\xE7\xF5es" },
  { key: "engagementPct", label: "engajamento, %" },
  { key: "saves", label: "salvamentos" },
  { key: "clicks", label: "cliques" },
  { key: "conversions", label: "convers\xF5es" }
];
var campaignResultFilled = (r) => Boolean(r && (CAMPAIGN_RESULT_FIELDS.some((f) => r[f.key] != null) || r.note));
function campaignEfficiency(r) {
  const cost = r?.cost ?? null;
  const per = (n, k = 1) => cost != null && n ? Math.round(cost / n * k * 100) / 100 : null;
  return { perThousandViews: per(r?.views, 1e3), perClick: per(r?.clicks), perConversion: per(r?.conversions) };
}
function campaignResultText(campaign, role, r) {
  const n = (v) => v.toLocaleString("pt-BR");
  const e = campaignEfficiency(r);
  const parts = [
    r.views != null ? `${n(r.views)} visualiza\xE7\xF5es` : "",
    r.engagementPct != null ? `engajamento de ${n(r.engagementPct)}%` : "",
    r.saves != null ? `${n(r.saves)} salvamentos` : "",
    r.clicks != null ? `${n(r.clicks)} cliques` : "",
    r.conversions != null ? `${n(r.conversions)} convers\xF5es` : "",
    e.perConversion != null ? `R$ ${n(e.perConversion)} por convers\xE3o` : e.perClick != null ? `R$ ${n(e.perClick)} por clique` : e.perThousandViews != null ? `R$ ${n(e.perThousandViews)} por mil visualiza\xE7\xF5es` : ""
  ].filter(Boolean);
  return `Campanha "${campaign}"${role ? ` (${role})` : ""}: ${parts.join(", ") || "sem n\xFAmero informado"}.${r.note ? ` ${r.note.trim()}` : ""}`;
}
var handleKey = (h) => (h ?? "").trim().replace(/^@+/, "").replace(/[.]+$/, "").toLowerCase();
function extractMentions(caption) {
  const out = /* @__PURE__ */ new Set();
  for (const m of (caption ?? "").matchAll(/(?<![\w.@])@([A-Za-z0-9_](?:[A-Za-z0-9_.]{1,28})[A-Za-z0-9_])/g)) out.add(handleKey(m[1]));
  return [...out];
}
var DISCOVERY_WINDOW_DAYS = 180;
var PROFILE_READ_NETWORKS = ["instagram", "tiktok"];
var PROFILE_READ_POSTS = 12;
var NETWORK_PROFILE_ESTIMATE_USD = 0.08;
var NETWORK_PROFILE_VISUAL_ESTIMATE_USD = 0.3;
var PROFILE_VISUAL_IMAGES = 8;
var PROFILE_VISUAL_VIDEOS = 3;
var isSponsoredCaption = (caption) => /(^|[\s#*(])(publi(cidade)?|publipost|ad|ads|parceria ?paga|parceriapaga|patrocinado|sponsored)(?=$|[\s.,!)*:])/i.test(caption ?? "");
function mentionConflicts(mentions, rivals) {
  const seen = new Set((mentions ?? []).map((m) => handleKey(m.handle)));
  if (!seen.size) return [];
  return rivals.filter((r) => SOCIAL_NETWORKS.some((net) => socialHandles(net, r.socials?.[net]).some((h) => seen.has(handleKey(h))))).map((r) => r.name);
}
var wellFormed = (t) => t.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "");
var isBenchmark = (c) => c.kind === "reference" && Boolean(c.referenceSegment?.trim());
var isRival = (c) => c.kind !== "self" && !isBenchmark(c);
var PULSE_IG_METRICS = {
  ig_seguidores: { label: "seguidores", additive: false, snapshot: true },
  ig_alcance: { label: "alcance", additive: false, snapshot: false },
  ig_visualizacoes: { label: "visualiza\xE7\xF5es", additive: true, snapshot: false },
  ig_contas_engajadas: { label: "contas engajadas", additive: false, snapshot: false },
  ig_interacoes: { label: "intera\xE7\xF5es", additive: true, snapshot: false },
  ig_curtidas: { label: "curtidas", additive: true, snapshot: false },
  ig_comentarios: { label: "coment\xE1rios", additive: true, snapshot: false },
  ig_salvamentos: { label: "salvamentos", additive: true, snapshot: false },
  ig_compartilhamentos: { label: "compartilhamentos", additive: true, snapshot: false },
  ig_toques_no_perfil: { label: "toques nos links do perfil", additive: true, snapshot: false }
};
var PULSE_ACCESS_WARN_DAYS = 14;
var DEMAND_ORIGINS = ["initiative", "routine", "client_request"];
var DEMAND_ORIGIN_LABELS = { initiative: "iniciativa", routine: "rotina da conta", client_request: "pedido do cliente" };
var DEMAND_STATUSES = ["requested", "planned", "in_progress", "done", "dropped"];
var DEMAND_STATUS_LABELS = { requested: "pedido", planned: "planejada", in_progress: "em andamento", done: "conclu\xEDda", dropped: "descartada" };
var DEMAND_OPEN = ["requested", "planned", "in_progress"];
var DEMAND_BALLS = ["raro", "client", "third_party"];
var DEMAND_BALL_LABELS = { raro: "Raro", client: "cliente", third_party: "terceiros" };
var DEMAND_PENDING_STATUSES = ["open", "answered", "dropped"];
var DEMAND_PENDING_STATUS_LABELS = { open: "aberta", answered: "respondida", dropped: "dispensada" };
var DEMAND_PENDING_MAX = 12;
var DEMAND_PENDING_TEXT_MAX = 400;
var DEMAND_PENDING_REASON_MIN = 8;
var CONTACT_VIAS = [{ value: "WhatsApp", label: "WhatsApp" }, { value: "reuni\xE3o", label: "Reuni\xE3o" }, { value: "telefone", label: "Telefone" }, { value: "e-mail", label: "E-mail" }];
var viaKey = (text) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]/g, "");
function contactVia(text) {
  const key = viaKey(text ?? "");
  return key ? CONTACT_VIAS.find((v) => viaKey(v.value) === key)?.value ?? null : null;
}
var DEMAND_REVISION_STATUSES = ["waiting", "reading", "ready", "failed"];
var REVISION_ITEM_STATUSES = ["open", "done", "ask", "dropped"];
var REVISION_ITEM_LABELS = { open: "em aberto", done: "feito", ask: "perguntar ao cliente", dropped: "n\xE3o vamos fazer" };
var REVISION_VIAS = ["whatsapp", "email", "reuniao", "telefone", "ritmo", "outro"];
var REVISION_VIA_LABELS = { whatsapp: "WhatsApp", email: "e-mail", reuniao: "reuni\xE3o", telefone: "telefone", ritmo: "Ritmo", outro: "outro" };
var REVISION_READING_MAX = 6e4;
var REVISION_ITEMS_MAX = 80;
var REVISION_ESTIMATE_USD = 0.15;
function demandOverdue(d, now = /* @__PURE__ */ new Date()) {
  return Boolean(d.dueAt && DEMAND_OPEN.includes(d.status) && new Date(d.dueAt).getTime() < now.getTime());
}
var PANORAMA_VERDICTS = ["go", "adjust", "talk"];
var PANORAMA_VERDICT_LABELS = { go: "D\xE1 para fazer como pedido", adjust: "Vale fazer diferente", talk: "Conversar com o cliente antes" };
var PANORAMA_TONES = ["ok", "attention", "conflict"];
var PANORAMA_POINT_KINDS = ["brand", "product", "history", "result", "direction", "duplicate", "asset", "market", "reference"];
var PANORAMA_POINT_LABELS = { brand: "O que a marca diz", product: "Produto", history: "J\xE1 foi feito", result: "Como foi da \xFAltima vez", direction: "O que est\xE1 em jogo", duplicate: "J\xE1 est\xE1 pedido", asset: "J\xE1 existe na galeria", market: "O que os concorrentes fazem", reference: "O que veio de refer\xEAncia" };
var PANORAMA_REF_TYPES = ["bis", "product", "deliverable", "demand", "post", "initiative", "hypothesis", "asset", "competitor", "reference"];
var PANORAMA_STATUSES = ["none", "pending", "ready", "failed"];
var PANORAMA_ESTIMATE_USD = 0.15;
var TEAM_FUNCTION_DEFAULTS = [
  { key: "operacao", name: "Opera\xE7\xE3o", description: "distribui o trabalho da casa e confirma o percurso de cada demanda" },
  { key: "atendimento", name: "Atendimento", description: "fala com o cliente: entende o pedido, tira d\xFAvidas, devolve e acompanha" },
  { key: "gerente_de_contas", name: "Gerente de contas", description: "responde pela conta: prioridade, escopo, o que \xE9 avulso" },
  { key: "planejamento", name: "Planejamento", description: "define o caminho quando o pedido pede conceito, estrat\xE9gia ou mudan\xE7a de dire\xE7\xE3o" },
  { key: "redacao", name: "Reda\xE7\xE3o", description: "escreve: t\xEDtulo, legenda, roteiro, texto de pe\xE7a" },
  { key: "direcao_de_arte", name: "Dire\xE7\xE3o de arte", description: "define e desenha o visual da pe\xE7a" },
  { key: "edicao_de_video", name: "Edi\xE7\xE3o de v\xEDdeo", description: "monta e finaliza v\xEDdeo: corte, legenda, trilha" },
  { key: "arte_final", name: "Arte-final", description: "fecha o arquivo: formatos, adapta\xE7\xF5es, sa\xEDda para impress\xE3o ou canal" },
  { key: "social_media", name: "Social media", description: "programa, publica e acompanha nas redes" },
  { key: "gestao_de_trafego", name: "Gest\xE3o de tr\xE1fego", description: "sobe e ajusta m\xEDdia paga" },
  { key: "aprovacao_interna", name: "Aprova\xE7\xE3o interna", description: "aprova pela Raro antes de a pe\xE7a ir ao cliente" },
  { key: "financeiro", name: "Financeiro", description: "lan\xE7a, cobra avulso e paga terceiros" }
];
var TEAM_FUNCTION_OPERATIONS = "operacao";
var TEAM_FUNCTION_GATES = ["aprovacao_interna"];
var TEAM_FUNCTION_MACHINE = ["revisao"];
var STEP_STATUSES = ["proposed", "waiting", "active", "done", "skipped"];
var STEP_STATUS_LABELS = { proposed: "proposta", waiting: "aguardando", active: "agora", done: "conclu\xEDda", skipped: "pulada" };
var ROUTE_STATUSES = ["none", "pending", "proposed", "confirmed", "failed"];
var ROOM_FUNCTIONS = ["planejamento", "redacao"];
var roomKindOf = (functionKey) => functionKey === "planejamento" ? "plan" : functionKey === "redacao" ? "write" : null;
var ROOM_LABELS = {
  plan: { name: "Planejamento", open: "Planejar com a Machine", reopen: "Abrir a sala de planejamento" },
  write: { name: "Reda\xE7\xE3o", open: "Escrever com a Machine", reopen: "Abrir a sala de reda\xE7\xE3o" }
};
var PLAN_OUTLINE_LABELS = { objective: "Objetivo", idea: "Ideia central", format: "Formato", size: "Tamanho", message: "Mensagem", line: "Linha" };
var ROOM_PIN_KINDS = ["asset", "product"];
var ROOM_PIN_LABELS = { asset: "da galeria", product: "produto" };
var ROOM_PINS_MAX = 12;
var EMPTY_OUTLINE = { objective: "", idea: "", format: "", size: "", message: "", line: "", pieces: [], open: [] };
var ROOM_MESSAGES_MAX = 80;
var ROOM_TURN_ESTIMATE_USD = 0.06;
var ROOM_WRITE_ESTIMATE_USD = 0.25;
var ROOM_RESEARCH_ESTIMATE_USD = 0.4;
var ROOM_DOC_ESTIMATE_USD = 0.1;
var ROUTE_ESTIMATE_USD = 0.15;
function currentStage(steps) {
  const open = steps.filter((s) => s.status !== "done" && s.status !== "skipped");
  return open.length ? Math.min(...open.map((s) => s.stage)) : null;
}
var CHANNEL_PUBLISHERS = ["raro", "client"];
var CHANNEL_PUBLISHER_LABELS = { raro: "a Raro publica", client: "o cliente publica" };
var DEMAND_FILE_KINDS = ["image", "audio", "video", "document", "other"];
var DEMAND_FILE_KIND_LABELS = { image: "imagem", audio: "\xE1udio", video: "v\xEDdeo", document: "documento", other: "arquivo" };
function demandFileKind(mime, name) {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(ext)) return "image";
  if (mime.startsWith("audio/") || ["mp3", "m4a", "wav", "ogg", "opus", "mpga"].includes(ext)) return "audio";
  if (mime.startsWith("video/") || ["mp4", "mov", "m4v", "webm"].includes(ext)) return "video";
  if (mime === "application/pdf" || mime.startsWith("text/") || ["pdf", "docx", "pptx", "xlsx", "csv", "txt", "md"].includes(ext)) return "document";
  return "other";
}
var STEP_DEFAULT_HOURS = 2;
var APPROVAL_STATUSES = ["open", "approved", "changes", "cancelled"];
var APPROVAL_STATUS_LABELS = { open: "com o cliente", approved: "aprovada", changes: "ajuste pedido", cancelled: "retirada" };
var APPROVAL_JUSTIFICATION_MIN = 12;
var CHANGE_VERDICTS = ["fits", "partly", "against"];
var CHANGE_VERDICT_LABELS = { fits: "O ajuste cabe", partly: "Cabe em parte", against: "Esbarra na marca ou no combinado" };
var DEMAND_ITEM_STATUSES = ["planned", "in_progress", "ready", "with_client", "approved", "changes", "dropped"];
var DEMAND_ITEM_STATUS_LABELS = { planned: "planejado", in_progress: "em produ\xE7\xE3o", ready: "pronto", with_client: "com o cliente", approved: "aprovado", changes: "ajuste pedido", dropped: "fora" };
var DEMAND_ITEM_CLIENT_VISIBLE = ["with_client", "approved", "changes"];
var DEMAND_ITEMS_MAX = 40;
var ITEMS_ESTIMATE_USD = 0.12;
var DRIVE_LINK = /https?:\/\/(?:drive|docs)\.google\.com\/[^\s<>"')\]]+/gi;
function driveLinks(text) {
  const found = (text ?? "").match(DRIVE_LINK) ?? [];
  return [...new Set(found.map((u) => u.replace(/[.,;:!?]+$/, "")))].slice(0, 5);
}
var ITEM_PLACEMENTS = ["feed", "story"];
var ITEM_PLACEMENT_LABELS = { feed: "Feed", story: "Story" };
var ITEM_PLACEMENT_FIELDS = [
  { key: "title", label: "T\xEDtulo na arte" },
  { key: "sub", label: "Subt\xEDtulo", only: "story" },
  { key: "interaction", label: "Intera\xE7\xE3o (enquete, caixinha, emoji)", only: "story" },
  { key: "seal", label: "Selo" },
  { key: "slides", label: "Telas do carrossel (uma por linha)", only: "feed" },
  { key: "coverTitle", label: "T\xEDtulo da capa (v\xEDdeo)", only: "feed" },
  { key: "lettering", label: "Lettering (v\xEDdeo)", only: "feed" }
];
var TAG_VOTES = ["yes", "no"];
var TAG_RULES = ["majority", "veto", "decider"];
var TAG_RULE_LABELS = { majority: "Maioria decide (empate \xE9 n\xE3o)", veto: "Quem tem veto barra; no resto, a maioria", decider: "Uma pessoa decide; os outros opinam" };
var TAG_STATUSES = ["pending", "approved", "declined"];
var TAG_REVIEW_DEFAULT = { on: false, rule: "veto", hour: 9 };
var TAG_SUPERFAN_30D = 5;
var TAG_PROFILE_PAID_MAX = 8;
function tagResult(rule, reviewers, votes) {
  const mine = votes.filter((v) => reviewers.some((r) => r.id === v.reviewerId));
  if (reviewers.length === 0) return null;
  if (rule === "decider") {
    const d = reviewers.find((r) => r.decides);
    const v = d ? mine.find((x) => x.reviewerId === d.id) : null;
    if (v) return v.vote === "yes" ? "approved" : "declined";
    if (d) return null;
  }
  if (rule === "veto" && mine.some((v) => v.vote === "no" && reviewers.find((r) => r.id === v.reviewerId)?.veto)) return "declined";
  const yes = mine.filter((v) => v.vote === "yes").length, no = mine.length - yes, left = reviewers.length - mine.length;
  if (left === 0) return yes > no ? "approved" : "declined";
  if (rule === "veto") return null;
  if (yes > no + left) return "approved";
  if (no >= yes + left) return "declined";
  return null;
}
var bisLegalTexts = (items) => (items ?? []).map((i) => String(i.data?.mandatoryText ?? "").trim()).filter((t) => t.length >= 4);
function withoutLegalText(text, legal) {
  let out = text;
  for (const t of legal) out = out.replace(new RegExp(t.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+"), "gi"), "");
  return out.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}
var COMMENT_REPLY_MAX = 600;
var INBOX_STATUSES = ["open", "answered", "ignored"];
var INBOX_WINDOW_HOURS = 24;
var INBOX_REPLY_ESTIMATE_USD = 0.08;
var ITEM_PUBLISH_STATUSES = ["scheduled", "publishing", "manual", "published", "failed"];
function outsideWorkHours(when) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Sao_Paulo", weekday: "short", hour: "2-digit", hour12: false }).formatToParts(new Date(when));
  const day = parts.find((p) => p.type === "weekday")?.value ?? "";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  return day === "Sat" || day === "Sun" || hour < 8 || hour >= 18;
}
function itemPublishMoment(publishAt, publishTime) {
  if (!publishAt) return null;
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(publishAt));
  const m = /^(\d{1,2})[:h](\d{2})?/.exec((publishTime ?? "").trim());
  const hh = String(m ? Math.min(23, Number(m[1])) : 10).padStart(2, "0"), mm = String(m?.[2] ? Math.min(59, Number(m[2])) : 0).padStart(2, "0");
  return /* @__PURE__ */ new Date(`${day}T${hh}:${mm}:00-03:00`);
}
var APPROVAL_MARKS_MAX = 20;
function markTime(sec) {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
function markWhere(m) {
  return [m.plain ? "Na pe\xE7a" : ITEM_PLACEMENT_LABELS[m.placement], m.pages > 1 && m.page ? `tela ${m.page}` : "", m.timeSec != null ? `aos ${markTime(m.timeSec)}` : ""].filter(Boolean).join(", ");
}
function itemFullText(i) {
  const parts = [i.text?.trim() ? `LEGENDA:
${i.text.trim()}` : ""];
  for (const p of i.placements ?? []) {
    const fields = ITEM_PLACEMENT_FIELDS.map((f) => p[f.key]?.trim() ? `${f.label}: ${p[f.key].trim()}` : "").filter(Boolean);
    if (fields.length) parts.push(`${ITEM_PLACEMENT_LABELS[p.key].toUpperCase()}:
${fields.join("\n")}`);
  }
  return parts.filter(Boolean).join("\n\n");
}
var DRIVE_JOB_FOLDERS = { references: "1 Refer\xEAncias", work: "2 Trabalho", proof: "3 Prova", approved: "4 Aprovados" };
var fileSlug = (text, max = 24) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, max).replace(/-+$/, "");
var DEMAND_SCOPES = ["monthly", "job"];
var DEMAND_SCOPE_LABELS = { monthly: "Cronograma", job: "Job" };
var DRIVE_MONTHLY_FOLDER = "_escopo mensal";
var DRIVE_GROUP_FOLDER = "_grupo";
var DRIVE_PRIVATE_FOLDER = "_contratos e financeiro";
var MONTHS_PT = ["Janeiro", "Fevereiro", "Mar\xE7o", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
function demandMonthLabel(month) {
  const [year, m] = month.split("-");
  return `${MONTHS_PT[Number(m) - 1] ?? m} ${year}`;
}
function monthKey(when, plus = 0) {
  const [y, m] = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" }).format(when).split("-").map(Number);
  const n = y * 12 + (m - 1) + plus;
  return `${Math.floor(n / 12)}-${String(n % 12 + 1).padStart(2, "0")}`;
}
var DEMAND_SHORT_NAME_MAX = 60;
function demandShortNameByRule(title) {
  const words = title.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().split(" ");
  let out = "";
  for (const w of words.slice(0, 9)) {
    if (`${out} ${w}`.trim().length > DEMAND_SHORT_NAME_MAX) break;
    out = `${out} ${w}`.trim();
  }
  return (out || title.trim().slice(0, DEMAND_SHORT_NAME_MAX)).replace(/\s+(de|da|do|das|dos|para|e|com|em|a|o)$/i, "");
}
var folderSafe = (text) => text.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, DEMAND_SHORT_NAME_MAX);
function driveMonthlyFolderName(month, shortName) {
  return { year: month.slice(0, 4), name: `${demandMonthLabel(month)} - ${folderSafe(shortName || "Cronograma")}` };
}
var jobNumber = (n) => String(n).padStart(4, "0");
var DEMAND_SCOPE_QUESTION = "Uma ag\xEAncia de comunica\xE7\xE3o recebeu esta demanda de um cliente. Ela \xE9 a produ\xE7\xE3o recorrente de conte\xFAdo do m\xEAs para as redes sociais (pauta do m\xEAs, cronograma de posts, calend\xE1rio editorial, conte\xFAdos do escopo mensal)? Responda n\xE3o quando for um trabalho avulso: campanha, lan\xE7amento, evento, embalagem, site, v\xEDdeo institucional, material impresso, identidade visual, ou uma pe\xE7a \xFAnica.";
var DEMAND_SCOPE_SURE = 0.75;
function demandScopeByRule(d) {
  const text = `${d.title} ${d.description ?? ""}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const monthly = /escopo mensal|\bpauta\b|cronograma|calendario editorial|planejamento mensal|(conteudos?|posts?|publicacoes) (do|de|para o?) ?(mes|janeiro|fevereiro|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)/.exec(text);
  if (monthly) return { scope: "monthly", why: `o pedido fala em \u201C${monthly[0].trim()}\u201D` };
  const job = /campanha|lancamento|\bevento\b|\bfeira\b|embalagem|rotulo|\bsite\b|landing|institucional|catalogo|identidade visual|\blogo\b|impress[oa]|outdoor|convite/.exec(text);
  if (job) return { scope: "job", why: `o pedido fala em \u201C${job[0].trim()}\u201D` };
  if ((d.items ?? 0) >= 6) return { scope: "monthly", why: `s\xE3o ${d.items} conte\xFAdos numa demanda s\xF3` };
  return null;
}
function driveJobFolderName(number, shortName) {
  return `${jobNumber(number)} - ${folderSafe(shortName) || "job"}`;
}
function proofFileName(number, title, placement, piece = {}, page) {
  const size = placement === "story" ? "1080x1920px" : piece.width && piece.height ? pieceSizeText({ w: piece.width, h: piece.height }, piece.unit || "px") : "1080x1350px";
  const ext = piece.family === "video" || /v[ií]deo|reel|anima/i.test(piece.format ?? "") ? "mp4" : (piece.family === "print" || piece.family === "document") && piece.unit && piece.unit !== "px" ? "pdf" : "png";
  return `cont-${String(number).padStart(2, "0")}-${fileSlug(title) || "conteudo"}_${size}${page ? `_${String(page).padStart(2, "0")}` : ""}.${ext}`;
}
function parseProofName(name) {
  const n = /cont[-_ ]?(\d{1,3})/i.exec(name);
  const size = /(\d{3,5})\s*x\s*(\d{3,5})/i.exec(name);
  const page = /_(\d{1,2})\.[a-z0-9]+$/i.exec(name);
  const story = size ? Number(size[2]) / Number(size[1]) > 1.6 : /story|stories/i.test(name);
  return { number: n ? Number(n[1]) : null, placement: story ? "story" : "feed", page: page ? Number(page[1]) : null };
}
var CAMPAIGN_KINDS = ["continuous", "window"];
var CAMPAIGN_KIND_LABELS = { continuous: "Cont\xEDnua", window: "Janela" };
var CAMPAIGN_LINE_KINDS = ["media", "creators", "production"];
var CAMPAIGN_LINE_LABELS = { media: "M\xEDdia", creators: "Network", production: "Produ\xE7\xE3o" };
var MEDIA_PLATFORMS = ["meta", "google", "tiktok", "linkedin", "youtube", "other"];
var MEDIA_PLATFORM_LABELS = { meta: "Meta", google: "Google", tiktok: "TikTok", linkedin: "LinkedIn", youtube: "YouTube", other: "Outra" };
var CAMPAIGN_SPEND_ORIGINS = ["human", "collected"];
var CAMPAIGN_PACE_TOLERANCE = 15;
function campaignPace(input) {
  if (!(input.planned > 0)) return null;
  const now = input.now ?? /* @__PURE__ */ new Date();
  let from, to;
  if (input.kind === "continuous") {
    from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  } else {
    if (!input.startsAt || !input.endsAt || input.endsAt <= input.startsAt) return null;
    from = input.startsAt;
    to = new Date(input.endsAt.getTime() + 864e5);
  }
  if (now < from) return null;
  const elapsedPct = Math.round(Math.min(1, (now.getTime() - from.getTime()) / (to.getTime() - from.getTime())) * 100);
  const spentPct = Math.round(input.spent / input.planned * 100);
  const diff = spentPct - elapsedPct;
  return { elapsedPct, spentPct, state: diff > CAMPAIGN_PACE_TOLERANCE ? "over" : diff < -CAMPAIGN_PACE_TOLERANCE ? "under" : "on" };
}
var CAMPAIGN_PACE_LABELS = { under: "gastando menos que o previsto", on: "no ritmo", over: "gastando mais r\xE1pido que o previsto" };
var AD_LOW_BALANCE_DAYS = 3;
var AD_ACCOUNT_STATUS = {
  1: { label: "ativa", level: "ok" },
  2: { label: "desativada", level: "error" },
  3: { label: "pagamento pendente", level: "error" },
  7: { label: "em an\xE1lise de risco", level: "warn" },
  8: { label: "aguardando acerto de pagamento", level: "error" },
  9: { label: "em per\xEDodo de car\xEAncia", level: "warn" },
  100: { label: "encerramento pendente", level: "error" },
  101: { label: "encerrada", level: "error" }
};
function parseMoneyText(raw) {
  const c = raw.replace(/[^\d.,]/g, "");
  if (!/\d/.test(c)) return null;
  const last = Math.max(c.lastIndexOf(","), c.lastIndexOf("."));
  const n = Number(last >= 0 && c.length - last - 1 === 2 ? `${c.slice(0, last).replace(/[.,]/g, "")}.${c.slice(last + 1)}` : c.replace(/[.,]/g, ""));
  return Number.isFinite(n) ? n : null;
}
function adBalance(raw, avgDailySpend, at = /* @__PURE__ */ new Date()) {
  const cents = (v) => {
    const n = Number(v);
    return v === null || v === void 0 || v === "" || !Number.isFinite(n) ? null : n / 100;
  };
  const code = raw.account_status ?? null;
  const prepaid = Boolean(raw.is_prepay_account);
  const m = prepaid && raw.funding ? raw.funding.match(/(?:R\$|US\$|\$|€|BRL|USD)\s*([\d.,]*\d)/i) ?? raw.funding.match(/([\d.,]*\d)\s*(?:BRL|USD|EUR|reais)/i) : null;
  const available = m ? parseMoneyText(m[1]) : null;
  const cap = cents(raw.spend_cap), spent = cents(raw.amount_spent), due = cents(raw.balance);
  const capRemaining = cap && cap > 0 && spent !== null ? Math.max(0, Math.round((cap - spent) * 100) / 100) : null;
  const limits = [available, capRemaining].filter((v) => v !== null);
  const binding = limits.length ? Math.min(...limits) : null;
  const daysLeft = binding !== null && avgDailySpend > 0 ? Math.round(binding / avgDailySpend * 10) / 10 : null;
  return {
    status: { code, ...code !== null && AD_ACCOUNT_STATUS[code] || { label: code !== null ? `situa\xE7\xE3o ${code}` : "desconhecida", level: "warn" } },
    prepaid,
    available,
    spendCap: cap && cap > 0 ? cap : null,
    capRemaining,
    amountDue: !prepaid && due ? due : null,
    avgDailySpend: Math.round(avgDailySpend * 100) / 100,
    daysLeft,
    low: daysLeft !== null && daysLeft < AD_LOW_BALANCE_DAYS,
    fundingUnavailable: Boolean(raw.fundingUnavailable),
    at: at.toISOString()
  };
}
var adBillingUrl = (externalId) => `https://business.facebook.com/billing_hub/payment_settings/?asset_id=${externalId.replace(/^act_/, "")}`;
var adPlatformBillingUrl = (platform, externalId) => platform === "meta" ? adBillingUrl(externalId) : platform === "google" ? "https://ads.google.com/aw/billing/summary" : null;
var adPlatformManagerUrl = (platform, accountExternalId, level, externalId) => platform === "meta" ? adManagerUrl(accountExternalId, level, externalId) : platform === "google" ? "https://ads.google.com/aw/campaigns" : null;
var AD_RESULT_LABELS = {
  link_click: "cliques no link",
  conversions: "convers\xF5es",
  landing_page_view: "visitas \xE0 p\xE1gina",
  "onsite_conversion.messaging_conversation_started_7d": "conversas iniciadas",
  lead: "cadastros",
  "onsite_conversion.lead_grouped": "cadastros no formul\xE1rio",
  purchase: "compras",
  "offsite_conversion.fb_pixel_purchase": "compras no site",
  add_to_cart: "adi\xE7\xF5es ao carrinho",
  video_view: "visualiza\xE7\xF5es de v\xEDdeo",
  post_engagement: "engajamentos",
  page_engagement: "engajamentos com a p\xE1gina"
};
var AD_RESULT_PRIORITY = ["purchase", "offsite_conversion.fb_pixel_purchase", "lead", "onsite_conversion.lead_grouped", "conversions", "onsite_conversion.messaging_conversation_started_7d", "landing_page_view", "link_click", "video_view", "post_engagement"];
var adResultGuess = (results) => AD_RESULT_PRIORITY.find((k) => (results[k] ?? 0) > 0) ?? null;
var adCostPerSale = (s) => s.won > 0 && s.spend != null && s.spend > 0 ? Math.round(s.spend / s.won * 100) / 100 : null;
var adReturn = (s) => s.wonValue > 0 && s.spend != null && s.spend > 0 ? Math.round(s.wonValue / s.spend * 10) / 10 : null;
var AD_SALES_WINDOW_DAYS = 90;
function adSalesText(s) {
  const money = (v) => `R$ ${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
  const won = s.won > 0 ? `${s.won} ${s.won === 1 ? "venda" : "vendas"}${s.wonValue > 0 ? `, ${money(s.wonValue)} vendidos` : ""}${s.cycleDaysMedian !== null ? `, ${String(s.cycleDaysMedian).replace(".", ",")} dias do lead \xE0 venda (mediana)` : ""}` : "nenhuma venda fechada";
  return `${s.leads} ${s.leads === 1 ? "lead" : "leads"} \xB7 ${won}${s.lost > 0 ? ` \xB7 ${s.lost} ${s.lost === 1 ? "perdido" : "perdidos"}${s.lostReasons.length ? ` (${s.lostReasons.map((r) => `${r.reason}: ${r.count}`).join("; ")})` : ""}` : ""}`;
}
var AD_ANALYSIS_ESTIMATE_USD = 0.3;
var BOARD_BLOCKS = ["week", "notes", "audience", "comments", "stories", "top_post", "media", "sales", "series", "demands", "win", "rivals"];
var BOARD_BLOCK_LABELS = {
  week: "Jornal da semana",
  notes: "Bilhetes (coment\xE1rio e elogio)",
  audience: "Audi\xEAncia",
  comments: "Coment\xE1rios",
  stories: "Stories",
  top_post: "Post da semana",
  media: "M\xEDdia paga",
  sales: "Vendas",
  series: "Dados do cliente",
  demands: "Opera\xE7\xE3o da conta",
  win: "Conquista do time",
  rivals: "Radar dos concorrentes"
};
var BOARD_BLOCK_HINTS = {
  week: "a semana lida pela Machine; s\xF3 com os assuntos dos blocos ligados",
  notes: "o melhor coment\xE1rio da semana e o que o cliente disse",
  audience: "seguidores e alcance das contas da marca",
  comments: "quantos chegaram, quantos foram respondidos e em quanto tempo",
  stories: "os stories da semana e os n\xFAmeros deles",
  top_post: "o post de maior alcance",
  media: "quanto foi investido, saldo das contas e avisos",
  sales: "vendas e custo por venda que vieram dos an\xFAncios",
  series: "o que o cliente entrega ou se liga em dados externos: vendas, etiquetas, acessos",
  demands: "demandas em aberto, com quem est\xE3o e o que atrasou",
  win: "aprova\xE7\xE3o de primeira, semana sem atraso",
  rivals: "a faixa das marcas, a p\xE1gina dos concorrentes no jornal e os concorrentes na audi\xEAncia"
};
var BOARD_CARD_BLOCK = { audience: "audience", followers: "audience", reach: "audience", comments: "comments", stories: "stories", top_post: "top_post", media: "media", sales: "sales", series: "series", demands: "demands", win: "win" };
var BOARD_TOPIC_BLOCK = { "m\xEDdia paga": "media", vendas: "sales", "os n\xFAmeros do cliente": "series", concorrentes: "rivals" };
var BOARD_AUDIENCES = ["team", "client"];
var BOARD_CLIENT_NEVER = ["rivals"];
var boardBlocksOf = (audience) => BOARD_BLOCKS.filter((b) => audience === "team" || !BOARD_CLIENT_NEVER.includes(b));
var RITMO_AREAS = ["pulse", "calendar", "media", "plans", "publish", "comments", "inbox", "tags"];
var RITMO_AREA_LABELS = { pulse: "Pulso", calendar: "Calend\xE1rio", media: "Campanhas: m\xEDdia", plans: "Campanhas: planos", publish: "Redes: publica\xE7\xF5es", comments: "Redes: coment\xE1rios", inbox: "Redes: mensagens", tags: "Redes: marca\xE7\xF5es" };
var RITMO_CLIENT_AREAS = ["pulse", "calendar", "media", "plans"];
var ritmoAreasOf = (audience) => RITMO_AREAS.filter((a) => audience === "team" || RITMO_CLIENT_AREAS.includes(a));
var RITMO_MONEY_AREAS = ["media", "plans"];
var RITMO_MONEY_BLOCKS = ["media", "sales", "series"];
var RITMO_AREA_ROUTES = [
  [/^board(\/|$)/, "pulse"],
  [/^ritmo\/calendar/, "calendar"],
  [/^demand-items\/[^/]+\/publish/, "publish"],
  [/^publishing/, "publish"],
  [/^(social-comments)(\/|$)/, "comments"],
  [/^(inbox-threads|inbox)(\/|$)/, "inbox"],
  [/^(tags|social-tags|tag-reviewers)(\/|$)/, "tags"],
  [/^(ads|ad-campaigns|ad-sets|ad-accounts)(\/|$)/, "media"],
  [/^(campaigns|campaign-lines|campaign-decisions|campaign-builds|campaign-rooms|campaign-room-files)(\/|$)/, "plans"]
];
function ritmoAreaOf(path) {
  const p = path.replace(/^\/+/, "").replace(/\?.*$/, "").replace(/^organizations\/[^/]+\//, "");
  return RITMO_AREA_ROUTES.find(([re]) => re.test(p))?.[1] ?? null;
}
var cleanRitmoAreas = (list2, audience) => ritmoAreasOf(audience).filter((a) => list2.includes(a));
function ritmoAccessFor(views, who) {
  const all = ritmoAreasOf(who.audience);
  const side = views.filter((v) => v.audience === who.audience);
  if (who.audience === "team" && (who.partner || side.length === 0)) return { areas: [...all], money: true, blocks: [...BOARD_BLOCKS] };
  const personal = side.filter((v) => v.userIds.includes(who.userId));
  const byFunction = who.audience === "team" ? side.filter((v) => v.functionIds.some((f) => who.functionIds.includes(f))) : [];
  const mine = personal.length ? personal : byFunction.length ? byFunction : side.filter((v) => v.isDefault);
  const money = mine.some((v) => v.money);
  const open = new Set(mine.flatMap((v) => v.areas ?? all));
  return {
    money,
    areas: all.filter((a) => open.has(a) && (money || !RITMO_MONEY_AREAS.includes(a))),
    blocks: cleanBoardBlocks(mine.flatMap((v) => v.blocks)).filter((b) => (money || !RITMO_MONEY_BLOCKS.includes(b)) && (who.audience === "team" || !BOARD_CLIENT_NEVER.includes(b)))
  };
}
var BOARD_VIEW_NAME_MAX = 60;
var BOARD_NO_MONEY = ["week", "notes", "audience", "comments", "stories", "top_post", "demands", "win", "rivals"];
var BOARD_CLIENT_VIEW_DEFAULTS = [
  { name: "Cliente: diretoria", blocks: ["week", "notes", "audience", "stories", "top_post", "media", "sales", "series", "demands"] },
  { name: "Cliente: marketing", blocks: ["week", "notes", "audience", "stories", "top_post", "media", "demands"] },
  { name: "Cliente sem vis\xE3o marcada", blocks: ["week", "notes", "audience", "stories", "top_post", "demands"], isDefault: true }
];
var BOARD_VIEW_DEFAULTS = [
  { name: "Conta inteira", blocks: [...BOARD_BLOCKS], functions: ["operacao", "atendimento", "gerente_de_contas", "planejamento", "gestao_de_trafego"] },
  { name: "Cria\xE7\xE3o", blocks: BOARD_NO_MONEY, functions: ["redacao", "direcao_de_arte", "edicao_de_video", "arte_final"] },
  { name: "Social media", blocks: ["week", "notes", "audience", "comments", "stories", "top_post", "media", "demands", "win", "rivals"], functions: ["social_media"] },
  { name: "Financeiro", blocks: ["media", "sales", "series", "demands"], functions: ["financeiro"] },
  { name: "Quem n\xE3o est\xE1 em nenhuma vis\xE3o", blocks: BOARD_NO_MONEY, functions: [], isDefault: true }
];
var cleanBoardBlocks = (list2) => [...new Set(list2)].filter((b) => BOARD_BLOCKS.includes(b));
function boardBlocksFor(views, who) {
  views = views.filter((v) => v.audience === "team");
  if (who.partner || views.length === 0) return [...BOARD_BLOCKS];
  const personal = views.filter((v) => v.userIds.includes(who.userId));
  const byFunction = views.filter((v) => v.functionIds.some((f) => who.functionIds.includes(f)));
  const mine = personal.length ? personal : byFunction.length ? byFunction : views.filter((v) => v.isDefault);
  return cleanBoardBlocks(mine.flatMap((v) => v.blocks));
}
function boardClientBlocksFor(views, userId) {
  const client = views.filter((v) => v.audience === "client");
  const personal = client.filter((v) => v.userIds.includes(userId));
  return cleanBoardBlocks((personal.length ? personal : client.filter((v) => v.isDefault)).flatMap((v) => v.blocks)).filter((b) => !BOARD_CLIENT_NEVER.includes(b));
}
var BOARD_WEEK_ESTIMATE_USD = 0.1;
var GADS_HEADLINE_MAX = 30;
var GADS_DESCRIPTION_MAX = 90;
var GADS_HEADLINES_MIN = 3;
var GADS_HEADLINES_MAX = 15;
var GADS_DESCRIPTIONS_MIN = 2;
var GADS_DESCRIPTIONS_MAX = 4;
var GADS_KEYWORDS_MAX = 20;
var GADS_GROUPS_MAX = 6;
var GADS_MATCHES = ["PHRASE", "EXACT", "BROAD"];
var GADS_MATCH_LABELS = { PHRASE: "frase", EXACT: "exata", BROAD: "ampla" };
var gadsKeywordText = (k) => k.match === "EXACT" ? `[${k.text}]` : k.match === "PHRASE" ? `"${k.text}"` : k.text;
function gadsKeywordParse(line) {
  const t = line.trim();
  if (!t) return null;
  const exact = /^\[(.+)\]$/.exec(t), phrase = /^["“](.+)["”]$/.exec(t);
  const text = (exact?.[1] ?? phrase?.[1] ?? t).replace(/[\[\]"“”!@%^*()={};~`<>?\\|]/g, " ").replace(/\s+/g, " ").trim().toLowerCase().slice(0, 80);
  return text ? { text, match: exact ? "EXACT" : phrase ? "PHRASE" : "BROAD" } : null;
}
function gadsDraftCheck(d) {
  const dropped = [];
  const uniq = (xs) => [...new Map(xs.map((x) => [x.toLowerCase(), x])).values()];
  const fit = (xs, max, what, group) => uniq(xs.map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean)).filter((x) => {
    if (x.length <= max) return true;
    dropped.push(`${what} de \u201C${group}\u201D com ${x.length} caracteres (o limite \xE9 ${max}): \u201C${x}\u201D`);
    return false;
  });
  const groups = d.groups.slice(0, GADS_GROUPS_MAX).map((g) => {
    const name = g.name.replace(/\s+/g, " ").trim().slice(0, 80) || "Grupo";
    const keywords = [...new Map(g.keywords.map((k) => gadsKeywordParse(gadsKeywordText(k))).filter((k) => Boolean(k)).map((k) => [`${k.match}:${k.text}`, k])).values()].slice(0, GADS_KEYWORDS_MAX);
    return { name, keywords, headlines: fit(g.headlines, GADS_HEADLINE_MAX, "t\xEDtulo", name).slice(0, GADS_HEADLINES_MAX), descriptions: fit(g.descriptions, GADS_DESCRIPTION_MAX, "descri\xE7\xE3o", name).slice(0, GADS_DESCRIPTIONS_MAX) };
  }).filter((g) => g.keywords.length || g.headlines.length || g.descriptions.length);
  const negatives = uniq(d.negatives.map((n) => gadsKeywordParse(n)?.text ?? "").filter(Boolean)).slice(0, 60);
  const draft = { ...d, name: d.name.replace(/\s+/g, " ").trim().slice(0, 120), dailyBudget: d.dailyBudget && d.dailyBudget > 0 ? Math.round(d.dailyBudget * 100) / 100 : null, places: uniq(d.places.map((p) => p.trim()).filter(Boolean)).slice(0, 20), finalUrl: d.finalUrl.trim().slice(0, 500), groups, negatives };
  const blockers = [];
  if (!draft.name) blockers.push("falta o nome da campanha");
  if (!draft.dailyBudget) blockers.push("falta a verba por dia");
  if (!/^https:\/\/[^\s.]+\.[^\s]+$/.test(draft.finalUrl)) blockers.push("falta a p\xE1gina de destino (um endere\xE7o https)");
  if (!groups.length) blockers.push("falta ao menos um grupo de an\xFAncios");
  for (const g of groups) {
    if (!g.keywords.length) blockers.push(`o grupo \u201C${g.name}\u201D est\xE1 sem palavra-chave`);
    if (g.headlines.length < GADS_HEADLINES_MIN) blockers.push(`o grupo \u201C${g.name}\u201D precisa de ao menos ${GADS_HEADLINES_MIN} t\xEDtulos (tem ${g.headlines.length})`);
    if (g.descriptions.length < GADS_DESCRIPTIONS_MIN) blockers.push(`o grupo \u201C${g.name}\u201D precisa de ao menos ${GADS_DESCRIPTIONS_MIN} descri\xE7\xF5es (tem ${g.descriptions.length})`);
  }
  return { draft, dropped, blockers };
}
var GADS_DRAFT_ESTIMATE_USD = 0.3;
var AD_WRITE_LEVELS = ["campaign", "adset", "ad"];
var AD_WRITE_LEVEL_LABELS = { campaign: "a campanha", adset: "o conjunto", ad: "o an\xFAncio" };
var AD_BUDGET_CONFIRM_RATIO = 1.5;
var adWriteAction = (status) => status === "ACTIVE" ? "pause" : status === "PAUSED" ? "resume" : null;
var CAMPAIGN_BUILD_KINDS = ["campaign", "adset"];
var CAMPAIGN_BUILD_KIND_LABELS = { campaign: "Campanha", adset: "Conjunto" };
var CAMPAIGN_BUILD_STATUSES = ["todo", "linked", "dropped"];
var CAMPAIGN_BUILD_STATUS_LABELS = { todo: "a criar", linked: "criada", dropped: "n\xE3o vai ser criada" };
function adNameMatch(a, b) {
  const norm = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const x = norm(a), y = norm(b);
  if (!x || !y) return 0;
  if (Math.min(x.length, y.length) >= 4 && (x.includes(y) || y.includes(x))) return 1;
  const wx = new Set(x.split(" ").filter((w) => w.length > 2)), wy = new Set(y.split(" ").filter((w) => w.length > 2));
  if (!wx.size || !wy.size) return 0;
  const common = [...wx].filter((w) => wy.has(w)).length;
  return common / Math.min(wx.size, wy.size);
}
var AD_BUILD_MATCH_MIN = 0.6;
var AD_ALERT_KINDS = ["disconnected", "account", "balance", "rejected", "stalled", "cost", "fatigue"];
var AD_STALLED_MIN_DAILY = 1;
var AD_COST_UP_RATIO = 1.5;
var AD_COST_UP_MIN_RECENT = 5;
var AD_COST_UP_MIN_BASE = 10;
var adAccessLost = (error) => Boolean(error && /access token|session has expired|has expired|oauth|code 190|\(190\)|not authorized|entrar com o facebook|entrar de novo|credencial guardada|permission_denied|unauthenticated/i.test(error));
var EMPTY_MEDIA_OUTLINE = { title: "", objective: "", kind: "", startsAt: "", endsAt: "", audience: "", places: "", hypothesis: "", success: "", lines: [], builds: [], creatives: [], open: [] };
var MEDIA_OUTLINE_LABELS = { objective: "Objetivo", audience: "Para quem", places: "Pra\xE7a", hypothesis: "O que se quer testar", success: "Como saber que deu certo" };
var EMPTY_MEDIA_ROOM = { status: "idle", error: null, steps: [], paths: [], outline: EMPTY_MEDIA_OUTLINE, ready: false, messages: [] };
var mediaOutlineReady = (o) => Boolean(o.objective.trim() && o.kind && (o.kind === "continuous" || o.startsAt && o.endsAt));
var MEDIA_ROOM_TURN_ESTIMATE_USD = 0.12;
var AD_ACCOUNT_READING_ESTIMATE_USD = 0.4;
var AD_SUGGESTION_LABELS = { adjust: "Ajuste", creative: "Criativo novo", adset: "Conjunto novo", campaign: "Campanha nova" };
var AD_STATUS_LABELS = { ACTIVE: "no ar", PAUSED: "pausado", CAMPAIGN_PAUSED: "campanha pausada", ADSET_PAUSED: "conjunto pausado", DISAPPROVED: "reprovado", PENDING_REVIEW: "em an\xE1lise", WITH_ISSUES: "com problema", IN_PROCESS: "processando", ARCHIVED: "arquivado", DELETED: "apagado", COMPLETED: "conclu\xEDda", PENDING_BILLING_INFO: "falta pagamento", PREAPPROVED: "pr\xE9-aprovado" };
var adStatusLabel = (s) => s ? AD_STATUS_LABELS[s] ?? s.toLowerCase().replace(/_/g, " ") : "sem situa\xE7\xE3o";
var adManagerUrl = (accountExternalId, level, externalId) => `https://adsmanager.facebook.com/adsmanager/manage/${level}?act=${accountExternalId.replace(/^act_/, "")}&selected_${level === "campaigns" ? "campaign" : level === "adsets" ? "adset" : "ad"}_ids=${externalId}`;
var PERSONAL_TOKEN_PREFIX = "mrt_";
var PERSONAL_TOKEN_DAYS = 90;
var MCP_RATE_PER_MINUTE = 60;
var OAUTH_CLIENT_PREFIX = "mrc_";
var OAUTH_ACCESS_PREFIX = "mra_";
var OAUTH_REFRESH_PREFIX = "mrr_";
var OAUTH_ACCESS_SECONDS = 3600;
var OAUTH_CODE_SECONDS = 300;
var OAUTH_REQUEST_SECONDS = 900;
var POINTS_IMPORT_MAX = 500;
export {
  ACTIVITY_AI_LABELS,
  ACTIVITY_JOB_LABELS,
  ACTIVITY_NOISE,
  AD_ACCOUNT_READING_ESTIMATE_USD,
  AD_ALERT_KINDS,
  AD_ANALYSIS_ESTIMATE_USD,
  AD_BUDGET_CONFIRM_RATIO,
  AD_BUILD_MATCH_MIN,
  AD_COST_UP_MIN_BASE,
  AD_COST_UP_MIN_RECENT,
  AD_COST_UP_RATIO,
  AD_FATIGUE_COST_UP,
  AD_FATIGUE_CTR_DROP,
  AD_FATIGUE_LOOKBACK_DAYS,
  AD_FATIGUE_MIN_CLICKS,
  AD_FATIGUE_MIN_DAYS,
  AD_FATIGUE_MIN_IMPRESSIONS,
  AD_FATIGUE_MIN_RESULTS,
  AD_LOW_BALANCE_DAYS,
  AD_PLATFORMS,
  AD_PLATFORM_LABELS,
  AD_RESULT_LABELS,
  AD_RESULT_PRIORITY,
  AD_SALES_WINDOW_DAYS,
  AD_STALLED_MIN_DAILY,
  AD_STATUS_LABELS,
  AD_SUGGESTION_LABELS,
  AD_WRITE_LEVELS,
  AD_WRITE_LEVEL_LABELS,
  AI_FEATURES,
  AI_FEEDBACK,
  AI_FEEDBACK_LABELS,
  AI_IMAGE_ESTIMATE_USD,
  AI_PENDING_MAX,
  AI_RUN_STATUSES,
  AI_USER_MONTHLY_USD,
  ANNUAL_PROPOSAL_ESTIMATE_USD,
  APPROVAL_JUSTIFICATION_MIN,
  APPROVAL_MARKS_MAX,
  APPROVAL_STATUSES,
  APPROVAL_STATUS_LABELS,
  ARGUMENT_STATUSES,
  ARGUMENT_STATUS_LABELS,
  ARGUMENT_STRENGTHS,
  ARGUMENT_STRENGTH_LABELS,
  ASSET_AI_EDIT,
  ASSET_AI_EDIT_LABELS,
  ASSET_AUDIT_VERDICTS,
  ASSET_AUDIT_VERDICT_LABELS,
  ASSET_COMPOSE_MAX,
  ASSET_EDIT_ANCHORS,
  ASSET_EDIT_FORMATS,
  ASSET_EDIT_FORMAT_LABELS,
  ASSET_EVENT_WARNING,
  ASSET_FILE_KINDS,
  ASSET_KEEP_ORIGINAL,
  ASSET_KINDS,
  ASSET_KIND_LABELS,
  ASSET_ORIGINS,
  ASSET_ORIGIN_HINTS,
  ASSET_ORIGIN_LABELS,
  ASSET_PEOPLE,
  ASSET_PEOPLE_LABELS,
  ASSET_PRODUCT_MIN,
  ASSET_ROLES,
  ASSET_ROLE_HINTS,
  ASSET_ROLE_LABELS,
  ASSET_STATUSES,
  ASSET_STATUS_LABELS,
  AUDIT_ACTIONS,
  BASE_SHARE_WARN,
  BASE_SIZE_ESTIMATE_USD,
  BIS_ASSET_BLOCKS,
  BIS_AUDIENCE_SCOPES,
  BIS_BLOCK_ASSET_KIND,
  BIS_BLOCK_FIELDS,
  BIS_BLOCK_HINTS,
  BIS_BLOCK_KINDS,
  BIS_BLOCK_LABELS,
  BIS_BOARD_ESTIMATE_USD,
  BIS_BOARD_FILES_MAX,
  BIS_BOARD_FILE_MAX,
  BIS_BODY_LABELS,
  BIS_BUILD_AREAS,
  BIS_BUILD_AREA_INFO,
  BIS_BUILD_KIND,
  BIS_CHANNELS,
  BIS_DEFAULT_CLASS,
  BIS_DRAWERS,
  BIS_FORCES,
  BIS_FORCE_LABELS,
  BIS_ICON_CAPS,
  BIS_ICON_FAMILIES,
  BIS_ILLUSTRATION_GROUNDS,
  BIS_ILLUSTRATORS,
  BIS_INFO_CLASSES,
  BIS_INFO_CLASS_HINTS,
  BIS_INFO_CLASS_LABELS,
  BIS_ITEM_STATUSES,
  BIS_LEGAL_PLACES,
  BIS_LOGO_ON_PHOTO,
  BIS_MOLD_ALIGN,
  BIS_MOLD_BAR,
  BIS_MOLD_CASE,
  BIS_MOLD_EYEBROW,
  BIS_MOLD_LOGO,
  BIS_MOLD_PATTERN,
  BIS_MOLD_PRINT,
  BIS_MOLD_WEIGHT,
  BIS_PIECES,
  BIS_REF_BATCH,
  BIS_REF_ESTIMATE_USD,
  BIS_REF_FINDINGS_MAX,
  BIS_REF_FINDING_KINDS,
  BIS_REF_FINDING_LABELS,
  BIS_REF_FINDING_STATES,
  BIS_REF_MESSAGES_MAX,
  BIS_REF_QUESTIONS_MAX,
  BIS_REF_STATUSES,
  BIS_REF_TEXT_MAX,
  BIS_RULE_KINDS,
  BIS_RULE_KIND_LABELS,
  BIS_SECTIONS,
  BIS_SECTION_BLOCKS,
  BIS_SECTION_HINTS,
  BIS_SECTION_LABELS,
  BIS_STUDY_ESTIMATE_USD,
  BIS_STUDY_KINDS,
  BIS_STUDY_OPTIONS_MAX,
  BIS_STUDY_REFS_MAX,
  BIS_VERSION_LEVELS,
  BIS_VERSION_LEVEL_LABELS,
  BOARD_AUDIENCES,
  BOARD_BLOCKS,
  BOARD_BLOCK_HINTS,
  BOARD_BLOCK_LABELS,
  BOARD_CARD_BLOCK,
  BOARD_CLIENT_NEVER,
  BOARD_CLIENT_VIEW_DEFAULTS,
  BOARD_TOPIC_BLOCK,
  BOARD_VIEW_DEFAULTS,
  BOARD_VIEW_NAME_MAX,
  BOARD_WEEK_ESTIMATE_USD,
  BR_STATES,
  CAMPAIGN_BUILD_KINDS,
  CAMPAIGN_BUILD_KIND_LABELS,
  CAMPAIGN_BUILD_STATUSES,
  CAMPAIGN_BUILD_STATUS_LABELS,
  CAMPAIGN_KINDS,
  CAMPAIGN_KIND_LABELS,
  CAMPAIGN_LINE_KINDS,
  CAMPAIGN_LINE_LABELS,
  CAMPAIGN_PACE_LABELS,
  CAMPAIGN_PACE_TOLERANCE,
  CAMPAIGN_RESULT_FIELDS,
  CAMPAIGN_SPEND_ORIGINS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_LABELS,
  CHANGE_VERDICTS,
  CHANGE_VERDICT_LABELS,
  CHANNEL_DORMANT_DAYS,
  CHANNEL_KINDS,
  CHANNEL_KIND_LABELS,
  CHANNEL_PUBLISHERS,
  CHANNEL_PUBLISHER_LABELS,
  CHANNEL_ROLES,
  CHANNEL_ROLE_LABELS,
  CHANNEL_ROLE_MIN_POSTS,
  CHANNEL_ROLE_SHORT,
  CLAIM_KINDS,
  CLAIM_KIND_LABELS,
  CLIENT_DECISIONS,
  CLIENT_DECISION_LABELS,
  CLIENT_ROLES,
  CLIENT_SECTIONS,
  CLIENT_SECTION_ENTITIES,
  CLIENT_SECTION_LABELS,
  CLIENT_STAGES,
  CLIENT_STAGE_LABELS,
  COMMENT_REPLY_MAX,
  COMMENT_STATUSES,
  COMMERCIAL_STATUSES,
  COMMERCIAL_STATUS_LABELS,
  COMPETITOR_KINDS,
  COMPETITOR_KIND_LABELS,
  COMPOSE_OPS,
  CONTACT_VIAS,
  CONTEXT_LEVELS,
  CONTEXT_LEVEL_LABELS,
  CONVERSATION_DEFAULT_GOALS,
  CONVERSATION_DEPTH,
  CONVERSATION_DEPTHS,
  CONVERSATION_INTRO_BODY_MAX,
  CONVERSATION_INTRO_TOPICS_MAX,
  CONVERSATION_KINDS,
  CONVERSATION_KIND_LABELS,
  CONVERSATION_PURPOSES,
  CONVERSATION_STATUSES,
  CONVERSATION_STATUS_LABELS,
  COST_CATEGORIES,
  COST_CATEGORY_LABELS,
  COST_KINDS,
  COST_KIND_LABELS,
  CREATION_DECISIONS,
  CREATION_DECISION_LABELS,
  CREATION_DECISION_NOTE_MAX,
  CREATION_FORMATS,
  CREATION_FORMAT_LABELS,
  CREATION_FORMAT_RATIO,
  CREATION_IMAGE_ESTIMATE_USD,
  CREATION_ITEMS_MAX,
  CREATION_LAYOUTS,
  CREATION_LAYOUT_LABELS,
  CREATION_LOCK_MS,
  CREATION_PIECES_MAX,
  CREATION_PIECE_BOARDS_MAX,
  CREATION_PIECE_FORMAT,
  CREATION_PIECE_KINDS,
  CREATION_PIECE_KIND_LABELS,
  CREATION_PIECE_STATUSES,
  CREATION_PIECE_STATUS_LABELS,
  CREATION_ROOM_KINDS,
  CREATION_ROOM_KIND_LABELS,
  CREATION_STATS_MAX,
  CREATION_TALK_READ,
  CREATION_TEXT_MAX,
  CREATION_TURN_ESTIMATE_USD,
  CREATOR_KINDS,
  CREATOR_KIND_LABELS,
  CREATOR_RISK_LABELS,
  CREATOR_RISK_LEVELS,
  CREATOR_STAGES,
  CREATOR_STAGE_LABELS,
  CREDIT_BRL,
  CREDIT_CARRYOVER,
  CYCLE_STATUSES,
  DATA_ADDRESS_ROLES,
  DATA_ADDRESS_ROLE_LABELS,
  DATA_COLUMN_ROLES,
  DATA_COLUMN_ROLE_LABELS,
  DATA_CONNECTORS,
  DATA_CONNECTOR_GROUPS,
  DATA_CONNECTOR_GROUP_LABELS,
  DATA_CONNECTOR_KEYS,
  DECISION_STATUSES,
  DECISION_STATUS_LABELS,
  DEFAULT_POSITIONING_DIMENSIONS,
  DELIVERABLE_ACTION_FORMATS,
  DELIVERABLE_BOARD,
  DELIVERABLE_CLIENT_VISIBLE,
  DELIVERABLE_FORMATS,
  DELIVERABLE_FORMAT_LABELS,
  DELIVERABLE_STATUSES,
  DELIVERABLE_STATUS_LABELS,
  DEMAND_BALLS,
  DEMAND_BALL_LABELS,
  DEMAND_FILE_KINDS,
  DEMAND_FILE_KIND_LABELS,
  DEMAND_ITEMS_MAX,
  DEMAND_ITEM_CLIENT_VISIBLE,
  DEMAND_ITEM_STATUSES,
  DEMAND_ITEM_STATUS_LABELS,
  DEMAND_OPEN,
  DEMAND_ORIGINS,
  DEMAND_ORIGIN_LABELS,
  DEMAND_PENDING_MAX,
  DEMAND_PENDING_REASON_MIN,
  DEMAND_PENDING_STATUSES,
  DEMAND_PENDING_STATUS_LABELS,
  DEMAND_PENDING_TEXT_MAX,
  DEMAND_REPO_PATH_RE,
  DEMAND_REVISION_STATUSES,
  DEMAND_SCOPES,
  DEMAND_SCOPE_LABELS,
  DEMAND_SCOPE_QUESTION,
  DEMAND_SCOPE_SURE,
  DEMAND_SHORT_NAME_MAX,
  DEMAND_STATUSES,
  DEMAND_STATUS_LABELS,
  DEVIATION_FLOOR,
  DEVIATION_THRESHOLD_PCT,
  DISCOVERY_WINDOW_DAYS,
  DISPUTE_PRIORITIES,
  DISPUTE_PRIORITY_LABELS,
  DOMAIN_EVENTS,
  DRIVE_GROUP_FOLDER,
  DRIVE_JOB_FOLDERS,
  DRIVE_MONTHLY_FOLDER,
  DRIVE_PRIVATE_FOLDER,
  EFFECT_SOURCES,
  EFFECT_SOURCE_LABELS,
  EMBEDDING_DIMENSIONS,
  EMPTY_MEDIA_OUTLINE,
  EMPTY_MEDIA_ROOM,
  EMPTY_OUTLINE,
  ENTITY_TYPES,
  ENTITY_TYPE_LABELS,
  EVIDENCE_TYPES,
  EVOLUTION_AXES,
  EVOLUTION_AXIS_LABELS,
  EXECUTORS,
  EXECUTOR_LABELS,
  EXPERIMENT_OUTCOMES,
  EXPERIMENT_OUTCOME_LABELS,
  FACE_CLUSTER_MIN,
  FACE_MATCH_MIN,
  FACE_SIGNATURE_DIMENSIONS,
  FACE_STATUSES,
  FEE_BASE_SHARE,
  FEE_CYCLE_MONTHS,
  FEE_CYCLE_STATUSES,
  FEE_CYCLE_STATUS_LABELS,
  FEE_ITEMS_MAX,
  FEE_ITEM_ORIGINS,
  FEE_MODES,
  FEE_PROPOSE_ESTIMATE_USD,
  FORWARD_NOTE_MAX,
  FORWARD_PEOPLE_MAX,
  FUTURE_PROFILE_NAME,
  FUTURE_VISION_GOAL,
  GADS_DESCRIPTIONS_MAX,
  GADS_DESCRIPTIONS_MIN,
  GADS_DESCRIPTION_MAX,
  GADS_DRAFT_ESTIMATE_USD,
  GADS_GROUPS_MAX,
  GADS_HEADLINES_MAX,
  GADS_HEADLINES_MIN,
  GADS_HEADLINE_MAX,
  GADS_KEYWORDS_DAYS,
  GADS_KEYWORDS_MAX,
  GADS_KEYWORDS_STALE_HOURS,
  GADS_KEYWORD_MATCH_LABELS,
  GADS_MATCHES,
  GADS_MATCH_LABELS,
  GADS_NEGATIVES_APPLY_MAX,
  GADS_SCOPE_LABELS,
  GADS_TERMS_REVIEW_ESTIMATE_USD,
  GADS_TERMS_REVIEW_MAX,
  GALLERY_KINDS,
  GENERATOR_TYPES,
  GITHUB_ORG,
  HUMAN_STATUSES,
  HYPOTHESIS_STATUSES,
  HYPOTHESIS_VERDICTS,
  ILLUSTRATION_ESTIMATE_USD,
  ILLUSTRATION_OPTIONS,
  ILLUSTRATION_SHAPES,
  IMAGE_CONTRACT_FIELDS,
  IMAGE_CONTRACT_OPEN,
  IMAGE_CONTRACT_STATUSES,
  IMAGE_CONTRACT_STATUS_LABELS,
  IMAGE_TIERS,
  INBOX_REPLY_ESTIMATE_USD,
  INBOX_STATUSES,
  INBOX_WINDOW_HOURS,
  INGEST_TOKEN_PREFIX,
  INITIATIVE_KINDS,
  INITIATIVE_STATUSES,
  INITIATIVE_STATUS_LABELS,
  INTERNAL_ROLES,
  INVITATION_STATUSES,
  ITEMS_ESTIMATE_USD,
  ITEM_PLACEMENTS,
  ITEM_PLACEMENT_FIELDS,
  ITEM_PLACEMENT_LABELS,
  ITEM_PUBLISH_STATUSES,
  JOB_STATUSES,
  JOB_TYPES,
  LAB_EXPLORE_ESTIMATE_USD,
  LAB_INPUTS_MAX,
  LAB_ITEM_KINDS,
  LAB_ITEM_KIND_LABELS,
  LAB_ITEM_STATUSES,
  LAB_JUDGEABLE,
  LAB_OPTIONS_MAX,
  LAB_PROPOSAL_ESTIMATE_USD,
  LAB_RESEARCH_ESTIMATE_USD,
  LAB_SHELF_ESTIMATE_USD,
  LAB_SHELF_MAX,
  LAB_STATUSES,
  LAB_TALK_CONTEXT_MAX,
  LAB_TALK_ESTIMATE_USD,
  LAB_VERDICTS,
  LAB_VERDICT_LABELS,
  LAB_VISUAL_MIN,
  LAB_VISUAL_QUESTION,
  LEARNING_SCOPES,
  LEARNING_SCOPE_LABELS,
  LIVE_CADENCES,
  LIVE_CADENCE_DAYS,
  LIVE_CADENCE_LABELS,
  MATERIAL_IDENTITIES,
  MATERIAL_IDENTITY_LABELS,
  MATERIAL_RECIPES_MAX,
  MATERIAL_RECIPE_DEFAULTS,
  MATERIAL_RENDER_SECONDS,
  MATERIAL_SLIDES_MAX,
  MATERIAL_SLIDE_FORMAT,
  MATERIAL_SLIDE_KINDS,
  MATERIAL_SLIDE_LABELS,
  MATERIAL_TEXT_MAX,
  MATERIAL_TONES,
  MCP_RATE_PER_MINUTE,
  MEASURED_POSITIONING_DIMENSIONS,
  MEASUREMENT_KINDS,
  MEASUREMENT_KIND_LABELS,
  MEDIA_OUTLINE_LABELS,
  MEDIA_PLATFORMS,
  MEDIA_PLATFORM_LABELS,
  MEDIA_ROOM_TURN_ESTIMATE_USD,
  MEMBERSHIP_ROLES,
  MEMBERSHIP_ROLE_LABELS,
  METRIC_UNITS,
  METRIC_UNIT_LABELS,
  MOCKUP_FRAMES,
  MOCKUP_FRAME_LABELS,
  MODO_ATTACH_ACCEPT,
  MODO_ATTACH_BYTES,
  MODO_ATTACH_KINDS,
  MODO_ATTACH_MAX,
  MODO_ATTACH_PROMPT_MAX,
  MODO_ATTACH_READING_MAX,
  MODO_BASE_RETIRE_AT,
  MODO_BOX_WIDTH,
  MODO_CARDS_MAX,
  MODO_COMPOSE_ESTIMATE_USD,
  MODO_DECISION_CONFIDENCE,
  MODO_DECK_ESTIMATE_USD,
  MODO_DECK_MAX,
  MODO_EMPHASIS_MAX,
  MODO_EXAMPLES_MAX,
  MODO_GUARD_MIN,
  MODO_HIGHLIGHT_DETAIL_MAX,
  MODO_HIGHLIGHT_MAX,
  MODO_ICON_COMPARE_MAX,
  MODO_ICON_ESTIMATE_USD,
  MODO_ICON_GPT_ESTIMATE_USD,
  MODO_ICON_OPTIONS,
  MODO_ICON_SOLID_SOFT,
  MODO_ICON_SUBJECT_MAX,
  MODO_JUSTIFICATION_MIN,
  MODO_KIND_CHANNELS,
  MODO_KIT_FILE_MAX,
  MODO_KIT_FONTS_MAX,
  MODO_KIT_LOGOS_MAX,
  MODO_LAYER_ZOOM_MAX,
  MODO_LEGAL_MAX,
  MODO_LEGAL_TEXT_MAX,
  MODO_NO_CAPTION,
  MODO_PERSON_KINDS,
  MODO_PHOTO_MIN,
  MODO_PHOTO_PLACES,
  MODO_PHOTO_PLACE_LABELS,
  MODO_PIECE_KINDS,
  MODO_PIECE_KIND_LABELS,
  MODO_PREVIEW_CHANNELS,
  MODO_PREVIEW_LABELS,
  MODO_READY_EMAIL_AFTER_MIN,
  MODO_REQUEST_TYPES,
  MODO_REUSE_MAX_DISTANCE,
  MODO_REUSE_SURE_DISTANCE,
  MODO_ROUTES,
  MODO_ROUTE_LABELS,
  MODO_SEAT_DISCOUNTS,
  MODO_SLIDE_ASK_MAX,
  MODO_SLIDE_EDIT_ESTIMATE_USD,
  MODO_SLIDE_KINDS,
  MODO_SLIDE_KIND_LABELS,
  MODO_SNAP_PAGES,
  MODO_STATIC_TYPES,
  MODO_TEXT_KEYS,
  MODO_TEXT_SCALE,
  MODO_TURN_ESTIMATE_USD,
  MODO_VARIANTS,
  MOLD_FIELD_LABELS,
  MOLD_ROOM_FILES_MAX,
  MOLD_ROOM_FILES_MIN,
  MOLD_ROOM_PREVIEWS,
  MOLD_ROOM_STATUSES,
  MULTI_MAX,
  MYSTERY_SHOP_CHANNELS,
  MYSTERY_SHOP_CHANNEL_LABELS,
  NETWORK_EVENT_KINDS,
  NETWORK_EVENT_KIND_LABELS,
  NETWORK_KINDS,
  NETWORK_KIND_LABELS,
  NETWORK_PLATFORMS,
  NETWORK_PLATFORM_LABELS,
  NETWORK_PROFILE_ESTIMATE_USD,
  NETWORK_PROFILE_VISUAL_ESTIMATE_USD,
  NETWORK_SENTIMENTS,
  NETWORK_SENTIMENT_LABELS,
  NETWORK_STATUSES,
  NETWORK_STATUS_LABELS,
  NETWORK_TIERS,
  NETWORK_TIER_LABELS,
  NOTIFICATION_KINDS,
  NOTIFICATION_KIND_LABELS,
  OAUTH_ACCESS_PREFIX,
  OAUTH_ACCESS_SECONDS,
  OAUTH_CLIENT_PREFIX,
  OAUTH_CODE_SECONDS,
  OAUTH_REFRESH_PREFIX,
  OAUTH_REQUEST_SECONDS,
  ORGANIZATION_SIZES,
  ORGANIZATION_SIZE_LABELS,
  ORGANIZATION_STATUSES,
  OUTCOME_PATHS,
  OUTCOME_PATH_LABELS,
  PAGE_IMPORTANCE,
  PANORAMA_ESTIMATE_USD,
  PANORAMA_POINT_KINDS,
  PANORAMA_POINT_LABELS,
  PANORAMA_REF_TYPES,
  PANORAMA_STATUSES,
  PANORAMA_TONES,
  PANORAMA_VERDICTS,
  PANORAMA_VERDICT_LABELS,
  PASSTHROUGH_FEE,
  PERSONAL_TOKEN_DAYS,
  PERSONAL_TOKEN_PREFIX,
  PERSON_RELATIONS,
  PERSON_RELATION_LABELS,
  PIECE_FAMILIES,
  PIECE_FAMILY_LABELS,
  PIECE_SIZES_MAX,
  PIECE_TYPE_DEFAULTS,
  PIECE_TYPE_MAX,
  PIECE_UNITS,
  PLACE_KINDS,
  PLACE_KIND_LABELS,
  PLAN_OUTLINE_LABELS,
  POINTS_IMPORT_MAX,
  POINT_CHANNELS,
  POINT_CHANNEL_LABELS,
  POINT_OWNERS,
  POINT_OWNER_LABELS,
  POSITIONING_DIMENSION_KINDS,
  POSITIONING_ORIGINS,
  POSITIONING_ORIGIN_LABELS,
  POSTS_PER_READ_OPTIONS,
  POST_PURPOSES,
  POST_PURPOSE_LABELS,
  PRESENCE_SITUATIONS,
  PRESENCE_SITUATION_HINTS,
  PRESENCE_SITUATION_LABELS,
  PRESENCE_STAKES,
  PRESENCE_STAKE_LABELS,
  PRICE_PERIODS,
  PRICE_PERIOD_LABELS,
  PRODUCT_FIELD_LABELS,
  PRODUCT_FILE_KINDS,
  PRODUCT_FILE_KIND_LABELS,
  PRODUCT_KINDS,
  PRODUCT_KIND_LABELS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABELS,
  PROFILE_READ_NETWORKS,
  PROFILE_READ_POSTS,
  PROFILE_VISUAL_IMAGES,
  PROFILE_VISUAL_VIDEOS,
  PROJECT_STATUSES,
  PROJECT_TYPES,
  PROMISE_GAPS,
  PROMISE_GAP_LABELS,
  PROSPECT_COMPETITORS_MAX,
  PROSPECT_STATUSES,
  PROSPECT_STATUS_LABELS,
  PROTECT_AREAS_MAX,
  PROTECT_CROP_HEAVY,
  PROTECT_MARGIN_DEFAULT,
  PROTECT_MARGIN_MAX,
  PROTECT_NOT_READY,
  PROTECT_POINTS_MAX,
  PROTECT_REASONS,
  PROTECT_REASON_LABELS,
  PROTECT_RENDER_STATUSES,
  PROTECT_STATUSES,
  PROTECT_TREATMENTS,
  PROTECT_TREATMENT_DONE,
  PROTECT_TREATMENT_LABELS,
  PUBLICATION_STATUSES,
  PUBLICATION_STATUS_LABELS,
  PULSE_ACCESS_WARN_DAYS,
  PULSE_IG_METRICS,
  QUESTION_SOURCES,
  QUESTION_SOURCE_LABELS,
  QUESTION_STATUSES,
  RARITY_DIMENSIONS,
  RARITY_DIMENSION_LABELS,
  RASTRO_BRIDGE_MIN,
  RASTRO_CASE_ESTIMATE_USD,
  RASTRO_CONSUMER_COMMENTS,
  RASTRO_CONSUMER_DAYS,
  RASTRO_CONSUMER_ESTIMATE_USD,
  RASTRO_CONSUMER_VIDEOS,
  RASTRO_CROSS_LABELS,
  RASTRO_EMBALAGEMMARCA,
  RASTRO_ENGARRAFADOR,
  RASTRO_FRESH_DAYS,
  RASTRO_GIRONEWS,
  RASTRO_LIST_PAGES_MAX,
  RASTRO_LOUD_SHARE,
  RASTRO_LOUD_VIDEOS,
  RASTRO_MOVE_KINDS,
  RASTRO_MOVE_KIND_LABELS,
  RASTRO_RISING_RECENT,
  RASTRO_RISING_YEAR,
  RASTRO_SAME_FACT_DAYS,
  RASTRO_SAME_FACT_NO,
  RASTRO_SAME_FACT_YES,
  RASTRO_SEARCH_DAYS,
  RASTRO_SECTORS,
  RASTRO_SECTOR_SKIP,
  RASTRO_SIGNAL_KINDS,
  RASTRO_SIGNAL_KIND_LABELS,
  RASTRO_STATUSES,
  RASTRO_SWEEP_DAYS,
  RASTRO_SWEEP_ESTIMATE_USD,
  RASTRO_THEME_MIN_BRANDS,
  RASTRO_THEME_STATUSES,
  RASTRO_VIDEO_OUTLIER_MIN,
  RASTRO_VIDEO_OUTLIER_TIMES,
  RASTRO_WEB,
  RASTRO_WINDOW_DAYS,
  RD_VOICE_DAYS,
  RD_VOICE_ESTIMATE_USD,
  RD_VOICE_KINDS,
  RD_VOICE_KIND_LABELS,
  RD_VOICE_MAX_TALKS,
  RD_VOICE_WINDOW_DAYS,
  REACH_LABELS,
  REACTION_KINDS,
  REACTION_KIND_LABELS,
  RELATION_KINDS,
  RELATION_KIND_LABELS,
  RELEASE_STATUSES,
  RELEASE_STATUS_LABELS,
  RENDER_STATUSES,
  RESEARCH_DOSSIER_ESTIMATE_USD,
  RESEARCH_PROFILE_ESTIMATE_USD,
  REVIEWS_PER_READ_OPTIONS,
  REVIEW_AUDIENCES,
  REVIEW_DECISIONS,
  REVIEW_ROUND_KINDS,
  REVIEW_ROUND_KIND_LABELS,
  REVIEW_ROUND_STATUSES,
  REVIEW_ROUND_STATUS_LABELS,
  REVISION_ESTIMATE_USD,
  REVISION_ITEMS_MAX,
  REVISION_ITEM_LABELS,
  REVISION_ITEM_STATUSES,
  REVISION_READING_MAX,
  REVISION_VIAS,
  REVISION_VIA_LABELS,
  RITMO_AREAS,
  RITMO_AREA_LABELS,
  RITMO_AREA_ROUTES,
  RITMO_CLIENT_AREAS,
  RITMO_MONEY_AREAS,
  RITMO_MONEY_BLOCKS,
  RITMO_NOTIFICATION_GROUPS,
  ROLE_RANK,
  ROOM_DOC_ESTIMATE_USD,
  ROOM_FUNCTIONS,
  ROOM_LABELS,
  ROOM_MESSAGES_MAX,
  ROOM_PINS_MAX,
  ROOM_PIN_KINDS,
  ROOM_PIN_LABELS,
  ROOM_RESEARCH_ESTIMATE_USD,
  ROOM_TURN_ESTIMATE_USD,
  ROOM_WRITE_ESTIMATE_USD,
  ROUTE_ESTIMATE_USD,
  ROUTE_STATUSES,
  SEARCH_MATCH_KINDS,
  SECTOR_NORM_KINDS,
  SECTOR_NORM_KIND_LABELS,
  SERENDIPITY_FEEDBACK,
  SERVICE_DEFAULTS,
  SERVICE_KINDS,
  SERVICE_KIND_LABELS,
  SERVICE_MARKUP_DEFAULT,
  SERVICE_MAX,
  SERVICE_UNITS,
  SERVICE_UNIT_LABELS,
  SESSION_KINDS,
  SESSION_KIND_LABELS,
  SESSION_STATUSES,
  SHELF_STATUSES,
  SHELF_STATUS_LABELS,
  SIGNAL_KINDS,
  SIGNAL_KIND_LABELS,
  SITE_CHANGE_RATE,
  SITE_MAP_MAX_PAGES,
  SOCIAL_NETWORKS,
  SOCIAL_NETWORK_LABELS,
  SOCIAL_PROOF_KINDS,
  SOCIAL_PROOF_LABELS,
  SOLUTION_STATUSES,
  SOLUTION_STATUS_LABELS,
  SOURCE_PROCESSING_STATUSES,
  SOURCE_TYPES,
  STATEMENT_KINDS,
  STEP_DEFAULT_HOURS,
  STEP_STATUSES,
  STEP_STATUS_LABELS,
  STRENGTH_SOURCES,
  STRENGTH_SOURCE_LABELS,
  SUGGESTION_KINDS,
  SUGGESTION_STATUSES,
  SYNTHESIS_STATUSES,
  TAG_PROFILE_PAID_MAX,
  TAG_REVIEW_DEFAULT,
  TAG_RULES,
  TAG_RULE_LABELS,
  TAG_STATUSES,
  TAG_SUPERFAN_30D,
  TAG_VOTES,
  TEAM_FUNCTION_DEFAULTS,
  TEAM_FUNCTION_GATES,
  TEAM_FUNCTION_MACHINE,
  TEAM_FUNCTION_OPERATIONS,
  TERRITORY_ASSET_KINDS,
  TERRITORY_ASSET_LABELS,
  VERDICTS,
  VERDICT_LABELS,
  VERSION_KINDS,
  VER_STAGES,
  VER_STAGE_GUIDE,
  VER_STAGE_LABELS,
  VISUAL_ENTITY_TYPES,
  VISUAL_FEEDBACK,
  VISUAL_FEEDBACK_LABELS,
  VISUAL_MODES,
  VISUAL_PURPOSES,
  VISUAL_REGENERATION,
  VISUAL_STATUSES,
  WALL_EVENTS,
  WALL_EVENT_LABELS,
  WALL_ITEM_KINDS,
  WALL_MODES,
  WALL_MODE_LABELS,
  WALL_SCREENS,
  WATCHED_PAGES_MAX,
  WATCH_CHANNELS,
  WATCH_CHANNEL_LABELS,
  WATCH_COST_USD,
  WATCH_FREQUENCIES,
  WATCH_LEVELS,
  WATCH_LEVEL_LABELS,
  WATCH_PRESETS,
  activityText,
  adAccessLost,
  adAdvertiserKey,
  adBalance,
  adBillingUrl,
  adCostPerSale,
  adFatigue,
  adManagerUrl,
  adNameMatch,
  adPageKey,
  adPlatformBillingUrl,
  adPlatformManagerUrl,
  adResultGuess,
  adReturn,
  adSalesText,
  adStatusLabel,
  adVideoStats,
  adWriteAction,
  adsOf,
  aiMonthStart,
  aiUsageState,
  argumentStatus,
  assetAiEditAllowed,
  assetAuditBlockers,
  assetBlockers,
  assetFormatRatio,
  assetRightsWithPeople,
  assetUseFile,
  assetWarnings,
  basePrice,
  bisActivePrograms,
  bisAppliesText,
  bisAppliesTo,
  bisAudienceKey,
  bisAudienceLabel,
  bisBlockAssetIds,
  bisBlockText,
  bisChanges,
  bisChannelKey,
  bisChannelLabel,
  bisDrawerOf,
  bisFor,
  bisHex,
  bisIllustratorOf,
  bisLegalTexts,
  bisNextVersionLabel,
  bisPieceKey,
  bisPieceLabel,
  bisProgramActive,
  bisProgramOf,
  bisRefBasis,
  bisSpecificity,
  bisToPublish,
  bisVersionLabels,
  bisVersionLevel,
  boardBlocksFor,
  boardBlocksOf,
  boardClientBlocksFor,
  campaignEfficiency,
  campaignPace,
  campaignResultFilled,
  campaignResultText,
  canonicalBisApplies,
  channelRole,
  cleanBoardBlocks,
  cleanProtectShape,
  cleanRitmoAreas,
  clientRepoUrl,
  colorLuminance,
  composeContrast,
  composeLayout,
  composeLum,
  composeSnap,
  composeTune,
  contactVia,
  contextBase,
  contextBaseLine,
  conversationDepthOf,
  conversationIntroIsLong,
  cpfValid,
  creationEmpty,
  creationForClient,
  creationLayoutFor,
  creationPieceEmpty,
  creatorConflicts,
  currentStage,
  defaultMockupFrame,
  demandFileKind,
  demandMonthLabel,
  demandOverdue,
  demandRepo,
  demandRepoPath,
  demandScopeByRule,
  demandShortNameByRule,
  detectCompetitors,
  dimensionsText,
  dossierLines,
  driveJobFolderName,
  driveLinks,
  driveMonthlyFolderName,
  extractMentions,
  faceOverlap,
  faceSignatureAllowed,
  feeCycle,
  feeCycleLabel,
  feeCycleMonth,
  feeWallet,
  fitBand,
  formatFollowers,
  forwardHref,
  gadsConflicts,
  gadsDraftCheck,
  gadsKeywordParse,
  gadsKeywordSummary,
  gadsKeywordText,
  gadsSuggestionKey,
  gadsTokens,
  gadsWasteTerms,
  handleKey,
  hasRoleAtLeast,
  imageContractCovers,
  imageContractStatusOf,
  imageTierFor,
  inProtectedArea,
  inferSourceType,
  instagramHandle,
  isBenchmark,
  isClientRole,
  isClosedMonth,
  isHexColor,
  isInternalRole,
  isRival,
  isSponsoredCaption,
  itemFullText,
  itemPublishMoment,
  jobNumber,
  labAskLooksLikeQuestion,
  labTally,
  listOf,
  mainWebsite,
  markTime,
  markWhere,
  materialHasValues,
  materialProblems,
  materialRecipeGuide,
  measuredDimension,
  measuredLevel,
  mediaOutlineReady,
  medianOf,
  mentionConflicts,
  metricKey,
  modoAskChannels,
  modoAsksData,
  modoAsksPartner,
  modoAsksSocial,
  modoAttachKind,
  modoAttachRefusal,
  modoBaseRoles,
  modoBisContext,
  modoClassesFor,
  modoEmphasis,
  modoExpertFits,
  modoFacesOnPiece,
  modoFontLabel,
  modoHasPreview,
  modoIconSvg,
  modoKitColors,
  modoMoldFor,
  modoMoldOf,
  modoMoldZones,
  modoMoldsFor,
  modoPhotoBox,
  modoPhotoFocus,
  modoPhotoPlace,
  modoPhotoPlaceOk,
  modoPhotoQuery,
  modoPieceText,
  modoPlain,
  modoReadable,
  modoRequestType,
  modoRoute,
  modoRuleEdit,
  modoSeatDiscount,
  modoShortNumber,
  modoSlideHasStats,
  modoStatusOf,
  modoUnitText,
  modoVariantCount,
  modoVocabularyChecks,
  moldFieldLabel,
  moldNotices,
  moldOptions,
  moldQuestions,
  monthKey,
  negativeCovers,
  negativeInScope,
  negativeMatches,
  negativeWouldBlock,
  networkTier,
  normalizeBisApplies,
  normalizeBisBlock,
  normalizeCreationContent,
  normalizeCreationPiece,
  outsideWorkHours,
  ownBrandSubject,
  parseCm,
  parseMoneyText,
  parsePieceSize,
  parseProofName,
  personalColumn,
  personalValue,
  pieceFileName,
  pieceSizeText,
  placeClaimed,
  placeSlug,
  polygonArea,
  postPurpose,
  proofFileName,
  prospectQuote,
  protectBox,
  protectCropReport,
  protectKey,
  protectOverlap,
  rarityIndexOf,
  rastroBrandKey,
  rastroMoveKind,
  rastroSector,
  rastroSectorsOf,
  rastroTermTrend,
  rastroWindowDays,
  releaseForAsset,
  releaseValid,
  resolvePriceReference,
  resolveTotalCost,
  ritmoAccessFor,
  ritmoAreaOf,
  ritmoAreasOf,
  roomKindOf,
  servicePrice,
  simplifyStroke,
  slugify,
  socialHandle,
  socialHandles,
  socialOf,
  socialProfileUrl,
  sumVideo,
  tagResult,
  termExcluded,
  unitsBand,
  watchDaysOf,
  watchMonthlyCostUsd,
  watchOf,
  websitesOf,
  wellFormed,
  withoutLegalText
};
//# sourceMappingURL=index.js.map